import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { AuthContext } from "../context/AuthContext";
import HasPermission from "../utils/HasPermission";
import zombieBoardMap from "../assets/infinite-zombie/board-map.jpg";
import "./css/Events.css";
import "./css/events-overview.css";
import "./css/events-planning.css";
import "./css/events-results.css";
import "./css/events-upload-split.css";
import "./css/events-analytics.css";
import "./css/events-zombie.css";
import "../components/events/events-upload.css";
import ImageUploadModal from "../components/events/ImageUploadModal";
import TemplateBoxAnnotator from "../components/events/TemplateBoxAnnotator";
import {
  getActualUnitCountForTroops,
  getParticipantActualUnitCount,
  getTroopTierNumber,
} from "../components/events/battleLogReportAdapter";
import BattleReportModal from "../components/events/BattleReportModal";
import EventsWorkspaceTabs from "../components/events/workspace/EventsWorkspaceTabs";
import EventPlanningPanel from "../components/events/workspace/EventPlanningPanel";
import EventBattlesPanel from "../components/events/workspace/EventBattlesPanel";
import EventResultsPanel from "../components/events/workspace/EventResultsPanel";
import EventOverviewPanel from "../components/events/workspace/EventOverviewPanel";
import EventAnalyticsPanel from "../components/events/workspace/EventAnalyticsPanel";
import EliteWarRealtimePlanner from "../components/events/planners/EliteWarRealtimePlanner";
import NationIntelRealtimePlanner from "../components/events/planners/NationIntelRealtimePlanner";
import { getPlannerComponentByPlugin } from "../components/events/planners/PlannerRegistry";
import CommanderOrphanAdminView from "../components/CommanderOrphanAdminView";
import { normalizeCommanderOption } from "../components/commanders/commanderUtils";
import MultiSelectFilter from "../components/MultiSelectFilter";
import SingleSelectDropdown from "../components/SingleSelectDropdown";
import { AdminTabs, AppButton, PageShell } from "../components/ui";

const STRONGHOLD_ITEMS = [
  { value: "guidedBomb", label: "Guided bomb", color: "#3b82f6" },
  { value: "ultraShield", label: "Ultra shield", color: "#ff8a00" },
  { value: "allOutDefence", label: "All out defence", color: "#8b5cf6" },
  { value: "reflectDamage", label: "Reflect damage", color: "#3b82f6" },
  { value: "increaseFirepower", label: "Increase firepower", color: "#22c55e" },
];

const EVENT_BATTLE_LOCATION_LABELS = {
  17104904: "City",
  16973837: "Alliance Flag",
  16973847: "Frenzy Tower",
  16973827: "Alliance Hall",
  16973829: "Alliance Fort",
  16973840: "Alliance Institute",
  17039373: "Elite Mine",
  17039375: "Elite Mine",
  17039377: "Elite Mine",
  17039379: "Elite Mine",
  17039381: "Elite Mine",
  17039363: "Resource Tile",
  17039365: "Resource Tile",
  17039367: "Resource Tile",
  17039369: "Resource Tile",
  17039371: "Resource Tile",
  17039382: "Ruins",
  17039428: "Power Ore Mine",
};

const STRIKE_RULE_TYPE_LABELS = {
  standard: "Configured point rule",
  bubble_drop: "Bubble drop",
  tier_gathering: "T5+ gathering",
};

const normalizeBattleLocationType = (battle = {}) => {
  const rawLocation = battle?.location && typeof battle.location === "object" ? battle.location : {};
  const locationType = String(rawLocation.type || "")
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (locationType) return locationType;
  const nodeType = Number(rawLocation.nodeType || battle?.locationNodeType || battle?.battleTypeCode || 0);
  if (nodeType === 17104904) return "city";
  if ([17039373, 17039375, 17039377, 17039379, 17039381].includes(nodeType)) return "elite_mine";
  if ([17039363, 17039365, 17039367, 17039369, 17039371].includes(nodeType)) return "resource_tile";
  if (nodeType === 17039428) return "power_ore";
  if (String(nodeType).startsWith("169738")) return "alliance_structure";
  return "";
};

const BATTLE_POINT_SOURCE_LABELS = {
  actual_report: "Exact",
  replay_estimate: "Estimated",
  not_available: "Unavailable",
};

const BATTLE_CONFIDENCE_LABELS = {
  exact: "Exact",
  medium: "Medium",
  low: "Low",
  none: "None",
};

const BATTLE_RISK_LABELS = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
  none: "None",
};

const BATTLE_EXCHANGE_LABELS = {
  critical: "Large exchange",
  high: "Large exchange",
  medium: "Medium exchange",
  low: "Small exchange",
  none: "No exchange",
};

const BATTLE_IMPACT_LABELS = {
  favorable: "Positive net",
  negative: "Negative net",
  even: "Even net",
  unresolved: "Unknown impact",
  unscored: "Unscored",
  same_event_nation: "Same nation",
};

const BATTLE_STRIKE_REVIEW_LABELS = {
  likely: "Strike candidate",
  possible: "Strike candidate",
  review: "Strike review",
  report_needed: "Report needed",
  none: "",
};

const DEFAULT_BUBBLE_DROP_WINDOW_MINUTES = 10;
const DEFAULT_BUBBLE_DROP_MIN_BATTLES = 2;

const BATTLE_RISK_RANK = {
  none: 0,
  low: 1,
  medium: 2,
  high: 3,
  critical: 4,
};

const NATION_SCORE_BREAKDOWN_FIELDS = [
  { key: "killScore", label: "Kill", rawKey: "k_s" },
  { key: "recruitScore", label: "Recruit", rawKey: "r_s" },
  { key: "npcScore", label: "Kill Monsters", rawKey: "n_s" },
  { key: "powerScore", label: "Power", rawKey: "p_s" },
  { key: "farmScore", label: "Gather", rawKey: "f_s" },
];

function formatNationNumberLabel(number) {
  const parsed = Number(number);
  return Number.isFinite(parsed) ? String(parsed) : "";
}

function asFiniteInteger(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : fallback;
}

function formatStrikeEvidenceUnits(value) {
  const units = asFiniteInteger(value, 0);
  if (units >= 1000000) return `${(units / 1000000).toFixed(units >= 10000000 ? 1 : 2).replace(/\.0+$/, "")}M`;
  if (units >= 1000) return `${(units / 1000).toFixed(units >= 100000 ? 0 : 1).replace(/\.0+$/, "")}K`;
  return units.toLocaleString();
}

function isOfficialScoreboardRow(row) {
  return String(row?.sourceKey || "").startsWith("official_scoreboard:")
    || String(row?.sourceContext?.scoreSource || "") === "official_scoreboard";
}

function getScoreboardRowPoints(row) {
  return Number(row?.points ?? row?.score ?? 0);
}

function getScoreboardRowKey(row, fallback = "") {
  return String(row?._id || row?.sourceKey || row?.key || fallback);
}

function getNationScoreBreakdown(row) {
  const game = row?.sourceContext?.game || {};
  const raw = row?.sourceContext?.raw || {};
  return NATION_SCORE_BREAKDOWN_FIELDS.map((field) => ({
    ...field,
    value: Number(game?.[field.key] ?? raw?.[field.rawKey] ?? raw?.[field.key] ?? 0),
  }));
}

function hasNationScoreBreakdown(row) {
  return getNationScoreBreakdown(row).some((field) => Number.isFinite(field.value) && field.value > 0);
}

function getScoreboardNationFallback(row) {
  const context = row?.sourceContext || {};
  const gameNationNumber = context.gameNationNumber ?? context.game?.serverId;
  if (gameNationNumber != null && gameNationNumber !== "") {
    return formatNationNumberLabel(gameNationNumber) || String(gameNationNumber);
  }
  return context.game?.kingdomName || "";
}

function formatIndividualScoreboardTypeLabel(value) {
  const type = String(value || "").trim();
  if (type === "day_player") return "Current Phase";
  if (type === "round_player") return "Weekly";
  if (type === "season_player") return "Season";
  const phaseMatch = type.match(/^phase[_-]?(\d+)[_-]?player$/i);
  if (phaseMatch) return `Phase ${phaseMatch[1]}`;
  return type
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ") || "Ranking";
}

function battleHasCaptureTruthRows(battle) {
  return Boolean(
    (Array.isArray(battle?.captureTruth?.attackerTroops) && battle.captureTruth.attackerTroops.length > 0)
      || (Array.isArray(battle?.captureTruth?.defenderTroops) && battle.captureTruth.defenderTroops.length > 0)
  );
}

function getBattleRowKey(battle) {
  return String(battle?._id || battle?.battleKey || battle?.reportKey || battle?.logId || "");
}

function getBattleTimestampMs(battle) {
  const date = new Date(battle?.at || battle?.battleTime || battle?.startsAt || battle?.createdAt || 0);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function getBattleLocationParts(battle = {}) {
  const rawLocation = battle?.location && typeof battle.location === "object" ? battle.location : {};
  const nodeType = Number(rawLocation.nodeType || battle?.locationNodeType || battle?.battleTypeCode || 0);
  const label = String(rawLocation.label || EVENT_BATTLE_LOCATION_LABELS[nodeType] || "").trim();
  const coordinates = rawLocation.coordinates && typeof rawLocation.coordinates === "object"
    ? rawLocation.coordinates
    : battle?.coordinates;
  const x = coordinates?.x;
  const y = coordinates?.y;
  return { nodeType, label, x, y };
}

function getBattleCityCoordinateKey(battle) {
  const { nodeType, label, x, y } = getBattleLocationParts(battle);
  const isCity = nodeType === 17104904 || label.toLowerCase() === "city";
  if (!isCity || x == null || y == null || x === "" || y === "") return "";
  return `${String(x).trim()},${String(y).trim()}`;
}

function getBattleEvidence(battle) {
  const replayStatus = String(battle?.enrichmentStatus || "");
  return {
    battleLog: Boolean(battle?.evidence?.battleLog ?? (battle?.source === "aoz_battle_log" || battle?.logId)),
    replay: Boolean(battle?.evidence?.replay ?? (replayStatus === "replay_captured" || (battle?.participants || []).length > 0)),
    report: getBattlePointSource(battle) === "actual_report",
  };
}

function getBattlePointValue(battle, side) {
  const key = side === "defender" ? "defenderPoints" : "attackerPoints";
  return battle?.[key]
    ?? battle?.eventPoints?.[key]
    ?? battle?.captureTruth?.eventPoints?.[key]
    ?? null;
}

function getBattlePointSource(battle) {
  if (battle?.pointSource) return String(battle.pointSource);
  const eventPoints = battle?.eventPoints || battle?.captureTruth?.eventPoints || {};
  const isEstimated = eventPoints?.estimated === true
    || String(eventPoints?.status || "") === "estimated"
    || String(eventPoints?.source || "") === "replay_simulation_estimate_v1"
    || battle?.captureTruth?.estimated === true
    || battle?.captureTruth?.estimate?.estimated === true
    || battle?.diagnostics?.replayPointEstimate;
  if (battleHasCaptureTruthRows(battle) && !isEstimated) {
    return "actual_report";
  }
  if (isEstimated) {
    return "replay_estimate";
  }
  return "not_available";
}

function getBattleConfidence(battle) {
  if (battle?.pointConfidence) return String(battle.pointConfidence);
  const source = getBattlePointSource(battle);
  if (source === "actual_report") return "exact";
  if (source === "replay_estimate") return "medium";
  return "none";
}

function getBattleRiskLevel(battle) {
  if (battle?.riskLevel) return String(battle.riskLevel);
  const source = getBattlePointSource(battle);
  if (source === "not_available") return "none";
  const riskBasis = battle?.riskBasis != null
    ? asFiniteInteger(battle.riskBasis, 0)
    : Math.max(0, -asFiniteInteger(battle?.netPoints, 0));
  if (riskBasis >= 1000000) return "critical";
  if (riskBasis >= 250000) return "high";
  if (riskBasis >= 50000) return "medium";
  if (riskBasis > 0) return "low";
  return "none";
}

function getBattleExchangeLevel(battle) {
  if (battle?.exchangeLevel) return String(battle.exchangeLevel);
  const source = getBattlePointSource(battle);
  if (source === "not_available") return "none";
  const exchangeBasis = Math.max(
    asFiniteInteger(getBattlePointValue(battle, "attacker"), 0),
    asFiniteInteger(getBattlePointValue(battle, "defender"), 0),
  );
  if (exchangeBasis >= 1000000) return "critical";
  if (exchangeBasis >= 250000) return "high";
  if (exchangeBasis >= 50000) return "medium";
  if (exchangeBasis > 0) return "low";
  return "none";
}

function getBattleImpactStatus(battle) {
  if (battle?.impactStatus) return String(battle.impactStatus);
  if (getBattlePointSource(battle) === "not_available") return "unscored";
  if (battle?.netPoints == null) return "unresolved";
  const net = asFiniteInteger(battle.netPoints, 0);
  if (net > 0) return "favorable";
  if (net < 0) return "negative";
  return "even";
}

function getBattleExpectedWinner(battle) {
  const rawWinner = String(
    battle?.captureTruth?.estimate?.winner
    || battle?.diagnostics?.replayPointEstimate?.winner
    || battle?.captureTruth?.winner
    || "",
  ).trim().toLowerCase();
  if (rawWinner === "attacker") return "Attacker";
  if (rawWinner === "defender") return "Defender";
  return "-";
}

function getBattleNeedsReport(battle) {
  if (battle?.review?.strikeCandidateOverride === false) return false;
  if (typeof battle?.needsReport === "boolean") return battle.needsReport;
  return getBattlePointSource(battle) === "replay_estimate" && BATTLE_RISK_RANK[getBattleRiskLevel(battle)] >= BATTLE_RISK_RANK.high;
}

function getBattleCityOwnerDefenderParticipant(battle = {}) {
  const participants = Array.isArray(battle?.participants) ? battle.participants : [];
  const defenderGameId = String(battle?.defender?.gameCommanderId || "").trim();
  const defenderCommanderId = String(battle?.defendingCommander || "").trim();
  const defenderRows = participants.filter((participant) => {
    const side = String(participant?.side || "").toLowerCase();
    return side === "defender" || side === "defending" || side === "defenderer";
  });
  return defenderRows.find((participant) => (
    (defenderGameId && String(participant?.gameCommanderId || "") === defenderGameId)
    || (defenderCommanderId && String(participant?.commander || "") === defenderCommanderId)
  )) || defenderRows[0] || null;
}

function getBattleCityOwnerDefenderUnits(battle = {}) {
  const owner = getBattleCityOwnerDefenderParticipant(battle);
  if (owner) return getParticipantActualUnitCount(owner);
  return getActualUnitCountForTroops(battle?.defender?.troops || []) || asFiniteInteger(battle?.defender?.totalUnits, 0);
}

function battleHasTierAtOrAbove(battle = {}, minTier = 5, sideFilter = "any") {
  const threshold = Math.max(1, asFiniteInteger(minTier, 5));
  const participants = Array.isArray(battle?.participants) ? battle.participants : [];
  const normalizedSideFilter = String(sideFilter || "any").toLowerCase();
  const participantMatchesSide = (participant) => {
    if (normalizedSideFilter === "any") return true;
    const side = String(participant?.side || "").toLowerCase();
    if (normalizedSideFilter === "defender") return side === "defender" || side === "defending" || side === "defenderer";
    if (normalizedSideFilter === "attacker") return side === "attacker" || side === "attacking" || side === "attackerer";
    return true;
  };
  const troopLists = [
    ...(participants.filter(participantMatchesSide).map((participant) => participant?.troops || [])),
    ...(normalizedSideFilter === "any" || normalizedSideFilter === "attacker" ? [battle?.attacker?.troops || []] : []),
    ...(normalizedSideFilter === "any" || normalizedSideFilter === "defender" ? [battle?.defender?.troops || []] : []),
  ];
  return troopLists.some((troops) => (
    (Array.isArray(troops) ? troops : []).some((troop) => {
      const count = asFiniteInteger(troop?.count, 0);
      if (count <= 0) return false;
      return getTroopTierNumber(troop?.troopGid || troop?.gid || troop?.id) >= threshold;
    })
  ));
}

function getBattleStrikeReviewLevel(battle) {
  if (battle?.review?.strikeCandidateOverride === false) return "none";
  if (battle?.strikeReviewLevel) return String(battle.strikeReviewLevel);
  const riskLevel = getBattleRiskLevel(battle);
  const impactStatus = getBattleImpactStatus(battle);
  if (impactStatus !== "negative" || BATTLE_RISK_RANK[riskLevel] < BATTLE_RISK_RANK.high) {
    return getBattleNeedsReport(battle) ? "report_needed" : "none";
  }
  const source = getBattlePointSource(battle);
  const confidence = getBattleConfidence(battle);
  if (source === "actual_report" && confidence === "exact") {
    return riskLevel === "critical" ? "likely" : "possible";
  }
  return "review";
}

function getConfiguredStrikeMatches(battle, strikeSettings = {}, context = {}) {
  const reasons = Array.isArray(strikeSettings?.reasons) ? strikeSettings.reasons : [];
  const pointSource = getBattlePointSource(battle);
  const confidence = getBattleConfidence(battle);
  const riskLevel = getBattleRiskLevel(battle);
  const exchangeLevel = getBattleExchangeLevel(battle);
  const impactStatus = getBattleImpactStatus(battle);
  const attackerNationRole = String(context.attackerNationRole || battle?.attackerNationRole || "");
  const defenderNationRole = String(context.defenderNationRole || battle?.defenderNationRole || "");
  const needsReport = getBattleNeedsReport(battle);
  const hasReport = getBattleEvidence(battle).report;
  const locationType = normalizeBattleLocationType(battle);
  const bubbleDropGroups = Array.isArray(context.bubbleDropGroups) ? context.bubbleDropGroups : [];
  return reasons.filter((reason) => {
    if (!reason || reason.enabled === false) return false;
    const ruleType = String(reason.ruleType || "standard");
    const reasonLocationType = String(reason.locationType || "any");
    if (reasonLocationType !== "any" && reasonLocationType !== locationType) return false;
    const reasonAttackerNationRole = String(reason.attackerNationRole || "any");
    if (reasonAttackerNationRole !== "any" && reasonAttackerNationRole !== attackerNationRole) return false;
    const reasonDefenderNationRole = String(reason.defenderNationRole || "any");
    if (reasonDefenderNationRole !== "any" && reasonDefenderNationRole !== defenderNationRole) return false;
    if (ruleType === "bubble_drop") {
      return bubbleDropGroups.some((group) => String(group?.ruleCode || "") === String(reason.code || ""));
    }
    if (ruleType === "tier_gathering") {
      const minTier = asFiniteInteger(reason.minTroopTier, 5);
      return battleHasTierAtOrAbove(battle, minTier || 5, "defender");
    }
    const reasonPointSource = String(reason.pointSource || "any");
    if (reasonPointSource !== "any" && reasonPointSource !== pointSource) return false;
    const reasonConfidence = String(reason.pointConfidence || reason.confidence || "any");
    if (reasonConfidence !== "any" && reasonConfidence !== confidence) return false;
    const reasonImpact = String(reason.impactStatus || "any");
    if (reasonImpact !== "any" && reasonImpact !== impactStatus) return false;
    const minRisk = String(reason.minRiskLevel || "none");
    if (BATTLE_RISK_RANK[riskLevel] < BATTLE_RISK_RANK[minRisk]) return false;
    const minExchange = String(reason.minExchangeLevel || "");
    if (minExchange && BATTLE_RISK_RANK[exchangeLevel] < BATTLE_RISK_RANK[minExchange]) return false;
    if (reason.requireNeedsReport === true && !needsReport) return false;
    if (reason.requireReportPresent === true && !hasReport) return false;
    return true;
  });
}

function getBattleStrikeCandidateState(battle, strikeSettings = {}, context = {}) {
  if (battle?.review?.strikeCandidateOverride === false) return { isCandidate: false, matches: [] };
  const configuredMatches = getConfiguredStrikeMatches(battle, strikeSettings, context);
  if (configuredMatches.length > 0) return { isCandidate: true, matches: configuredMatches };
  const hasConfiguredStrikeRules = Array.isArray(strikeSettings?.reasons)
    && strikeSettings.reasons.some((reason) => reason && reason.enabled !== false);
  if (hasConfiguredStrikeRules) return { isCandidate: false, matches: [] };
  const reviewLevel = getBattleStrikeReviewLevel(battle);
  const fallbackCandidate = ["likely", "possible", "review"].includes(reviewLevel);
  return { isCandidate: fallbackCandidate, matches: [] };
}

function getBattleStrikeCommanderRefs(battle = {}) {
  const refsById = new Map();
  const entityId = (value) => String(value?._id || value || "").trim();
  const addRef = ({ commanderId, side, name, allianceId, allianceShort }) => {
    const id = entityId(commanderId);
    if (!id) return;
    if (!refsById.has(id)) {
      refsById.set(id, {
        commanderId: id,
        side: String(side || "").trim(),
        name: String(name || "").trim(),
        allianceId: entityId(allianceId),
        allianceShort: String(allianceShort || "").trim(),
      });
      return;
    }
    const existing = refsById.get(id);
    refsById.set(id, {
      ...existing,
      side: existing.side || String(side || "").trim(),
      name: existing.name || String(name || "").trim(),
      allianceId: existing.allianceId || entityId(allianceId),
      allianceShort: existing.allianceShort || String(allianceShort || "").trim(),
    });
  };

  (Array.isArray(battle?.participants) ? battle.participants : []).forEach((participant) => {
    addRef({
      commanderId: participant?.commander,
      side: participant?.side,
      name: participant?.name,
      allianceId: participant?.alliance,
      allianceShort: participant?.allianceShort,
    });
  });

  addRef({
    commanderId: battle?.attackingCommander,
    side: "attacker",
    name: battle?.attacker?.name,
    allianceId: battle?.attackingAlliance,
    allianceShort: battle?.attacker?.allianceShort,
  });
  addRef({
    commanderId: battle?.defendingCommander,
    side: "defender",
    name: battle?.defender?.name,
    allianceId: battle?.defendingAlliance,
    allianceShort: battle?.defender?.allianceShort,
  });

  return Array.from(refsById.values());
}

const INFINITE_ZOMBIE_TOWERS = [
  { value: "", label: "Empty" },
  { value: "machineGun", label: "Machine Gun" },
  { value: "rocket", label: "Rocket" },
  { value: "emp", label: "EMP" },
  { value: "laser", label: "Laser" },
];

const INFINITE_ZOMBIE_TOWER_IMAGES = {
  machineGun: "/infinite-zombie/towers/machine-gun-direct.jpg?v=2",
  rocket: "/infinite-zombie/towers/rocket-direct.jpg?v=2",
  emp: "/infinite-zombie/towers/emp-direct.jpg?v=2",
  laser: "/infinite-zombie/towers/laser-direct.jpg?v=2",
};

const INFINITE_ZOMBIE_ITEMS = {
  orange: [
    {
      value: "orange_ore",
      label: "Resources (2400)",
      color: "#f59e0b",
      icon: "/infinite-zombie/items/orange_ore.png?v=4",
    },
    {
      value: "orange_emp",
      label: "Damage +30%",
      color: "#f59e0b",
      icon: "/infinite-zombie/items/orange_emp.png?v=4",
    },
    {
      value: "orange_rocket",
      label: "A-Bomb",
      color: "#f59e0b",
      icon: "/infinite-zombie/items/orange_rocket.png?v=4",
    },
    {
      value: "orange_laser",
      label: "Weather Controller",
      color: "#f59e0b",
      icon: "/infinite-zombie/items/orange_laser.png?v=4",
    },
  ],
  purple: [
    {
      value: "purple_ore",
      label: "Resource (1200)",
      color: "#a855f7",
      icon: "/infinite-zombie/items/purple_ore.png?v=4",
    },
    {
      value: "purple_ore_up",
      label: "Resource Rate +12%",
      color: "#a855f7",
      icon: "/infinite-zombie/items/purple_ore_up.png?v=4",
    },
    {
      value: "purple_hp",
      label: "HP (40)",
      color: "#a855f7",
      icon: "/infinite-zombie/items/purple_hp.png?v=4",
    },
    {
      value: "purple_rocket",
      label: "Rocket Damage +40%",
      color: "#a855f7",
      icon: "/infinite-zombie/items/purple_rocket.png?v=4",
    },
    {
      value: "purple_laser",
      label: "Laser Damage +40%",
      color: "#a855f7",
      icon: "/infinite-zombie/items/purple_laser.png?v=4",
    },
    {
      value: "purple_emp",
      label: "Damage +15%",
      color: "#a855f7",
      icon: "/infinite-zombie/items/purple_emp.png?v=4",
    },
    {
      value: "purple_engineer",
      label: "Freezing Robot",
      color: "#a855f7",
      icon: "/infinite-zombie/items/purple_engineer.png?v=4",
    },
  ],
  blue: [
    {
      value: "blue_ore",
      label: "Resources (600)",
      color: "#3b82f6",
      icon: "/infinite-zombie/items/blue_ore.png?v=4",
    },
    {
      value: "blue_ore_up",
      label: "Resource Rate +6%",
      color: "#3b82f6",
      icon: "/infinite-zombie/items/blue_ore_up.png?v=4",
    },
    {
      value: "blue_hp",
      label: "HP (20)",
      color: "#3b82f6",
      icon: "/infinite-zombie/items/blue_hp.png?v=4",
    },
    {
      value: "blue_rocket",
      label: "Machine Gun Damage +20%",
      color: "#3b82f6",
      icon: "/infinite-zombie/items/blue_rocket.png?v=4",
    },
    {
      value: "blue_laser",
      label: "Laser Damage +20%",
      color: "#3b82f6",
      icon: "/infinite-zombie/items/blue_laser.png?v=4",
    },
    {
      value: "blue_emp",
      label: "EMP Damage +20%",
      color: "#3b82f6",
      icon: "/infinite-zombie/items/blue_emp.png?v=4",
    },
    {
      value: "blue_gift",
      label: "Reward +1",
      color: "#3b82f6",
      icon: "/infinite-zombie/items/blue_gift.png?v=4",
    },
    {
      value: "blue_part",
      label: "Reward Quality Boost",
      color: "#3b82f6",
      icon: "/infinite-zombie/items/blue_part.png?v=4",
    },
    {
      value: "blue_engineer",
      label: "Energize",
      color: "#3b82f6",
      icon: "/infinite-zombie/items/blue_engineer.png?v=4",
    },
  ],
  green: [
    {
      value: "green_ore",
      label: "Resources (300)",
      color: "#22c55e",
      icon: "/infinite-zombie/items/green_ore.png?v=4",
    },
    {
      value: "green_ore_up",
      label: "Resource Rate +3%",
      color: "#22c55e",
      icon: "/infinite-zombie/items/green_ore_up.png?v=4",
    },
    {
      value: "green_hp",
      label: "HP (10)",
      color: "#22c55e",
      icon: "/infinite-zombie/items/green_hp.png?v=4",
    },
    {
      value: "green_engineer",
      label: "Explosive Robot",
      color: "#22c55e",
      icon: "/infinite-zombie/items/green_engineer.png?v=4",
    },
  ],
};

const INFINITE_ZOMBIE_WAVE_COUNT = 11;

const INFINITE_ZOMBIE_BOARD_COORDS = [
  { x: 48.0, y: 12.5 },
  { x: 39.7, y: 17.7 },
  { x: 52.9, y: 21.5 },
  { x: 40.4, y: 29.4 },
  { x: 61.2, y: 16.3 },
  { x: 66.0, y: 25.4 },
  { x: 60.0, y: 35.2 },
  { x: 62.4, y: 39.7 },
  { x: 45.2, y: 38.5 },
  { x: 51.8, y: 40.5 },
  { x: 54.2, y: 45.0 },
  { x: 50.0, y: 47.5 },
  { x: 45.8, y: 50.2 },
  { x: 43.4, y: 45.6 },
  { x: 40.9, y: 41.1 },
  { x: 36.2, y: 32.1 },
  { x: 27.8, y: 37.3 },
  { x: 32.7, y: 46.3 },
  { x: 35.0, y: 50.9 },
  { x: 33.3, y: 58.1 },
  { x: 54.7, y: 56.6 },
  { x: 67.3, y: 48.8 },
  { x: 59.0, y: 54.0 },
  { x: 59.5, y: 65.6 },
  { x: 53.1, y: 63.8 },
  { x: 55.5, y: 68.3 },
  { x: 44.6, y: 69.0 },
  { x: 47.0, y: 73.6 },
  { x: 56.1, y: 79.9 },
  { x: 42.8, y: 76.1 },
  { x: 50.4, y: 83.6 },
  { x: 38.8, y: 78.8 },
  { x: 38.1, y: 67.1 },
  { x: 33.9, y: 69.8 },
  { x: 36.3, y: 74.3 },
  { x: 28.0, y: 79.4 },
  { x: 39.9, y: 90.0 },
  { x: 45.2, y: 86.8 },
  { x: 30.4, y: 84.0 },
  { x: 25.6, y: 74.9 },
  { x: 26.7, y: 56.1 },
  { x: 29.1, y: 60.6 },
  { x: 23.2, y: 70.3 },
  { x: 44.42, y: 26.79 },
  { x: 57.63, y: 30.6 },
  { x: 49.29, y: 35.85 },
  { x: 37.55, y: 55.35 },
  { x: 50.59, y: 59.15 },
  { x: 42.26, y: 64.45 },
];

const DEFAULT_EVENT_TYPE_META = {
  defaultEventType: "triangleWar",
  eventTypes: [
    {
      id: "triangleWar",
      label: "Triangle War",
      requiredAllianceCount: 3,
      requiredNationCount: 0,
      supportedResultModes: ["alliance", "individual"],
      defaultResultMode: "alliance",
      supportsPlanner: true,
      plannerPlugin: "triangleStrongholdPlanner",
      plannerMode: "territory_async",
      analyticsPolicy: "include",
      sharePolicy: "public_token_supported",
      timeModel: "all_day_async",
      supportsBattles: true,
      supportsResults: true,
    },
    {
      id: "duelOfDominance",
      label: "Duel of Dominance",
      requiredAllianceCount: 2,
      requiredNationCount: 0,
      supportedResultModes: ["alliance", "individual"],
      defaultResultMode: "alliance",
      supportsPlanner: true,
      plannerPlugin: "triangleStrongholdPlanner",
      plannerMode: "territory_async",
      analyticsPolicy: "include",
      sharePolicy: "public_token_supported",
      timeModel: "all_day_async",
      supportsBattles: true,
      supportsResults: true,
    },
    {
      id: "infiniteZombie",
      label: "Infinite Zombie",
      requiredAllianceCount: 0,
      requiredNationCount: 0,
      supportedResultModes: ["individual"],
      defaultResultMode: "individual",
      supportsPlanner: true,
      plannerPlugin: "infiniteZombiePlanner",
      plannerMode: "personal_sandbox",
      analyticsPolicy: "exclude",
      sharePolicy: "public_token_supported",
      timeModel: "user_session",
      supportsBattles: false,
      supportsResults: true,
    },
    {
      id: "eliteWar",
      label: "Elite War",
      requiredAllianceCount: 2,
      requiredNationCount: 0,
      supportedResultModes: ["alliance", "individual", "nation"],
      defaultResultMode: "alliance",
      supportsPlanner: true,
      plannerPlugin: "eliteWarRealtimePlanner",
      plannerMode: "battlefield_realtime",
      analyticsPolicy: "include",
      sharePolicy: "public_token_supported",
      timeModel: "windowed_realtime_45m",
      supportsBattles: true,
      supportsResults: true,
    },
    {
      id: "polarInvasion",
      label: "Polar Invasion",
      requiredAllianceCount: 6,
      requiredNationCount: 0,
      supportedResultModes: ["alliance", "individual"],
      defaultResultMode: "alliance",
      supportsPlanner: false,
      plannerPlugin: "resultsOnlyPlanner",
      plannerMode: "none",
      analyticsPolicy: "include",
      sharePolicy: "unsupported",
      timeModel: "all_day_async",
      supportsBattles: true,
      supportsResults: true,
    },
    {
      id: "ironbloodBattlefield",
      label: "Ironblood Battlefield",
      requiredAllianceCount: 0,
      requiredNationCount: 2,
      supportedResultModes: ["individual", "nation", "alliance"],
      defaultResultMode: "individual",
      supportsPlanner: false,
      plannerPlugin: "resultsOnlyPlanner",
      plannerMode: "none",
      analyticsPolicy: "include",
      sharePolicy: "unsupported",
      timeModel: "all_day_realtime",
      supportsBattles: true,
      supportsResults: true,
    },
    {
      id: "wastelandShowdown",
      label: "Wasteland Showdown",
      requiredAllianceCount: 2,
      requiredNationCount: 0,
      supportedResultModes: ["alliance", "individual"],
      defaultResultMode: "alliance",
      supportsPlanner: false,
      plannerPlugin: "resultsOnlyPlanner",
      plannerMode: "none",
      analyticsPolicy: "include",
      sharePolicy: "unsupported",
      timeModel: "windowed_realtime",
      supportsBattles: true,
      supportsResults: true,
    },
    {
      id: "strongestFrenzyCommander",
      label: "Strongest Frenzy Commander",
      requiredAllianceCount: 0,
      requiredNationCount: 2,
      supportedResultModes: ["nation", "alliance", "individual"],
      defaultResultMode: "nation",
      supportsPlanner: true,
      plannerPlugin: "nationIntelRealtimePlanner",
      plannerMode: "nation_intel_realtime",
      analyticsPolicy: "include",
      sharePolicy: "public_token_supported",
      timeModel: "all_day_realtime",
      supportsBattles: true,
      supportsResults: true,
    },
    {
      id: "strongestVoidCommander",
      label: "Strongest Void Commander",
      requiredAllianceCount: 0,
      requiredNationCount: 2,
      supportedResultModes: ["nation", "alliance", "individual"],
      defaultResultMode: "nation",
      supportsPlanner: true,
      plannerPlugin: "nationIntelRealtimePlanner",
      plannerMode: "nation_intel_realtime",
      analyticsPolicy: "include",
      sharePolicy: "public_token_supported",
      timeModel: "all_day_realtime",
      supportsBattles: true,
      supportsResults: true,
    },
  ],
};


const TEMPLATE_LABEL_OPTIONS = [
  { value: "rowArea", label: "Row area (required)" },
  { value: "dateTime", label: "Date / Time" },
  { value: "commanderName", label: "Commander name" },
  { value: "points", label: "Points" },
  { value: "participantCount", label: "Number of participants" },
  { value: "allianceRanking", label: "Alliance ranking" },
  { value: "alliancePoints", label: "Alliance points" },
  // Legacy/internal compatibility labels
  { value: "nameRegion", label: "Name column (legacy)" },
  { value: "scoreRegion", label: "Score column (legacy)" },
  { value: "rankRegion", label: "Rank column (legacy)" },
];

const clamp01 = (value) => Math.max(0, Math.min(1, Number(value) || 0));

const toAbsoluteFromRow = (relative, rowArea) => {
  if (!relative || !rowArea) return null;
  return {
    x: clamp01(rowArea.x + relative.x * rowArea.w),
    y: clamp01(rowArea.y + relative.y * rowArea.h),
    w: clamp01(relative.w * rowArea.w),
    h: clamp01(relative.h * rowArea.h),
  };
};

const layoutToAnnotations = (layout) => {
  if (!layout?.rowArea) return [];
  const out = [{ id: 'ann-row', label: 'rowArea', box: layout.rowArea }];
  const name = toAbsoluteFromRow(layout.nameRegion, layout.rowArea);
  if (name) out.push({ id: 'ann-name', label: 'commanderName', box: name });
  const score = toAbsoluteFromRow(layout.scoreRegion, layout.rowArea);
  if (score) out.push({ id: 'ann-score', label: 'points', box: score });
  const rank = layout.rankRegion ? toAbsoluteFromRow(layout.rankRegion, layout.rowArea) : null;
  if (rank) out.push({ id: 'ann-rank', label: 'allianceRanking', box: rank });
  return out;
};

const annotationsToLayout = (annotations, rowsPerScreenshot, rowGapRatio) => {
  const rows = Math.max(1, Number(rowsPerScreenshot || 10));
  const byLabel = {};
  for (const row of annotations || []) {
    if (!row?.label || !row?.box) continue;
    if (!byLabel[row.label]) byLabel[row.label] = [];
    byLabel[row.label].push(row.box);
  }

  const rowAreaCandidates = byLabel.rowArea || [];
  const rowArea = rowAreaCandidates.length
    ? rowAreaCandidates
        .slice()
        .sort((a, b) => Number((b.w || 0) * (b.h || 0)) - Number((a.w || 0) * (a.h || 0)))[0]
    : null;
  if (!rowArea) return null;

  const toRowRelative = (box) => {
    if (!box || !rowArea?.w || !rowArea?.h) return null;
    const x = clamp01((box.x - rowArea.x) / rowArea.w);
    const w = clamp01(box.w / rowArea.w);

    const yRaw = ((box.y - rowArea.y) / rowArea.h) * rows;
    const y = clamp01(yRaw - Math.floor(yRaw));
    const h = clamp01((box.h / rowArea.h) * rows);

    return { x, y, w, h };
  };

  const pickAggregate = (aliases) => {
    const candidates = aliases.flatMap((label) => byLabel[label] || []);
    if (!candidates.length) return null;

    const rel = candidates
      .map((box) => toRowRelative(box))
      .filter(Boolean)
      .filter((box) => box.w > 0.01 && box.h > 0.01);

    if (!rel.length) return null;

    const median = (arr) => {
      const sorted = [...arr].sort((a, b) => a - b);
      return sorted[Math.floor(sorted.length / 2)];
    };

    return {
      x: median(rel.map((r) => r.x)),
      y: median(rel.map((r) => r.y)),
      w: median(rel.map((r) => r.w)),
      h: median(rel.map((r) => r.h)),
    };
  };

  const nameRegion = pickAggregate(["commanderName", "nameRegion"]);
  const scoreRegion = pickAggregate(["points", "scoreRegion", "alliancePoints"]);
  if (!nameRegion || !scoreRegion) return null;

  const layout = {
    rowArea,
    rowsPerScreenshot: rows,
    rowGapRatio: Math.max(0, Number(rowGapRatio || 0)),
    nameRegion,
    scoreRegion,
  };

  const rankRegion = pickAggregate(["allianceRanking", "rankRegion"]);
  if (rankRegion) {
    layout.rankRegion = rankRegion;
  }

  return layout;
};

const normalizeSelectionProfileDraft = (profile = {}) => ({
  resultsScope: profile?.resultsScope || "commander",
  layoutFamily: profile?.layoutFamily || "leaderboard",
  visibleRowsHintMin:
    profile?.visibleRowsHintMin == null || profile?.visibleRowsHintMin === ""
      ? ""
      : String(profile.visibleRowsHintMin),
  visibleRowsHintMax:
    profile?.visibleRowsHintMax == null || profile?.visibleRowsHintMax === ""
      ? ""
      : String(profile.visibleRowsHintMax),
  aspectRatioMin:
    profile?.aspectRatioMin == null || profile?.aspectRatioMin === ""
      ? ""
      : String(profile.aspectRatioMin),
  aspectRatioMax:
    profile?.aspectRatioMax == null || profile?.aspectRatioMax === ""
      ? ""
      : String(profile.aspectRatioMax),
  tagsText: Array.isArray(profile?.tags) ? profile.tags.join(", ") : String(profile?.tagsText || ""),
});

const buildSelectionProfilePayload = (profile = {}) => ({
  resultsScope: profile.resultsScope || "commander",
  layoutFamily: String(profile.layoutFamily || "leaderboard").trim() || "leaderboard",
  visibleRowsHintMin:
    profile.visibleRowsHintMin === "" || profile.visibleRowsHintMin == null
      ? null
      : Number(profile.visibleRowsHintMin),
  visibleRowsHintMax:
    profile.visibleRowsHintMax === "" || profile.visibleRowsHintMax == null
      ? null
      : Number(profile.visibleRowsHintMax),
  aspectRatioMin:
    profile.aspectRatioMin === "" || profile.aspectRatioMin == null
      ? null
      : Number(profile.aspectRatioMin),
  aspectRatioMax:
    profile.aspectRatioMax === "" || profile.aspectRatioMax == null
      ? null
      : Number(profile.aspectRatioMax),
  tags: String(profile.tagsText || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean),
});

const formatEventDate = (value) => {
  if (!value) return "No date";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "No date";
  return d.toLocaleDateString(undefined, { timeZone: "UTC" });
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const normalizeTime24Input = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(raw) ? raw : "";
};

const FIXED_FULL_DAY_EVENT_TYPES = new Set([
  "strongestFrenzyCommander",
  "strongestVoidCommander",
]);

const isFixedFullDayEventType = (eventTypeId) => (
  FIXED_FULL_DAY_EVENT_TYPES.has(String(eventTypeId || ""))
);

const deriveTime24FromStartsAt = (startsAt) => {
  if (!startsAt) return "";
  const d = new Date(startsAt);
  if (Number.isNaN(d.getTime())) return "";
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

const normalizeEventDateTime = (eventRow) => {
  if (!eventRow || typeof eventRow !== "object") return eventRow;
  const explicit = normalizeTime24Input(eventRow.startTime24);
  return {
    ...eventRow,
    startTime24: isFixedFullDayEventType(eventRow.eventType)
      ? ""
      : (explicit || deriveTime24FromStartsAt(eventRow.startsAt)),
  };
};

const formatEventDisplayLabel = (eventRow, eventTypeConfigById = {}) => {
  if (!eventRow || typeof eventRow !== "object") return "Event";
  const explicit = String(eventRow?.displayLabel || "").trim();
  if (explicit) return explicit;
  const typeLabel =
    eventTypeConfigById?.[eventRow?.eventType]?.label ||
    String(eventRow?.eventType || "Event");
  const dateLabel = eventRow?.startsAt ? formatEventDate(eventRow.startsAt) : "";
  const timeLabel = isFixedFullDayEventType(eventRow?.eventType)
    ? ""
    : String(eventRow?.startTime24 || "").trim();
  if (dateLabel && timeLabel) return `${typeLabel} ${dateLabel} ${timeLabel}`;
  if (dateLabel) return `${typeLabel} ${dateLabel}`;
  return typeLabel;
};

const buildStartsAtIso = (dateText, time24Text) => {
  if (!dateText) return null;
  const normalizedTime = normalizeTime24Input(time24Text);
  const isoText = normalizedTime
    ? `${dateText}T${normalizedTime}:00.000Z`
    : `${dateText}T00:00:00.000Z`;
  const d = new Date(isoText);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const Events = () => {
  const navigate = useNavigate();
  const { eventId: routeEventId = "" } = useParams();
  const {
    user,
    isAuthInitializing,
    authInitializationError,
    retryAuthBootstrap,
  } = useContext(AuthContext);
  const canReadEvents = HasPermission("readEvents");
  const canReadCommanders = HasPermission("readCommanders");
  const canWriteEvents = HasPermission("writeEvents");
  const canWriteCommanders = HasPermission("writeCommanders");
  const canDeleteEvents = HasPermission("deleteEvents");
  const canWriteNations = HasPermission("writeNations");
  const canWriteAlliances = HasPermission("writeAlliances");
  const role = String(user?.role || "");
  const isSuperUser = role === "superUser";
  const isSupporter = role === "supporter";
  const isNationAdmin = role === "nationAdmin";
  const canAccessAnalytics = isSuperUser || isSupporter || isNationAdmin;
  const routeSelectedEventId = String(routeEventId || "").trim();
  const isEventDetailRoute = Boolean(routeSelectedEventId);

  const nationId = useMemo(
    () => String(user?.nation?._id || user?.nation || ""),
    [user?.nation],
  );

  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(routeSelectedEventId);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventAllianceDraftIds, setEventAllianceDraftIds] = useState([]);
  const [eventAllianceSlotIds, setEventAllianceSlotIds] = useState([]);
  const [isSavingEventAlliances, setIsSavingEventAlliances] = useState(false);
  const [isEventAllianceEditorOpen, setIsEventAllianceEditorOpen] = useState(false);
  const [isSavingEventMeta, setIsSavingEventMeta] = useState(false);
  const [alliances, setAlliances] = useState([]);
  const [nations, setNations] = useState([]);
  const [commanders, setCommanders] = useState([]);
  const [scoreboard, setScoreboard] = useState([]);
  const [scoreboardsByDimension, setScoreboardsByDimension] = useState({
    nation: [],
    alliance: [],
    individual: [],
  });
  const [scoreboardSourcesByDimension, setScoreboardSourcesByDimension] = useState({
    nation: "",
    alliance: "",
    individual: "",
  });
  const [individualScoreboardType, setIndividualScoreboardType] = useState("round_player");
  const [isNationScoreBreakdownOpen, setIsNationScoreBreakdownOpen] = useState(false);
  const [recommendations, setRecommendations] = useState([]);

  const [eventTypeMeta, setEventTypeMeta] = useState(DEFAULT_EVENT_TYPE_META);
  const [eventType, setEventType] = useState(
    DEFAULT_EVENT_TYPE_META.defaultEventType,
  );
  const [eventTypeFilter, setEventTypeFilter] = useState("all");
  const [eventAllianceFilterId, setEventAllianceFilterId] = useState("all");
  const [eventSourceNationFilterId, setEventSourceNationFilterId] = useState("all");
  const [selectedAllianceIds, setSelectedAllianceIds] = useState(["", "", ""]);
  const [selectedParticipantNationIds, setSelectedParticipantNationIds] = useState(["", ""]);
  const [eventDate, setEventDate] = useState("");
  const [eventTime24, setEventTime24] = useState("");
  const [maxDefenders, setMaxDefenders] = useState(70);

  const [battleForm, setBattleForm] = useState({
    strongholdId: "",
    attackingAlliance: "",
    attackingCommander: "",
    defendingAlliance: "",
    defendingCommander: "",
    result: "attacker_win",
    pointsAwarded: "",
    source: "manual",
    notes: "",
    screenshotDataUrl: "",
  });

  const [resultForm, setResultForm] = useState({
    commanderId: "",
    allianceId: "",
    score: "",
    source: "manual",
    notes: "",
  });
  const [editingResultId, setEditingResultId] = useState("");
  const [isResultEntryModalOpen, setIsResultEntryModalOpen] = useState(false);

  const [resultEntryMode, setResultEntryMode] = useState("manual");
  const [uploadAllianceId, setUploadAllianceId] = useState("");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadReviewRows, setUploadReviewRows] = useState([]);
  const [uploadCommanderOptions, setUploadCommanderOptions] = useState([]);
  const [isProcessingUploads, setIsProcessingUploads] = useState(false);
  const [isApplyingUploadRows, setIsApplyingUploadRows] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [expandedRowPreview, setExpandedRowPreview] = useState(null);
  const [ocrTemplates, setOcrTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [isTemplateAdminOpen, setIsTemplateAdminOpen] = useState(false);
  const [isTemplateImageUploadOpen, setIsTemplateImageUploadOpen] = useState(false);
  const [templateUploadFiles, setTemplateUploadFiles] = useState([]);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [templateDraft, setTemplateDraft] = useState({
    _id: "",
    name: "",
    eventType: "",
    enabled: true,
    description: "",
    version: 1,
    rowsPerScreenshot: 10,
    rowGapRatio: 0.02,
    selectionProfile: {
      resultsScope: "commander",
      layoutFamily: "leaderboard",
      visibleRowsHintMin: "",
      visibleRowsHintMax: "",
      aspectRatioMin: "",
      aspectRatioMax: "",
      tagsText: "",
    },
    sourceImage: {
      dataUrl: "",
      fileName: "",
      mimeType: "",
      width: null,
      height: null,
    },
    annotations: [],
  });

  const [editingBattleId, setEditingBattleId] = useState("");
  const [battleLogSearch, setBattleLogSearch] = useState("");
  const [battleLogRuleFilter, setBattleLogRuleFilter] = useState("all");
  const [battleLogViewMode, setBattleLogViewMode] = useState("battles");
  const [battleLogDefenderNationFilter, setBattleLogDefenderNationFilter] = useState("all");
  const [battleLogReviewedFilter, setBattleLogReviewedFilter] = useState("all");
  const [battleLogAllianceFilterIds, setBattleLogAllianceFilterIds] = useState([]);
  const [battleLogCommanderFilterIds, setBattleLogCommanderFilterIds] = useState([]);
  const [battleLogSortConfig, setBattleLogSortConfig] = useState({ key: "time", direction: "desc" });
  const [expandedBubbleDropGroupIds, setExpandedBubbleDropGroupIds] = useState([]);
  const [expandedCommanderBattleGroupIds, setExpandedCommanderBattleGroupIds] = useState([]);
  const [reportBattleId, setReportBattleId] = useState("");
  const [reportBattleDetail, setReportBattleDetail] = useState(null);
  const [isLoadingReportBattleDetail, setIsLoadingReportBattleDetail] = useState(false);
  const [strikeBattleId, setStrikeBattleId] = useState("");
  const [strikeForm, setStrikeForm] = useState({
    commanderId: "",
    reasonCode: "",
    warningLevelDelta: "1",
    reasonNote: "",
    summaryLabel: "",
    summaryDetail: "",
    evidenceRefs: [],
    autoReviewBattleIds: [],
    autoReviewRelatedBattles: false,
    autoReviewRelatedReviewNote: "",
  });
  const [strikeSettings, setStrikeSettings] = useState({ defaultExpiryDays: 14, reasons: [] });
  const [isApplyingStrike, setIsApplyingStrike] = useState(false);
  const [existingEventCommanderStrikes, setExistingEventCommanderStrikes] = useState([]);
  const [isLoadingExistingEventStrikes, setIsLoadingExistingEventStrikes] = useState(false);
  const [reviewBattleId, setReviewBattleId] = useState("");
  const [reviewForm, setReviewForm] = useState({ reviewNote: "" });
  const [reviewingBattleId, setReviewingBattleId] = useState("");
  const [copiedReportRequestBattleId, setCopiedReportRequestBattleId] = useState("");
  const [isBattleEntryModalOpen, setIsBattleEntryModalOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [eventDateEditor, setEventDateEditor] = useState(null);
  const [isUploadLauncherOpen, setIsUploadLauncherOpen] = useState(false);
  const [createAndOpenUpload, setCreateAndOpenUpload] = useState(false);
  const [uploadLaunchConfig, setUploadLaunchConfig] = useState({
    eventType: "",
    allianceId: "all",
    eventId: "",
    visibleRows: "",
    resultsScope: "commander",
    eventDate: "",
    eventTime24: "",
    eventName: "",
  });
  const [pendingTemplateAutoSelect, setPendingTemplateAutoSelect] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [activeSection, setActiveSection] = useState("events");
  const [activeTab, setActiveTab] = useState("results");
  const [templateAdminEventType, setTemplateAdminEventType] = useState("");
  const [analyticsFilters, setAnalyticsFilters] = useState({
    eventTypes: [],
    startDate: "",
    endDate: "",
    nationIds: [],
    allianceIds: [],
    cityRoles: ["primary"],
    statuses: ["active"],
    eventIds: [],
    includeLegacyMembers: false,
  });
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
  const [analyticsSearch, setAnalyticsSearch] = useState("");
  const analyticsAllianceScopeRef = useRef("");
  const [resultsPanelOpen, setResultsPanelOpen] = useState({
    entry: true,
    nation: true,
    alliance: true,
    individual: true,
  });
  const [focusedIslandId, setFocusedIslandId] = useState("");
  const [isAutoPopulateOpen, setIsAutoPopulateOpen] = useState(false);
  const [autoPopulateMode, setAutoPopulateMode] = useState("standard");
  const [autoPopulateMinCityLevel, setAutoPopulateMinCityLevel] = useState(20);
  const [autoPopulateForceReset, setAutoPopulateForceReset] = useState(false);
  const [isAutoPopulating, setIsAutoPopulating] = useState(false);
  const [editingStrongholdId, setEditingStrongholdId] = useState("");
  const [slotEditor, setSlotEditor] = useState({
    defendingCommander: "",
    item: "",
  });

  const [zombiePlan, setZombiePlan] = useState({
    boardSlots: [],
    startItems: { orange: "", purple: "", blue: "", green: "" },
    waveItems: Array.from({ length: INFINITE_ZOMBIE_WAVE_COUNT }, (_, idx) => ({
      wave: idx + 1,
      item: "",
    })),
  });
  const [activeZombieSlotId, setActiveZombieSlotId] = useState("S1");
  const [isZombieSaving, setIsZombieSaving] = useState(false);
  const [zombieMapZoom, setZombieMapZoom] = useState(1);
  const [zombieMapPan, setZombieMapPan] = useState({ x: 0, y: 0 });
  const [isZombieMapDragging, setIsZombieMapDragging] = useState(false);
  const zombieMapRef = useRef(null);
  const zombieMapSuppressClickRef = useRef(false);
  const zombieMapDragRef = useRef({
    pointerId: null,
    dragging: false,
    moved: false,
    captured: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });
  const zombieMapPointersRef = useRef(new Map());
  const zombieMapPinchRef = useRef({
    active: false,
    startDistance: 0,
    startZoom: 1,
    startPan: { x: 0, y: 0 },
    startMidpoint: { x: 0, y: 0 },
  });

  const allianceNameById = useMemo(() => {
    const out = {};
    alliances.forEach((a) => {
      out[String(a._id)] = a.acronym || a.name;
    });
    commanders.forEach((c) => {
      const allianceId = String(c?.alliance?._id || c?.alliance || "");
      if (!allianceId) return;
      const label = c?.alliance?.acronym || c?.alliance?.name || "";
      if (label && !out[allianceId]) out[allianceId] = label;
    });
    return out;
  }, [alliances, commanders]);

  const commanderNameById = useMemo(() => {
    const out = {};
    commanders.forEach((c) => {
      out[String(c._id)] = c.name;
    });
    (recommendations || []).forEach((c) => {
      out[String(c._id)] = c.name;
    });
    return out;
  }, [commanders, recommendations]);

  const commanderPowerById = useMemo(() => {
    const out = {};
    commanders.forEach((c) => {
      out[String(c._id)] = Number(c.battlePower || 0);
    });
    (recommendations || []).forEach((c) => {
      const id = String(c._id);
      if (!out[id]) out[id] = Number(c.battlePower || 0);
    });
    return out;
  }, [commanders, recommendations]);

  const commanderById = useMemo(() => {
    const out = {};
    commanders.forEach((c) => {
      out[String(c._id)] = c;
    });
    (recommendations || []).forEach((c) => {
      const id = String(c._id);
      if (!out[id]) out[id] = c;
    });
    return out;
  }, [commanders, recommendations]);

  const selectedEventNationIdSet = useMemo(() => {
    return new Set(
      [selectedEvent?.nation, ...(selectedEvent?.participantNations || [])]
        .map((nation) => String(nation?._id || nation || ""))
        .filter(Boolean),
    );
  }, [selectedEvent?.nation, selectedEvent?.participantNations]);

  const selectedEventHomeNationId = useMemo(
    () => String(selectedEvent?.nation?._id || selectedEvent?.nation || nationId || ""),
    [nationId, selectedEvent?.nation],
  );

  useEffect(() => {
    if (!selectedEventHomeNationId || !canReadCommanders) {
      setStrikeSettings({ defaultExpiryDays: 14, reasons: [] });
      return;
    }
    let cancelled = false;
    axiosInstance.get(`/nations/${selectedEventHomeNationId}/strike-settings`)
      .then((response) => {
        if (cancelled) return;
        setStrikeSettings(response?.data?.strikeSettings || { defaultExpiryDays: 14, reasons: [] });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Error loading strike settings:", error);
        setStrikeSettings({ defaultExpiryDays: 14, reasons: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [canReadCommanders, selectedEventHomeNationId]);

  const allianceById = useMemo(() => {
    const out = {};
    (alliances || []).forEach((alliance) => {
      const id = String(alliance?._id || "");
      if (id) out[id] = alliance;
    });
    return out;
  }, [alliances]);

  const nationLabelById = useMemo(() => {
    const out = {};
    (nations || []).forEach((nation) => {
      const nationId = String(nation?._id || "");
      const activeLabel = formatNationNumberLabel(nation?.number);
      if (nationId && activeLabel) out[nationId] = activeLabel;
      (Array.isArray(nation?.consumedNations) ? nation.consumedNations : []).forEach((consumed) => {
        const consumedId = String(consumed?._id || "");
        const consumedLabel = formatNationNumberLabel(consumed?.number);
        if (!consumedId || !consumedLabel) return;
        out[consumedId] = activeLabel ? `${consumedLabel} -> ${activeLabel}` : consumedLabel;
      });
    });
    return out;
  }, [nations]);

  const eventNationAllianceTagMap = useMemo(() => {
    const out = new Map();
    (alliances || []).forEach((alliance) => {
      const allianceNationId = String(alliance?.nation?._id || alliance?.nation || "");
      if (!allianceNationId || !selectedEventNationIdSet.has(allianceNationId)) return;
      [alliance?.acronym, alliance?.name].forEach((value) => {
        const tag = String(value || "").trim().toLowerCase();
        if (!tag) return;
        const nationSet = out.get(tag) || new Set();
        nationSet.add(allianceNationId);
        out.set(tag, nationSet);
      });
    });
    return out;
  }, [alliances, selectedEventNationIdSet]);

  const eventAllianceIdsByNationTag = useMemo(() => {
    const out = new Map();
    (alliances || []).forEach((alliance) => {
      const allianceId = String(alliance?._id || "");
      const allianceNationId = String(alliance?.nation?._id || alliance?.nation || "");
      if (!allianceId || !allianceNationId || !selectedEventNationIdSet.has(allianceNationId)) return;
      [alliance?.acronym, alliance?.name].forEach((value) => {
        const tag = String(value || "").trim().toLowerCase();
        if (!tag) return;
        const key = `${allianceNationId}:${tag}`;
        const allianceIds = out.get(key) || new Set();
        allianceIds.add(allianceId);
        out.set(key, allianceIds);
      });
    });
    return out;
  }, [alliances, selectedEventNationIdSet]);

  const getAllianceNationId = useCallback((allianceId) => {
    const alliance = allianceById[String(allianceId || "")];
    const nation = String(alliance?.nation?._id || alliance?.nation || "");
    return selectedEventNationIdSet.has(nation) ? nation : "";
  }, [allianceById, selectedEventNationIdSet]);

  const getScopedRawAllianceKey = useCallback((rawTag, nationId) => {
    const tag = String(rawTag || "").trim().toLowerCase();
    const normalizedNationId = String(nationId || "");
    if (!tag || !normalizedNationId) return "";
    return `raw:${normalizedNationId}:${tag}`;
  }, []);

  const getRawAllianceCandidateNationIds = useCallback((rawTag) => {
    const tag = String(rawTag || "").trim().toLowerCase();
    if (!tag) return [];
    return Array.from(eventNationAllianceTagMap.get(tag) || [])
      .filter((candidateNationId) => selectedEventNationIdSet.has(String(candidateNationId || "")));
  }, [eventNationAllianceTagMap, selectedEventNationIdSet]);

  const getRawAllianceCandidateAllianceIds = useCallback((rawTag, nationId) => {
    const tag = String(rawTag || "").trim().toLowerCase();
    const normalizedNationId = String(nationId || "");
    if (!tag || !normalizedNationId) return [];
    return Array.from(eventAllianceIdsByNationTag.get(`${normalizedNationId}:${tag}`) || []);
  }, [eventAllianceIdsByNationTag]);

  const formatBattleLogAllianceOptionLabel = useCallback((label, nationId) => {
    const normalizedLabel = String(label || "").trim();
    const nationLabel = nationLabelById[String(nationId || "")] || "";
    return nationLabel ? `${normalizedLabel} (${nationLabel})` : normalizedLabel;
  }, [nationLabelById]);

  const getBattleNationRoleForSide = useCallback((battle, side) => {
    if (!battle || !selectedEventHomeNationId) return "";
    const normalizedSide = String(side || "").toLowerCase();
    const isAttacker = normalizedSide === "attacker";
    const isDefender = normalizedSide === "defender";
    if (!isAttacker && !isDefender) return "";
    const resolveSideNationId = (allianceId, rawTag) => {
      const alliance = allianceById[String(allianceId || "")];
      const allianceNationId = String(alliance?.nation?._id || alliance?.nation || "");
      if (allianceNationId && selectedEventNationIdSet.has(allianceNationId)) return allianceNationId;

      const tag = String(rawTag || "").trim().toLowerCase();
      const tagNationIds = tag ? Array.from(eventNationAllianceTagMap.get(tag) || []) : [];
      const eventNationMatches = tagNationIds.filter((id) => selectedEventNationIdSet.has(String(id || "")));
      return eventNationMatches.length === 1 ? eventNationMatches[0] : "";
    };
    const classifyNationId = (nationId) => {
      const normalizedNationId = String(nationId || "");
      if (!normalizedNationId) return "";
      return normalizedNationId === selectedEventHomeNationId ? "home" : "opponent";
    };

    const directNationId = isAttacker
      ? resolveSideNationId(battle.attackingAlliance, battle.attacker?.allianceShort)
      : resolveSideNationId(battle.defendingAlliance, battle.defender?.allianceShort);
    const directRole = classifyNationId(directNationId);
    if (directRole) return directRole;

    const matchingParticipantNations = new Set(
      (battle.participants || [])
        .filter((participant) => {
          const participantSide = String(participant?.side || "").toLowerCase();
          if (isAttacker) {
            return participantSide === "attacker" || participantSide === "attacking" || participantSide === "attackerer";
          }
          return participantSide === "defender" || participantSide === "defending" || participantSide === "defenderer";
        })
        .map((participant) => resolveSideNationId(participant?.alliance, participant?.allianceShort))
        .filter(Boolean),
    );
    if (matchingParticipantNations.size !== 1) return "";
    return classifyNationId(Array.from(matchingParticipantNations)[0]);
  }, [allianceById, eventNationAllianceTagMap, selectedEventHomeNationId, selectedEventNationIdSet]);

  const getBattleAttackerNationRole = useCallback(
    (battle) => getBattleNationRoleForSide(battle, "attacker"),
    [getBattleNationRoleForSide],
  );

  const getBattleDefenderNationRole = useCallback(
    (battle) => getBattleNationRoleForSide(battle, "defender"),
    [getBattleNationRoleForSide],
  );

  const getBattleHomeCommanderRefs = useCallback((battle) => {
    return getBattleStrikeCommanderRefs(battle).filter((ref) => {
      const commander = commanderById[String(ref.commanderId || "")];
      const commanderNationId = String(commander?.nation?._id || commander?.nation || "");
      if (commanderNationId) return commanderNationId === selectedEventHomeNationId;
      const side = String(ref.side || "").toLowerCase();
      if (side === "attacker" || side === "attacking") return getBattleAttackerNationRole(battle) === "home";
      if (side === "defender" || side === "defending") return getBattleDefenderNationRole(battle) === "home";
      return false;
    });
  }, [commanderById, getBattleAttackerNationRole, getBattleDefenderNationRole, selectedEventHomeNationId]);

  const bubbleDropByBattleId = useMemo(() => {
    const bubbleRules = (strikeSettings?.reasons || [])
      .filter((reason) => reason && reason.enabled !== false && String(reason.ruleType || "standard") === "bubble_drop");
    if (!bubbleRules.length) return new Map();

    const rowsByCity = new Map();
    (selectedEvent?.battles || []).forEach((battle) => {
      if (getBattleDefenderNationRole(battle) !== "home") return;
      if (normalizeBattleLocationType(battle) !== "city") return;
      const cityKey = getBattleCityCoordinateKey(battle);
      const rowKey = getBattleRowKey(battle);
      const timestampMs = getBattleTimestampMs(battle);
      if (!cityKey || !rowKey || !timestampMs) return;
      const ownerDefenderUnits = getBattleCityOwnerDefenderUnits(battle);
      if (!rowsByCity.has(cityKey)) rowsByCity.set(cityKey, []);
      rowsByCity.get(cityKey).push({
        battle,
        rowKey,
        cityKey,
        timestampMs,
        ownerDefenderUnits,
        defenderLabel: String(battle?.defender?.name || battle?.defender?.allianceShort || battle?.defendingCommander || "").trim(),
      });
    });

    const result = new Map();
    let groupIndex = 0;
    bubbleRules.forEach((rule) => {
      const ruleCode = String(rule.code || "bubble_drop");
      const windowMs = Math.max(1, asFiniteInteger(rule.timeframeMinutes, DEFAULT_BUBBLE_DROP_WINDOW_MINUTES)) * 60 * 1000;
      const minBattles = Math.max(1, asFiniteInteger(rule.minDefenses, DEFAULT_BUBBLE_DROP_MIN_BATTLES));
      const minDefenderUnits = Math.max(0, asFiniteInteger(rule.minDefenderUnits, 0));
      rowsByCity.forEach((rows, cityKey) => {
        const eligibleRows = rows.filter((row) => row.ownerDefenderUnits >= minDefenderUnits);
        const sortedRows = [...eligibleRows].sort((left, right) => left.timestampMs - right.timestampMs);
        let startIndex = 0;
        while (startIndex < sortedRows.length) {
          const start = sortedRows[startIndex];
          const windowRows = [];
          let endIndex = startIndex;
          while (
            endIndex < sortedRows.length
            && sortedRows[endIndex].timestampMs - start.timestampMs <= windowMs
          ) {
            windowRows.push(sortedRows[endIndex]);
            endIndex += 1;
          }

          if (windowRows.length >= minBattles) {
            groupIndex += 1;
            const defenderLabels = [...new Set(windowRows.map((row) => String(row.defenderLabel || "").trim()).filter(Boolean))];
            const startTime = Math.min(...windowRows.map((row) => row.timestampMs));
            const endTime = Math.max(...windowRows.map((row) => row.timestampMs));
            const group = {
              id: `bubble-${ruleCode}-${cityKey}-${groupIndex}`,
              ruleCode,
              ruleLabel: String(rule.label || "Bubble drop"),
              cityKey,
              battleCount: windowRows.length,
              defenderCount: defenderLabels.length,
              defenderLabels,
              minDefenderUnits,
              startTime,
              endTime,
              battleKeys: windowRows.map((row) => row.rowKey),
            };
            windowRows.forEach((row) => {
              const existing = result.get(row.rowKey) || [];
              if (!existing.some((item) => item.id === group.id)) {
                result.set(row.rowKey, [...existing, group]);
              }
            });
            startIndex = endIndex;
          } else {
            startIndex += 1;
          }
        }
      });
    });
    return result;
  }, [getBattleDefenderNationRole, selectedEvent?.battles, strikeSettings]);

  const getBubbleDropGroupsForBattle = useCallback((battle) => (
    bubbleDropByBattleId.get(getBattleRowKey(battle)) || []
  ), [bubbleDropByBattleId]);

  const getPrimaryBubbleDropGroup = useCallback((battle) => (
    getBubbleDropGroupsForBattle(battle)[0] || null
  ), [getBubbleDropGroupsForBattle]);

  const getBubbleDropScopeBattleIds = useCallback((battle, groupOverride = null) => {
    const fallbackBattleId = String(battle?._id || "").trim();
    const group = groupOverride || getPrimaryBubbleDropGroup(battle);
    const battleKeys = Array.isArray(group?.battleKeys) ? group.battleKeys.map((key) => String(key || "")) : [];
    if (!battleKeys.length) return fallbackBattleId ? [fallbackBattleId] : [];
    const battleIdByKey = new Map(
      (selectedEvent?.battles || []).map((row) => [String(getBattleRowKey(row)), String(row?._id || "")]),
    );
    const ids = battleKeys
      .map((key) => battleIdByKey.get(key))
      .filter(Boolean);
    if (fallbackBattleId && !ids.includes(fallbackBattleId)) ids.push(fallbackBattleId);
    return Array.from(new Set(ids));
  }, [getPrimaryBubbleDropGroup, selectedEvent?.battles]);

  const battleLogAllianceOptions = useMemo(() => {
    const optionsByValue = new Map();
    const addOption = (value, label, sortPower = 0) => {
      const normalizedValue = String(value || "").trim();
      const normalizedLabel = String(label || "").trim();
      if (!normalizedValue || !normalizedLabel || optionsByValue.has(normalizedValue)) return;
      optionsByValue.set(normalizedValue, {
        value: normalizedValue,
        label: normalizedLabel,
        sortPower: Number(sortPower || 0),
      });
    };
    const addAllianceIdOption = (allianceId) => {
      const id = String(allianceId || "").trim();
      if (!id) return;
      const alliance = allianceById[id];
      const nationIdForAlliance = getAllianceNationId(id);
      if (!alliance || !nationIdForAlliance) return;
      addOption(
        id,
        formatBattleLogAllianceOptionLabel(alliance?.acronym || alliance?.name || allianceNameById[id], nationIdForAlliance),
        alliance?.battlePower,
      );
    };
    const addRawOption = (rawTag, allianceId = "") => {
      const tag = String(rawTag || "").trim();
      if (!tag) return;
      const allianceNationId = getAllianceNationId(allianceId);
      if (allianceNationId) {
        const allianceIds = getRawAllianceCandidateAllianceIds(tag, allianceNationId);
        if (allianceIds.length === 1) {
          addAllianceIdOption(allianceIds[0]);
          return;
        }
        addOption(
          getScopedRawAllianceKey(tag, allianceNationId),
          formatBattleLogAllianceOptionLabel(tag, allianceNationId),
        );
        return;
      }
      const candidateNationIds = getRawAllianceCandidateNationIds(tag);
      if (candidateNationIds.length !== 1) return;
      const allianceIds = getRawAllianceCandidateAllianceIds(tag, candidateNationIds[0]);
      if (allianceIds.length === 1) {
        addAllianceIdOption(allianceIds[0]);
        return;
      }
      addOption(
        getScopedRawAllianceKey(tag, candidateNationIds[0]),
        formatBattleLogAllianceOptionLabel(tag, candidateNationIds[0]),
      );
    };

    alliances
      .filter((alliance) => selectedEventNationIdSet.has(String(alliance?.nation?._id || alliance?.nation || "")))
      .forEach((alliance) => {
        addAllianceIdOption(alliance?._id);
    });
    (selectedEvent?.battles || []).forEach((battle) => {
      addAllianceIdOption(battle.attackingAlliance);
      addAllianceIdOption(battle.defendingAlliance);
      addRawOption(battle.attacker?.allianceShort, battle.attackingAlliance);
      addRawOption(battle.defender?.allianceShort, battle.defendingAlliance);
      (battle.participants || []).forEach((participant) => {
        addAllianceIdOption(participant.alliance);
        addRawOption(participant.allianceShort, participant.alliance);
      });
    });

    return Array.from(optionsByValue.values())
      .sort((a, b) => {
        const powerDelta = Number(b.sortPower || 0) - Number(a.sortPower || 0);
        if (powerDelta) return powerDelta;
        return a.label.localeCompare(b.label);
      })
      .map(({ sortPower, ...option }) => option);
  }, [
    allianceById,
    allianceNameById,
    alliances,
    formatBattleLogAllianceOptionLabel,
    getAllianceNationId,
    getRawAllianceCandidateAllianceIds,
    getRawAllianceCandidateNationIds,
    getScopedRawAllianceKey,
    selectedEvent,
    selectedEventNationIdSet,
  ]);

  const battleLogCommanderOptions = useMemo(() => {
    const optionsByValue = new Map();
    const addOption = (value, label) => {
      const normalizedValue = String(value || "").trim();
      const normalizedLabel = String(label || "").trim();
      if (!normalizedValue || !normalizedLabel || optionsByValue.has(normalizedValue)) return;
      optionsByValue.set(normalizedValue, { value: normalizedValue, label: normalizedLabel });
    };

    commanders
      .filter((commander) => selectedEventNationIdSet.has(String(commander?.nation?._id || commander?.nation || "")))
      .forEach((commander) => {
      const allianceId = String(commander?.alliance?._id || commander?.alliance || "");
      const allianceLabel = allianceNameById[allianceId] || commander?.alliance?.acronym || commander?.alliance?.name || "";
      addOption(
        commander?._id,
        allianceLabel ? `${commander?.name || "Unknown"} (${allianceLabel})` : commander?.name,
      );
    });
    (selectedEvent?.battles || []).forEach((battle) => {
      addOption(battle.attackingCommander, commanderNameById[String(battle.attackingCommander || "")]);
      addOption(battle.defendingCommander, commanderNameById[String(battle.defendingCommander || "")]);
      addOption(
        battle.attacker?.gameCommanderId ? `game:${battle.attacker.gameCommanderId}` : "",
        battle.attacker?.name,
      );
      addOption(
        battle.defender?.gameCommanderId ? `game:${battle.defender.gameCommanderId}` : "",
        battle.defender?.name,
      );
      (battle.participants || []).forEach((participant) => {
        addOption(participant.commander, commanderNameById[String(participant.commander || "")]);
        addOption(
          participant.gameCommanderId ? `game:${participant.gameCommanderId}` : "",
          participant.name,
        );
      });
    });

    return Array.from(optionsByValue.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [allianceNameById, commanderNameById, commanders, selectedEvent, selectedEventNationIdSet]);

  const getBattleSideParticipants = useCallback((battle, side) => {
    const participants = Array.isArray(battle?.participants) ? battle.participants : [];
    return participants.filter((participant) => {
      const participantSide = String(participant?.side || "").toLowerCase();
      return participantSide === side || participantSide === `${side}er`;
    });
  }, []);

  const getBattleSideTroopTotal = useCallback((battle, side) => {
    const sideRows = getBattleSideParticipants(battle, side);
    if (sideRows.length) {
      return sideRows.reduce((sum, row) => sum + getParticipantActualUnitCount(row), 0);
    }
    const fallbackSide = side === "attacker" ? battle?.attacker : battle?.defender;
    return getActualUnitCountForTroops(fallbackSide?.troops || []) || Number(fallbackSide?.totalUnits || 0);
  }, [getBattleSideParticipants]);

  const getBattleSideLabel = useCallback((battle, side) => {
    const sideRows = getBattleSideParticipants(battle, side);
    const isAttacker = side === "attacker";
    const allianceId = isAttacker ? battle?.attackingAlliance : battle?.defendingAlliance;
    const rawAllianceShort = isAttacker ? battle?.attacker?.allianceShort : battle?.defender?.allianceShort;
    const commanderId = isAttacker ? battle?.attackingCommander : battle?.defendingCommander;
    const rawCommanderName = isAttacker ? battle?.attacker?.name : battle?.defender?.name;
    const allianceLabel = String(allianceNameById[String(allianceId || "")] || rawAllianceShort || "-").trim();
    const commanderLabel = String(rawCommanderName || commanderNameById[String(commanderId || "")] || "-").trim();
    const extraCount = Math.max(0, sideRows.length - 1);
    return `(${allianceLabel || "-"}) ${commanderLabel || "-"}${extraCount > 0 ? ` +${extraCount}` : ""}`;
  }, [allianceNameById, commanderNameById, getBattleSideParticipants]);

  const getBattleSideReportRequestLabel = useCallback((battle, side) => {
    const sideRows = getBattleSideParticipants(battle, side);
    if (!sideRows.length) return getBattleSideLabel(battle, side);
    const labels = sideRows
      .map((participant) => {
        const allianceId = String(participant?.alliance || "").trim();
        const commanderId = String(participant?.commander || "").trim();
        const allianceLabel = String(
          allianceNameById[allianceId]
          || participant?.allianceShort
          || "-"
        ).trim();
        const commanderLabel = String(
          participant?.name
          || commanderNameById[commanderId]
          || "-"
        ).trim();
        return `(${allianceLabel || "-"}) ${commanderLabel || "-"}`;
      })
      .filter(Boolean);
    return Array.from(new Set(labels)).join(", ") || getBattleSideLabel(battle, side);
  }, [allianceNameById, commanderNameById, getBattleSideLabel, getBattleSideParticipants]);

  const getBattleLogSortValue = useCallback((battle, key) => {
    switch (key) {
      case "time":
        return new Date(battle?.at || battle?.battleTime || 0).getTime() || 0;
      case "battleLog":
        return getBattleEvidence(battle).battleLog ? 1 : 0;
      case "replay":
        return getBattleEvidence(battle).replay ? 1 : 0;
      case "report":
        return getBattleEvidence(battle).report ? 1 : 0;
      case "attacker":
        return getBattleSideLabel(battle, "attacker").toLowerCase();
      case "attackerTroops":
        return getBattleSideTroopTotal(battle, "attacker");
      case "defender":
        return getBattleSideLabel(battle, "defender").toLowerCase();
      case "defenderTroops":
        return getBattleSideTroopTotal(battle, "defender");
      case "homeNet":
        if (battle?.netPoints != null) return asFiniteInteger(battle.netPoints, 0);
        if (battle?.pointsGained != null || battle?.pointsLeaked != null) {
          return asFiniteInteger(battle.pointsGained, 0) - asFiniteInteger(battle.pointsLeaked, 0);
        }
        return 0;
      case "expectedWinner":
        return getBattleExpectedWinner(battle).toLowerCase();
      case "location":
        return String(
          battle?.location?.label
          || EVENT_BATTLE_LOCATION_LABELS[Number(battle?.location?.nodeType || battle?.locationNodeType || battle?.battleTypeCode || 0)]
          || battle?.strongholdId
          || "",
        ).toLowerCase();
      case "triage":
        return (
          (BATTLE_RISK_RANK[getBattleRiskLevel(battle)] * 1000000)
          + (BATTLE_RISK_RANK[getBattleExchangeLevel(battle)] * 10000)
          + (getBattleStrikeCandidateState(battle, strikeSettings, {
            attackerNationRole: getBattleAttackerNationRole(battle),
            defenderNationRole: getBattleDefenderNationRole(battle),
            bubbleDropGroups: getBubbleDropGroupsForBattle(battle),
          }).isCandidate ? 1000 : 0)
          + (getPrimaryBubbleDropGroup(battle) ? 500 : 0)
          + (getBattleNeedsReport(battle) ? 100 : 0)
          + Math.abs(asFiniteInteger(battle?.netPoints, 0))
        );
      default:
        return "";
    }
  }, [getBattleAttackerNationRole, getBattleDefenderNationRole, getBattleSideLabel, getBattleSideTroopTotal, getBubbleDropGroupsForBattle, getPrimaryBubbleDropGroup, strikeSettings]);

  const toggleBattleLogSort = useCallback((key) => {
    setBattleLogSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "desc" ? "asc" : "desc",
    }));
  }, []);

  const renderBattleLogSortHeader = useCallback((key, label) => {
    const active = battleLogSortConfig.key === key;
    const marker = active ? (battleLogSortConfig.direction === "asc" ? "^" : "v") : "";
    return (
      <button
        type="button"
        className={`events-battle-log-sort-btn${active ? " active" : ""}`}
        onClick={() => toggleBattleLogSort(key)}
      >
        <span>{label}</span>
        <span className="events-battle-log-sort-marker">{marker}</span>
      </button>
    );
  }, [battleLogSortConfig, toggleBattleLogSort]);

  const toggleBubbleDropGroup = useCallback((groupId) => {
    const normalizedGroupId = String(groupId || "").trim();
    if (!normalizedGroupId) return;
    setExpandedBubbleDropGroupIds((current) => (
      current.includes(normalizedGroupId)
        ? current.filter((value) => value !== normalizedGroupId)
        : [...current, normalizedGroupId]
    ));
  }, []);

  const toggleCommanderBattleGroup = useCallback((groupId) => {
    const normalizedGroupId = String(groupId || "").trim();
    if (!normalizedGroupId) return;
    setExpandedCommanderBattleGroupIds((current) => (
      current.includes(normalizedGroupId)
        ? current.filter((value) => value !== normalizedGroupId)
        : [...current, normalizedGroupId]
    ));
  }, []);

  const getBattleAllianceFilterValues = useCallback((battle) => {
    const values = new Set();
    const addAllianceValue = (allianceId, rawTag) => {
      const allianceIdString = String(allianceId || "").trim();
      const allianceNationId = getAllianceNationId(allianceIdString);
      if (allianceIdString && allianceNationId) {
        values.add(allianceIdString);
      }
      const candidateNationIds = allianceNationId
        ? [allianceNationId]
        : getRawAllianceCandidateNationIds(rawTag);
      if (candidateNationIds.length === 1) {
        const rawAllianceIds = getRawAllianceCandidateAllianceIds(rawTag, candidateNationIds[0]);
        if (rawAllianceIds.length === 1) {
          values.add(rawAllianceIds[0]);
        }
        const rawKey = getScopedRawAllianceKey(rawTag, candidateNationIds[0]);
        if (rawKey) values.add(rawKey);
      }
    };

    addAllianceValue(battle?.attackingAlliance, battle?.attacker?.allianceShort);
    addAllianceValue(battle?.defendingAlliance, battle?.defender?.allianceShort);
    (battle?.participants || []).forEach((participant) => {
      addAllianceValue(participant?.alliance, participant?.allianceShort);
    });
    return Array.from(values);
  }, [
    getAllianceNationId,
    getRawAllianceCandidateAllianceIds,
    getRawAllianceCandidateNationIds,
    getScopedRawAllianceKey,
  ]);

  const filteredEventBattles = useMemo(() => {
    const search = battleLogSearch.trim().toLowerCase();
    const selectedAllianceSet = new Set(battleLogAllianceFilterIds.map((value) => String(value)));
    const selectedCommanderSet = new Set(battleLogCommanderFilterIds.map((value) => String(value)));
    const selectedRuleCode = String(battleLogRuleFilter || "").startsWith("rule:")
      ? String(battleLogRuleFilter).slice(5)
      : "";
    return (selectedEvent?.battles || [])
      .filter((battle) => {
        const bubbleDropGroups = getBubbleDropGroupsForBattle(battle);
        const ruleMatches = getConfiguredStrikeMatches(
          battle,
          strikeSettings,
          {
            attackerNationRole: getBattleAttackerNationRole(battle),
            defenderNationRole: getBattleDefenderNationRole(battle),
            bubbleDropGroups,
          },
        );
        if (battleLogRuleFilter === "none" && ruleMatches.length > 0) return false;
        if (selectedRuleCode && !ruleMatches.some((reason) => String(reason.code || "") === selectedRuleCode)) return false;
        if (battleLogDefenderNationFilter !== "all" && getBattleDefenderNationRole(battle) !== battleLogDefenderNationFilter) return false;
        const isReviewed = Boolean(battle.review?.reviewedAt || battle.review?.reviewed);
        if (battleLogReviewedFilter === "unreviewed" && isReviewed) return false;
        if (battleLogReviewedFilter === "reviewed" && !isReviewed) return false;

        if (selectedAllianceSet.size > 0) {
          const battleAllianceValues = getBattleAllianceFilterValues(battle);
          if (!battleAllianceValues.some((value) => selectedAllianceSet.has(value))) return false;
        }

        if (selectedCommanderSet.size > 0) {
          const battleCommanderValues = [
            battle.attackingCommander,
            battle.defendingCommander,
            battle.attacker?.gameCommanderId ? `game:${battle.attacker.gameCommanderId}` : "",
            battle.defender?.gameCommanderId ? `game:${battle.defender.gameCommanderId}` : "",
            ...(battle.participants || []).flatMap((p) => [
              p.commander,
              p.gameCommanderId ? `game:${p.gameCommanderId}` : "",
            ]),
          ].map((value) => String(value || "")).filter(Boolean);
          if (!battleCommanderValues.some((value) => selectedCommanderSet.has(value))) return false;
        }

        if (!search) return true;
        const haystack = [
          battle.source,
          battle.enrichmentStatus,
          battle.attacker?.name,
          battle.attacker?.allianceShort,
          battle.defender?.name,
          battle.defender?.allianceShort,
          allianceNameById[String(battle.attackingAlliance || "")],
          allianceNameById[String(battle.defendingAlliance || "")],
          commanderNameById[String(battle.attackingCommander || "")],
          commanderNameById[String(battle.defendingCommander || "")],
          ...(battle.participants || []).flatMap((p) => [p.name, p.allianceShort]),
        ].filter(Boolean).join(" ").toLowerCase();
        return haystack.includes(search);
      })
      .slice()
      .sort((a, b) => {
        const left = getBattleLogSortValue(a, battleLogSortConfig.key);
        const right = getBattleLogSortValue(b, battleLogSortConfig.key);
        const direction = battleLogSortConfig.direction === "asc" ? 1 : -1;
        let comparison = 0;
        if (typeof left === "number" && typeof right === "number") {
          comparison = left - right;
        } else {
          comparison = String(left ?? "").localeCompare(String(right ?? ""), undefined, {
            numeric: true,
            sensitivity: "base",
          });
        }
        if (comparison === 0) {
          comparison = (new Date(b.at || b.battleTime || 0).getTime() || 0) - (new Date(a.at || a.battleTime || 0).getTime() || 0);
        }
        return comparison * direction;
      });
  }, [
    selectedEvent,
    battleLogSearch,
    battleLogRuleFilter,
    battleLogDefenderNationFilter,
    battleLogReviewedFilter,
    battleLogAllianceFilterIds,
    battleLogCommanderFilterIds,
    strikeSettings,
    allianceNameById,
    commanderNameById,
    battleLogSortConfig,
    getBattleAllianceFilterValues,
    getBattleAttackerNationRole,
    getBattleDefenderNationRole,
    getBattleLogSortValue,
    getBubbleDropGroupsForBattle,
  ]);

  const selectedReportBattle = useMemo(() => {
    if (!reportBattleId) return null;
    const summaryBattle = (selectedEvent?.battles || []).find((battle) => String(battle?._id || "") === String(reportBattleId)) || null;
    if (!reportBattleDetail || String(reportBattleDetail?._id || "") !== String(reportBattleId)) {
      return summaryBattle;
    }
    return {
      ...(summaryBattle || {}),
      ...reportBattleDetail,
    };
  }, [reportBattleDetail, reportBattleId, selectedEvent]);

  const selectedStrikeBattle = useMemo(() => {
    if (!strikeBattleId) return null;
    return (selectedEvent?.battles || []).find((battle) => String(battle?._id || "") === String(strikeBattleId)) || null;
  }, [strikeBattleId, selectedEvent]);

  const selectedReviewBattle = useMemo(() => {
    if (!reviewBattleId) return null;
    return (selectedEvent?.battles || []).find((battle) => String(battle?._id || "") === String(reviewBattleId)) || null;
  }, [reviewBattleId, selectedEvent]);

  const strikeCommanderOptions = useMemo(() => (
    getBattleStrikeCommanderRefs(selectedStrikeBattle || {}).map((ref) => {
      const allianceLabel = allianceNameById[ref.allianceId] || ref.allianceShort || "-";
      const commanderLabel = ref.name || commanderNameById[ref.commanderId] || "Unknown commander";
      const sideLabel = ref.side ? `${ref.side.charAt(0).toUpperCase()}${ref.side.slice(1)}` : "Participant";
      return {
        value: ref.commanderId,
        label: `${sideLabel}: (${allianceLabel}) ${commanderLabel}`,
      };
    })
  ), [allianceNameById, commanderNameById, selectedStrikeBattle]);

  const selectedStrikeCommanderLabel = useMemo(() => (
    strikeCommanderOptions.find((option) => String(option.value || "") === String(strikeForm.commanderId || ""))?.label
    || "This commander"
  ), [strikeCommanderOptions, strikeForm.commanderId]);

  const strikeReasonOptions = useMemo(() => {
    const configuredMatches = getConfiguredStrikeMatches(
      selectedStrikeBattle || {},
      strikeSettings,
      {
        attackerNationRole: getBattleAttackerNationRole(selectedStrikeBattle),
        defenderNationRole: getBattleDefenderNationRole(selectedStrikeBattle),
        bubbleDropGroups: getBubbleDropGroupsForBattle(selectedStrikeBattle),
      },
    );
    const enabledReasons = (strikeSettings?.reasons || []).filter((reason) => reason?.enabled !== false);
    const source = configuredMatches.length ? configuredMatches : enabledReasons;
    return source.map((reason) => ({
      value: String(reason.code || ""),
      label: String(reason.label || reason.code || "Strike reason"),
      warningLevelDelta: String(reason.warningLevelDelta || 1),
      reasonNoteTemplate: String(reason.reasonNoteTemplate || ""),
    })).filter((reason) => reason.value);
  }, [getBattleAttackerNationRole, getBattleDefenderNationRole, getBubbleDropGroupsForBattle, selectedStrikeBattle, strikeSettings]);

  useEffect(() => {
    const commanderId = String(strikeForm.commanderId || "").trim();
    const eventId = String(selectedEventId || selectedEvent?._id || "").trim();
    if (!strikeBattleId || !commanderId || !eventId || !selectedEventHomeNationId) {
      setExistingEventCommanderStrikes([]);
      setIsLoadingExistingEventStrikes(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoadingExistingEventStrikes(true);
    axiosInstance.get("/commander-strikes", {
      params: {
        commanderId,
        nationId: selectedEventHomeNationId,
        eventId,
        status: "all",
        limit: 50,
      },
    })
      .then((response) => {
        if (cancelled) return;
        const strikes = Array.isArray(response?.data?.strikes) ? response.data.strikes : [];
        setExistingEventCommanderStrikes(strikes.filter((strike) => (
          String(strike?.effectiveStatus || strike?.status || "").toLowerCase() !== "reversed"
        )));
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Error checking existing event strikes:", error);
          setExistingEventCommanderStrikes([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingExistingEventStrikes(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedEvent?._id, selectedEventHomeNationId, selectedEventId, strikeBattleId, strikeForm.commanderId]);

  const battleLogRuleFilterOptions = useMemo(() => {
    const options = [
      { value: "all", label: "All rows" },
      { value: "none", label: "No rules applied" },
    ];
    (strikeSettings?.reasons || [])
      .filter((reason) => reason && reason.enabled !== false && reason.code)
      .forEach((reason) => {
        const ruleType = String(reason.ruleType || "standard");
        const typeLabel = STRIKE_RULE_TYPE_LABELS[ruleType] || "Configured rule";
        const label = String(reason.label || reason.code || typeLabel);
        const optionLabel = ruleType === "standard" || label.toLowerCase() === typeLabel.toLowerCase()
          ? label
          : `${typeLabel}: ${label}`;
        options.push({
          value: `rule:${String(reason.code)}`,
          label: optionLabel,
          ruleType,
        });
      });
    return options;
  }, [strikeSettings]);

  const formatBattleLogTime = useCallback((value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }, []);

  const formatBattleServerTimeForCopy = useCallback((value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes} UTC`;
  }, []);

  const renderBattleDataMark = useCallback((available, label) => (
    <span
      className={`events-battle-data-mark ${available ? "available" : "missing"}`}
      aria-label={`${label}: ${available ? "available" : "missing"}`}
      title={`${label}: ${available ? "available" : "missing"}`}
    >
      {available ? "\u2713" : "X"}
    </span>
  ), []);

  const formatBattleLocation = useCallback((battle) => {
    const rawLocation = battle?.location && typeof battle.location === "object" ? battle.location : {};
    const nodeType = Number(rawLocation.nodeType || battle?.locationNodeType || battle?.battleTypeCode || 0);
    const label = String(
      rawLocation.label
      || EVENT_BATTLE_LOCATION_LABELS[nodeType]
      || (nodeType > 0 ? "Unknown" : "")
    ).trim();
    const coordinates = rawLocation.coordinates && typeof rawLocation.coordinates === "object"
      ? rawLocation.coordinates
      : battle?.coordinates;
    const x = coordinates?.x;
    const y = coordinates?.y;
    const coordinateLabel = x || y ? `${x ?? "-"},${y ?? "-"}` : "";
    if (label && coordinateLabel) return `${label} (${coordinateLabel})`;
    if (label) return label;
    if (coordinateLabel) return coordinateLabel;
    return selectedEvent?.strongholds?.find((s) => String(s._id) === String(battle?.strongholdId))?.name || "-";
  }, [selectedEvent?.strongholds]);

  const buildBattleReportRequestText = useCallback((battle) => {
    return [
      "Please provide info on this battle, either report in AC or explanation",
      `Server Time: ${formatBattleServerTimeForCopy(battle?.at || battle?.battleTime)}`,
      `Location: ${formatBattleLocation(battle)}`,
      `Attacker(s): ${getBattleSideReportRequestLabel(battle, "attacker")}`,
      `Defender(s): ${getBattleSideReportRequestLabel(battle, "defender")}`,
    ].join("\n");
  }, [
    formatBattleLocation,
    formatBattleServerTimeForCopy,
    getBattleSideReportRequestLabel,
  ]);

  const formatBattlePoints = useCallback((value, pointSource = "actual_report") => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return "-";
    const label = Math.trunc(parsed).toLocaleString();
    return pointSource === "replay_estimate" ? `~${label}` : label;
  }, []);

  const formatSignedBattlePoints = useCallback((value, pointSource = "actual_report") => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return "-";
    const sign = parsed > 0 ? "+" : (parsed < 0 ? "-" : "");
    const label = Math.abs(Math.trunc(parsed)).toLocaleString();
    const prefix = pointSource === "replay_estimate" ? "~" : "";
    return `${prefix}${sign}${label}`;
  }, []);

  const getId = (value) => String(value?._id || value || "");

  const selectedEventAllianceSet = useMemo(() => {
    return new Set(
      (selectedEvent?.alliances || []).map((a) => getId(a)).filter(Boolean),
    );
  }, [selectedEvent?.alliances]);

  const selectedEventNationSet = useMemo(() => {
    return new Set(
      [selectedEvent?.nation, ...(selectedEvent?.participantNations || [])]
        .map((n) => getId(n))
        .filter(Boolean),
    );
  }, [selectedEvent?.nation, selectedEvent?.participantNations]);

  const eventAlliances = useMemo(() => {
    const explicit = alliances.filter((a) => selectedEventAllianceSet.has(getId(a?._id)));
    const nationScoped = alliances.filter((a) => selectedEventNationSet.has(getId(a?.nation)));

    const combined = [...explicit];
    nationScoped.forEach((alliance) => {
      if (!combined.some((x) => String(x?._id) === String(alliance?._id))) {
        combined.push(alliance);
      }
    });

    // For cross-nation events (e.g. frenzy/void), expose the full nation-scoped alliance list.
    if (selectedEventNationSet.size > 1) return combined;
    return explicit.length ? explicit : nationScoped;
  }, [alliances, selectedEventAllianceSet, selectedEventNationSet]);

  const eventAllianceIdSet = useMemo(
    () => new Set((eventAlliances || []).map((a) => getId(a?._id)).filter(Boolean)),
    [eventAlliances],
  );

  const getBattleNationHighlight = useCallback((battle) => {
    if (!battle || !selectedEventHomeNationId) return "";
    const resolveSideNationId = (allianceId, rawTag) => {
      const tag = String(rawTag || "").trim().toLowerCase();
      const tagNationIds = tag ? eventNationAllianceTagMap.get(tag) : null;
      if (tagNationIds?.size) {
        const opponentNationId = Array.from(tagNationIds).find((id) => id !== selectedEventHomeNationId);
        if (opponentNationId) return opponentNationId;
        if (tagNationIds.has(selectedEventHomeNationId)) return selectedEventHomeNationId;
      }
      const alliance = allianceById[String(allianceId || "")];
      return String(alliance?.nation?._id || alliance?.nation || "");
    };

    const attackerNationId = resolveSideNationId(battle.attackingAlliance, battle.attacker?.allianceShort);
    const defenderNationId = resolveSideNationId(battle.defendingAlliance, battle.defender?.allianceShort);
    if (attackerNationId && defenderNationId && attackerNationId === defenderNationId) return "same-nation";
    if (attackerNationId === selectedEventHomeNationId) return "attacking";
    if (defenderNationId === selectedEventHomeNationId) return "defending";

    const participants = Array.isArray(battle.participants) ? battle.participants : [];
    const participantSideHasHomeNation = (side) => participants.some((participant) => {
      const participantSide = String(participant?.side || "").toLowerCase();
      if (participantSide !== side && participantSide !== `${side}er`) return false;
      return resolveSideNationId(participant?.alliance, participant?.allianceShort) === selectedEventHomeNationId;
    });

    const attackerParticipantNations = new Set(
      participants
        .filter((participant) => {
          const participantSide = String(participant?.side || "").toLowerCase();
          return participantSide === "attacker" || participantSide === "attackerer";
        })
        .map((participant) => resolveSideNationId(participant?.alliance, participant?.allianceShort))
        .filter(Boolean),
    );
    const defenderParticipantNations = new Set(
      participants
        .filter((participant) => {
          const participantSide = String(participant?.side || "").toLowerCase();
          return participantSide === "defender" || participantSide === "defenderer";
        })
        .map((participant) => resolveSideNationId(participant?.alliance, participant?.allianceShort))
        .filter(Boolean),
    );
    if (
      attackerParticipantNations.size === 1
      && defenderParticipantNations.size === 1
      && Array.from(attackerParticipantNations)[0] === Array.from(defenderParticipantNations)[0]
    ) {
      return "same-nation";
    }

    if (participantSideHasHomeNation("attacker")) return "attacking";
    if (participantSideHasHomeNation("defender")) return "defending";
    return "";
  }, [allianceById, eventNationAllianceTagMap, selectedEventHomeNationId]);

  const getBattleHomeTotals = useCallback((battle) => {
    if (battle?.pointsGained != null || battle?.pointsLeaked != null) {
      return {
        gained: battle.pointsGained == null ? null : asFiniteInteger(battle.pointsGained, 0),
        leaked: battle.pointsLeaked == null ? null : asFiniteInteger(battle.pointsLeaked, 0),
      };
    }
    const highlight = getBattleNationHighlight(battle);
    if (highlight === "same-nation") return { gained: 0, leaked: 0 };
    if (highlight === "attacking") {
      return {
        gained: asFiniteInteger(getBattlePointValue(battle, "attacker"), 0),
        leaked: asFiniteInteger(getBattlePointValue(battle, "defender"), 0),
      };
    }
    if (highlight === "defending") {
      return {
        gained: asFiniteInteger(getBattlePointValue(battle, "defender"), 0),
        leaked: asFiniteInteger(getBattlePointValue(battle, "attacker"), 0),
      };
    }
    return { gained: null, leaked: null };
  }, [getBattleNationHighlight]);

  const summarizeBattleLogRows = useCallback((rows) => {
    const summary = {
      totalCount: 0,
      exactGained: 0,
      exactOpponentPoints: 0,
      exactNet: 0,
      estimatedGained: 0,
      estimatedOpponentPoints: 0,
      estimatedNet: 0,
      highRiskCount: 0,
      largeExchangeCount: 0,
      unresolvedImpactCount: 0,
      strikeCandidateCount: 0,
      strikeReviewCount: 0,
      bubbleDropCount: 0,
      needsReportCount: 0,
      evidence: { battleLog: 0, replay: 0, report: 0 },
    };
    (rows || []).forEach((battle) => {
      summary.totalCount += 1;
      const evidence = getBattleEvidence(battle);
      if (evidence.battleLog) summary.evidence.battleLog += 1;
      if (evidence.replay) summary.evidence.replay += 1;
      if (evidence.report) summary.evidence.report += 1;
      const riskLevel = getBattleRiskLevel(battle);
      if (BATTLE_RISK_RANK[riskLevel] >= BATTLE_RISK_RANK.high) summary.highRiskCount += 1;
      const exchangeLevel = getBattleExchangeLevel(battle);
      if (BATTLE_RISK_RANK[exchangeLevel] >= BATTLE_RISK_RANK.high) summary.largeExchangeCount += 1;
      if (getBattleImpactStatus(battle) === "unresolved") summary.unresolvedImpactCount += 1;
      const strikeCandidateState = getBattleStrikeCandidateState(
        battle,
        strikeSettings,
        {
          attackerNationRole: getBattleAttackerNationRole(battle),
          defenderNationRole: getBattleDefenderNationRole(battle),
          bubbleDropGroups: getBubbleDropGroupsForBattle(battle),
        },
      );
      const strikeLevel = getBattleStrikeReviewLevel(battle);
      if (strikeCandidateState.isCandidate) summary.strikeCandidateCount += 1;
      if (strikeLevel === "review") summary.strikeReviewCount += 1;
      if (getPrimaryBubbleDropGroup(battle)) summary.bubbleDropCount += 1;
      if (getBattleNeedsReport(battle)) summary.needsReportCount += 1;
      const pointSource = getBattlePointSource(battle);
      const totals = getBattleHomeTotals(battle);
      if (totals.gained == null && totals.leaked == null) return;
      const gained = asFiniteInteger(totals.gained, 0);
      const opponentPoints = asFiniteInteger(totals.leaked, 0);
      const net = gained - opponentPoints;
      if (pointSource === "actual_report") {
        summary.exactGained += gained;
        summary.exactOpponentPoints += opponentPoints;
        summary.exactNet += net;
      } else if (pointSource === "replay_estimate") {
        summary.estimatedGained += gained;
        summary.estimatedOpponentPoints += opponentPoints;
        summary.estimatedNet += net;
      }
    });
    return summary;
  }, [getBattleAttackerNationRole, getBattleDefenderNationRole, getBattleHomeTotals, getBubbleDropGroupsForBattle, getPrimaryBubbleDropGroup, strikeSettings]);

  const battleLogLeadershipSummary = useMemo(
    () => summarizeBattleLogRows(selectedEvent?.battles || []),
    [selectedEvent?.battles, summarizeBattleLogRows],
  );

  const filteredBattleLogSummary = useMemo(
    () => summarizeBattleLogRows(filteredEventBattles),
    [filteredEventBattles, summarizeBattleLogRows],
  );

  const battleLogDisplayRows = useMemo(() => {
    const selectedRuleCode = String(battleLogRuleFilter || "").startsWith("rule:")
      ? String(battleLogRuleFilter).slice(5)
      : "";
    const selectedRule = selectedRuleCode
      ? (strikeSettings?.reasons || []).find((reason) => String(reason?.code || "") === selectedRuleCode)
      : null;
    const compareBattleTimeAscending = (left, right) => {
      const timestampComparison = (getBattleTimestampMs(left) || 0) - (getBattleTimestampMs(right) || 0);
      if (timestampComparison !== 0) return timestampComparison;
      return String(getBattleRowKey(left)).localeCompare(String(getBattleRowKey(right)), undefined, {
        numeric: true,
        sensitivity: "base",
      });
    };

    const compareByCurrentSort = (left, right) => {
      const leftValue = getBattleLogSortValue(left, battleLogSortConfig.key);
      const rightValue = getBattleLogSortValue(right, battleLogSortConfig.key);
      const direction = battleLogSortConfig.direction === "asc" ? 1 : -1;
      let comparison = 0;
      if (typeof leftValue === "number" && typeof rightValue === "number") {
        comparison = leftValue - rightValue;
      } else {
        comparison = String(leftValue ?? "").localeCompare(String(rightValue ?? ""), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }
      if (comparison === 0) {
        comparison = (getBattleTimestampMs(right) || 0) - (getBattleTimestampMs(left) || 0);
      }
      return comparison * direction;
    };

    if (battleLogViewMode === "commanders") {
      const expandedCommanderGroupIds = new Set(expandedCommanderBattleGroupIds.map((value) => String(value)));
      const groupsByCommanderId = new Map();
      filteredEventBattles.forEach((battle) => {
        getBattleHomeCommanderRefs(battle).forEach((ref) => {
          const commanderId = String(ref.commanderId || "");
          if (!commanderId) return;
          if (!groupsByCommanderId.has(commanderId)) {
            groupsByCommanderId.set(commanderId, { commanderRef: ref, rows: [] });
          }
          groupsByCommanderId.get(commanderId).rows.push(battle);
        });
      });

      const commanderGroups = Array.from(groupsByCommanderId.entries())
        .map(([commanderId, group]) => {
          const sortedRows = [...group.rows].sort(compareBattleTimeAscending);
          const parentBattle = sortedRows[0];
          const childBattles = sortedRows.slice(1);
          return {
            groupId: `commander-${commanderId}`,
            commanderId,
            commanderRef: group.commanderRef,
            parentBattle,
            childBattles,
            battleIds: sortedRows.map((battle) => String(battle?._id || "")).filter(Boolean),
          };
        })
        .filter((group) => group.parentBattle)
        .sort((left, right) => compareByCurrentSort(left.parentBattle, right.parentBattle));

      return commanderGroups.flatMap((group, groupIndex) => {
        const rows = [{
          type: "commanderGroupParent",
          key: `commander-parent-${group.groupId}`,
          battle: group.parentBattle,
          commanderGroup: group,
          childCount: group.childBattles.length,
          isExpanded: expandedCommanderGroupIds.has(group.groupId),
        }];
        if (expandedCommanderGroupIds.has(group.groupId)) {
          group.childBattles.forEach((battle, childIndex) => {
            rows.push({
              type: "commanderGroupChild",
              key: `commander-child-${group.groupId}-${getBattleRowKey(battle)}`,
              battle,
              commanderGroup: group,
              isLastGroupChild: childIndex === group.childBattles.length - 1,
            });
          });
        }
        if (groupIndex < commanderGroups.length - 1) {
          rows.push({
            type: "commanderGroupSpacer",
            key: `commander-spacer-${group.groupId}`,
          });
        }
        return rows;
      });
    }

    const shouldGroupBubbleDrops = String(selectedRule?.ruleType || "") === "bubble_drop";
    if (!shouldGroupBubbleDrops) {
      return filteredEventBattles.map((battle) => ({
        type: "battle",
        key: `battle-${getBattleRowKey(battle)}`,
        battle,
      }));
    }

    const expandedGroupIds = new Set(expandedBubbleDropGroupIds.map((value) => String(value)));
    const groupsById = new Map();

    filteredEventBattles.forEach((battle) => {
      const group = getBubbleDropGroupsForBattle(battle)
        .find((candidateGroup) => String(candidateGroup?.ruleCode || "") === selectedRuleCode)
        || getPrimaryBubbleDropGroup(battle);
      if (!group?.id) return;
      const groupId = String(group.id);
      if (!groupsById.has(groupId)) {
        groupsById.set(groupId, { group, rows: [] });
      }
      groupsById.get(groupId).rows.push(battle);
    });

    const groups = Array.from(groupsById.values())
      .map(({ group, rows }) => {
        const sortedRows = [...rows].sort(compareBattleTimeAscending);
        const parentBattle = sortedRows[0];
        const childBattles = sortedRows.slice(1);
        return { group, parentBattle, childBattles };
      })
      .filter((row) => row.parentBattle)
      .sort((left, right) => compareByCurrentSort(left.parentBattle, right.parentBattle));

    return groups.flatMap(({ group, parentBattle, childBattles }, groupIndex) => {
      const groupId = String(group.id);
      const rows = [{
        type: "bubbleGroupParent",
        key: `bubble-parent-${groupId}`,
        battle: parentBattle,
        group,
        childCount: childBattles.length,
        isExpanded: expandedGroupIds.has(groupId),
      }];
      if (expandedGroupIds.has(groupId)) {
        childBattles.forEach((battle, childIndex) => {
          rows.push({
            type: "bubbleGroupChild",
            key: `bubble-child-${groupId}-${getBattleRowKey(battle)}`,
            battle,
            group,
            isLastGroupChild: childIndex === childBattles.length - 1,
          });
        });
      }
      if (groupIndex < groups.length - 1) {
        rows.push({
          type: "bubbleGroupSpacer",
          key: `bubble-spacer-${groupId}`,
        });
      }
      return rows;
    });
  }, [
    battleLogRuleFilter,
    battleLogSortConfig,
    battleLogViewMode,
    expandedBubbleDropGroupIds,
    expandedCommanderBattleGroupIds,
    filteredEventBattles,
    getBattleLogSortValue,
    getBubbleDropGroupsForBattle,
    getBattleHomeCommanderRefs,
    getPrimaryBubbleDropGroup,
    strikeSettings,
  ]);

  const battleLogImpactRanks = useMemo(() => {
    const rows = (selectedEvent?.battles || [])
      .map((battle) => {
        const totals = getBattleHomeTotals(battle);
        if (totals.gained == null && totals.leaked == null) return null;
        return {
          key: getBattleRowKey(battle),
          net: asFiniteInteger(totals.gained, 0) - asFiniteInteger(totals.leaked, 0),
        };
      })
      .filter((row) => row?.key);

    const sortedRows = [...rows].sort((left, right) => left.net - right.net);
    const total = sortedRows.length;
    const topBandSize = Math.max(1, Math.ceil(total * 0.1));
    const highBandSize = Math.max(topBandSize, Math.ceil(total * 0.25));
    const ranks = new Map();

    sortedRows.forEach((row, index) => {
      const worstRank = index + 1;
      const bestRank = total - index;
      let label = "Even impact";
      let tone = "neutral";
      if (row.net < 0) {
        tone = "negative";
        label = worstRank <= topBandSize
          ? "Worst tier"
          : (worstRank <= highBandSize ? "High negative" : "Negative");
      } else if (row.net > 0) {
        tone = "positive";
        label = bestRank <= topBandSize
          ? "Best tier"
          : (bestRank <= highBandSize ? "High positive" : "Positive");
      }
      ranks.set(row.key, {
        ...row,
        total,
        worstRank,
        bestRank,
        label,
        tone,
      });
    });
    return ranks;
  }, [getBattleHomeTotals, selectedEvent?.battles]);

  const eventCommanders = useMemo(() => {
    return commanders.filter((c) =>
      eventAllianceIdSet.has(getId(c?.alliance)),
    );
  }, [commanders, eventAllianceIdSet]);

  const eventCommanderSelectOptions = useMemo(() => {
    return [...eventCommanders]
      .map((commander) => {
        const allianceId = String(commander?.alliance?._id || commander?.alliance || "");
        const allianceLabel = allianceNameById[allianceId] || commander?.alliance?.acronym || commander?.alliance?.name || "";
        return {
          id: String(commander?._id || ""),
          label: allianceLabel
            ? `${commander?.name || "Unknown"} (${allianceLabel})`
            : `${commander?.name || "Unknown"}`,
        };
      })
      .filter((option) => option.id)
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [eventCommanders, allianceNameById]);

  const eventTypeConfigById = useMemo(() => {
    const map = {};
    (eventTypeMeta?.eventTypes || []).forEach((type) => {
      map[type.id] = type;
    });
    return map;
  }, [eventTypeMeta]);

  const activeCreateTypeConfig =
    eventTypeConfigById[eventType] ||
    eventTypeMeta?.eventTypes?.[0] ||
    DEFAULT_EVENT_TYPE_META.eventTypes[0];
  const activeCreateIsFixedFullDay = isFixedFullDayEventType(eventType);

  const selectedEventTypeConfig =
    eventTypeConfigById[selectedEvent?.eventType] ||
    eventTypeMeta?.eventTypes?.find((t) => String(t.id) === String(selectedEvent?.eventType)) ||
    null;
  const selectedPlannerPluginId = String(selectedEventTypeConfig?.plannerPlugin || "");
  const PlannerComponent = useMemo(
    () => getPlannerComponentByPlugin(selectedPlannerPluginId),
    [selectedPlannerPluginId],
  );

  const workspaceEventTabs = useMemo(() => {
    const tabs = [];
    if (selectedEventTypeConfig?.supportsResults !== false) {
      tabs.push({ id: "results", label: "Results" });
    }
    if (selectedEventTypeConfig?.supportsBattles !== false) {
      tabs.push({ id: "battles", label: "Battle Log" });
    }
    if (selectedEventTypeConfig?.supportsPlanner === true) {
      tabs.push({ id: "planning", label: "Planning" });
    }
    tabs.push({ id: "recommendations", label: "Player List" });
    return tabs;
  }, [
    selectedEventTypeConfig?.supportsPlanner,
    selectedEventTypeConfig?.supportsBattles,
    selectedEventTypeConfig?.supportsResults,
  ]);

  useEffect(() => {
    if (activeSection !== "events") return;
    const isActiveTabVisible = workspaceEventTabs.some((tab) => tab.id === activeTab);
    if (!isActiveTabVisible) setActiveTab(workspaceEventTabs[0]?.id || "results");
  }, [activeSection, activeTab, workspaceEventTabs]);

  const maxAllianceSlots = useMemo(() => {
    const counts = (eventTypeMeta?.eventTypes || []).map((type) =>
      Number(type?.requiredAllianceCount || 0),
    );
    return Math.max(3, ...counts, 0);
  }, [eventTypeMeta]);

  const maxNationSlots = useMemo(() => {
    const counts = (eventTypeMeta?.eventTypes || []).map((type) =>
      Number(type?.requiredNationCount || 0),
    );
    return Math.max(2, ...counts, 0);
  }, [eventTypeMeta]);

  const nationOptions = useMemo(() => {
    return (nations || [])
      .slice()
      .sort((a, b) => Number(a?.number || 0) - Number(b?.number || 0));
  }, [nations]);

  const strongholdsByIsland = useMemo(() => {
    if (!selectedEvent) return [];
    const groups = (selectedEvent.islands || []).map((island) => ({
      island,
      strongholds: (selectedEvent.strongholds || []).filter(
        (s) => String(s.islandId) === String(island._id),
      ),
    }));
    return groups;
  }, [selectedEvent]);

  const strongholdNameById = useMemo(() => {
    const out = {};
    (selectedEvent?.strongholds || []).forEach((s) => {
      out[String(s._id)] = s.name;
    });
    return out;
  }, [selectedEvent?.strongholds]);

  const participantResults = useMemo(() => {
    return (selectedEvent?.participantResults || [])
      .slice()
      .sort((a, b) => Number(b?.score || 0) - Number(a?.score || 0));
  }, [selectedEvent?.participantResults]);

  const officialParticipantResultTotal = useMemo(() => (
    participantResults.reduce((sum, row) => sum + asFiniteInteger(row?.score, 0), 0)
  ), [participantResults]);

  const availableResultModes = useMemo(() => {
    const fromEvent = Array.isArray(selectedEvent?.resultModes)
      ? selectedEvent.resultModes.filter(Boolean)
      : [];
    const hasParticipantRows = Array.isArray(selectedEvent?.participantResults)
      && selectedEvent.participantResults.length > 0;
    if (fromEvent.length) {
      if (hasParticipantRows && !fromEvent.includes("individual")) {
        return [...fromEvent, "individual"];
      }
      return fromEvent;
    }

    if (selectedEvent?.eventType === "infiniteZombie") return ["individual"];
    return ["alliance", "individual"];
  }, [selectedEvent?.resultModes, selectedEvent?.eventType, selectedEvent?.participantResults]);

  const nationNameById = useMemo(() => {
    const out = {};
    (nationOptions || []).forEach((n) => {
      const nationId = String(n?._id || "");
      const activeLabel = formatNationNumberLabel(n?.number);
      if (nationId && activeLabel) {
        out[nationId] = activeLabel;
      }

      (Array.isArray(n?.consumedNations) ? n.consumedNations : []).forEach((consumed) => {
        const consumedId = String(consumed?._id || "");
        const consumedLabel = formatNationNumberLabel(consumed?.number);
        if (!consumedId || !consumedLabel) return;
        out[consumedId] = activeLabel
          ? `${consumedLabel} (merged into ${activeLabel})`
          : consumedLabel;
      });
    });
    return out;
  }, [nationOptions]);

  const selectedEventNationLabels = useMemo(() => {
    const officialNationRows = scoreboardSourcesByDimension.nation === "official_scoreboard"
      ? (scoreboardsByDimension.nation || [])
      : [];
    if (officialNationRows.length) {
      const seenOfficial = new Set();
      return officialNationRows
        .map((row) => {
          const id = String(row?.nation?._id || row?.nation || row?.subjectNation?._id || row?.subjectNation || "");
          const label = nationNameById[id]
            || getScoreboardNationFallback(row)
            || (row?.nation?.number != null ? formatNationNumberLabel(row.nation.number) : "")
            || id;
          const normalized = String(label || "").trim();
          if (!normalized || seenOfficial.has(normalized)) return "";
          seenOfficial.add(normalized);
          return normalized;
        })
        .filter(Boolean);
    }

    const ids = [
      selectedEvent?.nation,
      ...(Array.isArray(selectedEvent?.participantNations) ? selectedEvent.participantNations : []),
    ];
    const seen = new Set();
    return ids
      .map((value) => {
        const id = String(value?._id || value || "");
        if (!id || seen.has(id)) return "";
        seen.add(id);
        if (value && typeof value === "object" && value.number != null) {
          return nationNameById[id] || formatNationNumberLabel(value.number) || id;
        }
        return nationNameById[id] || id;
      })
      .filter(Boolean);
  }, [nationNameById, scoreboardsByDimension.nation, scoreboardSourcesByDimension.nation, selectedEvent?.nation, selectedEvent?.participantNations]);

  const selectedEventNationMatchupLabel = useMemo(() => {
    const labels = selectedEventNationLabels
      .map((label) => String(label || "").trim())
      .filter(Boolean);
    if (labels.length >= 2) {
      return `${labels[0]} vs ${labels.slice(1).join(", ")}`;
    }
    return labels[0] || "";
  }, [selectedEventNationLabels]);

  const individualResultRows = participantResults;

  const allianceResultRows = useMemo(() => {
    if (!availableResultModes.includes("alliance")) return [];
    const officialAllianceRows = scoreboardSourcesByDimension.alliance === "official_scoreboard"
      ? (scoreboardsByDimension.alliance || [])
      : [];
    if (officialAllianceRows.length) {
      return officialAllianceRows
        .map((row, index) => ({
          ...row,
          key: getScoreboardRowKey(row, `alliance-${index}`),
          alliance: row.alliance || null,
          nationId: row.nation || "",
          nationDisplayName: getScoreboardNationFallback(row),
          points: getScoreboardRowPoints(row),
          displayName: row.alliance
            ? ""
            : (row.sourceContext?.acronym || row.sourceContext?.allianceAcronym || row.sourceContext?.allianceName || row.key || ""),
          official: true,
        }))
        .sort((a, b) => b.points - a.points);
    }

    if (participantResults.length) {
      const acc = {};
      participantResults.forEach((row) => {
        const key = String(row.alliance || "");
        if (!key) return;
        if (!acc[key]) {
          acc[key] = { alliance: row.alliance, points: 0 };
        }
        acc[key].points += Number(row.score || 0);
      });
      return Object.values(acc).sort((a, b) => b.points - a.points);
    }

    return (scoreboard || []).map((row) => ({
      alliance: row.alliance,
      nationId: row.nation || "",
      nationDisplayName: getScoreboardNationFallback(row),
      points: getScoreboardRowPoints(row),
    }));
  }, [availableResultModes, participantResults, scoreboard, scoreboardsByDimension.alliance, scoreboardSourcesByDimension.alliance]);

  const nationResultRows = useMemo(() => {
    if (!availableResultModes.includes("nation")) return [];
    const officialNationRows = scoreboardSourcesByDimension.nation === "official_scoreboard"
      ? (scoreboardsByDimension.nation || [])
      : [];
    if (officialNationRows.length) {
      return officialNationRows
        .map((row, index) => ({
          ...row,
          key: getScoreboardRowKey(row, `nation-${index}`),
          nationId: row.nation || "",
          points: getScoreboardRowPoints(row),
          displayName: row.nation
            ? ""
            : (row.sourceContext?.game?.kingdomName
              || (row.sourceContext?.gameNationNumber ? formatNationNumberLabel(row.sourceContext.gameNationNumber) : "")
              || row.key
              || ""),
          official: true,
        }))
        .sort((a, b) => b.points - a.points);
    }

    const acc = {};

    participantResults.forEach((row) => {
      const commander = commanderById[String(row.commander)] || {};
      const historicalNationId = String(row?.subjectNation?._id || row?.subjectNation || "");
      const nationId = historicalNationId || String(commander?.nation?._id || commander?.nation || "");
      if (!nationId) return;
      if (!acc[nationId]) {
        acc[nationId] = { nationId, points: 0 };
      }
      acc[nationId].points += Number(row.score || 0);
    });

    if (!Object.keys(acc).length) {
      (selectedEvent?.participantNations || []).forEach((nationId) => {
        const key = String(nationId || "");
        if (!key) return;
        acc[key] = { nationId: key, points: 0 };
      });
    }

    return Object.values(acc).sort((a, b) => b.points - a.points);
  }, [availableResultModes, participantResults, commanderById, selectedEvent?.participantNations, scoreboardsByDimension.nation, scoreboardSourcesByDimension.nation]);

  const nationMatchupScoreboard = useMemo(() => {
    const rows = Array.isArray(nationResultRows) ? nationResultRows : [];
    if (!rows.length) return { rows: [], total: 0 };

    const getNationResultLabel = (row) => (
      nationNameById[String(row?.nationId)] ||
      row?.displayName ||
      getScoreboardNationFallback(row) ||
      String(row?.nationId || row?.key || "Nation")
    );

    const homeNationId = String(selectedEvent?.nation?._id || selectedEvent?.nation || "");
    const homeRow = rows.find((row) => homeNationId && String(row?.nationId || "") === homeNationId) || rows[0];
    const opponentRow = rows.find((row) => String(row?.key || row?.nationId || "") !== String(homeRow?.key || homeRow?.nationId || ""));
    const matchupRows = [homeRow, opponentRow]
      .filter(Boolean)
      .map((row, index) => ({
        key: String(row?.key || row?.nationId || index),
        label: getNationResultLabel(row),
        points: Number(row?.points || 0),
        side: index === 0 ? "home" : "opponent",
      }));
    const total = matchupRows.reduce((sum, row) => sum + Number(row.points || 0), 0);
    return { rows: matchupRows, total };
  }, [nationNameById, nationResultRows, selectedEvent?.nation]);

  const hasNationResultBreakdown = useMemo(
    () => nationResultRows.some((row) => hasNationScoreBreakdown(row)),
    [nationResultRows],
  );

  const individualScoreboardRows = useMemo(() => {
    const officialIndividualRows = scoreboardSourcesByDimension.individual === "official_scoreboard"
      ? (scoreboardsByDimension.individual || [])
      : [];
    if (!officialIndividualRows.length) return individualResultRows;
    const selectedType = String(individualScoreboardType || "").trim();
    const rowsForSelectedType = selectedType
      ? officialIndividualRows.filter((row) => (
        String(row?.sourceContext?.scoreboardType || "") === selectedType
      ))
      : officialIndividualRows;
    return rowsForSelectedType
      .map((row, index) => ({
        ...row,
        _id: row._id || row.sourceKey || `official-${index}`,
        score: getScoreboardRowPoints(row),
        nationId: row.nation || "",
        nationDisplayName: getScoreboardNationFallback(row),
        commanderDisplayName: row.commander
          ? ""
          : (row.sourceContext?.commanderName || row.key || ""),
        allianceDisplayName: row.alliance
          ? ""
          : (row.sourceContext?.allianceAcronym || row.sourceContext?.allianceName || ""),
        official: true,
      }))
      .sort((a, b) => Number(b?.score || 0) - Number(a?.score || 0));
  }, [individualResultRows, individualScoreboardType, scoreboardsByDimension.individual, scoreboardSourcesByDimension.individual]);

  const individualScoreboardTabs = useMemo(() => {
    if (scoreboardSourcesByDimension.individual !== "official_scoreboard") return [];
    const seen = new Set();
    const rows = [];
    (scoreboardsByDimension.individual || []).forEach((row) => {
      const type = String(row?.sourceContext?.scoreboardType || "").trim();
      if (!type || seen.has(type)) return;
      seen.add(type);
      rows.push({
        id: type,
        label: formatIndividualScoreboardTypeLabel(type),
      });
    });
    const getTabOrder = (id) => {
      const phaseMatch = String(id || "").match(/^phase[_-]?(\d+)[_-]?player$/i);
      if (phaseMatch) return Number(phaseMatch[1]) * 10;
      if (id === "day_player") return 55;
      if (id === "round_player") return 60;
      if (id === "season_player") return 70;
      return 100;
    };
    return rows.sort((a, b) => (
      getTabOrder(a.id) - getTabOrder(b.id)
        || String(a.label).localeCompare(String(b.label))
    ));
  }, [scoreboardsByDimension.individual, scoreboardSourcesByDimension.individual]);

  useEffect(() => {
    if (!individualScoreboardTabs.length) return;
    if (individualScoreboardTabs.some((tab) => tab.id === individualScoreboardType)) return;
    const weekly = individualScoreboardTabs.find((tab) => tab.id === "round_player");
    setIndividualScoreboardType((weekly || individualScoreboardTabs[0]).id);
  }, [individualScoreboardTabs, individualScoreboardType]);

  const analyticsCommanderRows = useMemo(() => {
    const rows = Array.isArray(analyticsData?.commanderStats) ? analyticsData.commanderStats : [];
    const required = new Set((analyticsFilters?.eventIds || []).map((id) => String(id)));
    const search = String(analyticsSearch || "").trim().toLowerCase();

    return rows.filter((row) => {
      if (required.size > 0) {
        const eventSet = new Set((row.eventIds || []).map((id) => String(id)));
        for (const reqId of required) {
          if (!eventSet.has(reqId)) return false;
        }
      }

      if (!search) return true;
      const hay = `${row.name || ""} ${row.allianceName || ""}`.toLowerCase();
      return hay.includes(search);
    });
  }, [analyticsData, analyticsFilters?.eventIds, analyticsSearch]);

  const analyticsIntersectionIds = useMemo(() => {
    if ((analyticsFilters?.eventIds || []).length > 0) {
      return analyticsCommanderRows
        .filter((row) => (analyticsFilters.eventIds || []).every((id) => (row.eventIds || []).includes(id)))
        .map((row) => String(row.commanderId));
    }

    return Array.isArray(analyticsData?.inEveryEvent)
      ? analyticsData.inEveryEvent.map((id) => String(id))
      : [];
  }, [analyticsData, analyticsCommanderRows, analyticsFilters?.eventIds]);

  const analyticsHeatmapRows = useMemo(() => {
    const baseRows = Array.isArray(analyticsData?.heatmap?.rows) ? analyticsData.heatmap.rows : [];
    const keep = new Set(analyticsCommanderRows.map((r) => String(r.commanderId)));
    return baseRows.filter((row) => keep.has(String(row.commanderId)));
  }, [analyticsData, analyticsCommanderRows]);

  const commanderPlacementById = useMemo(() => {
    const out = {};
    (selectedEvent?.strongholds || []).forEach((s) => {
      if (s.defendingCommander) {
        out[String(s.defendingCommander)] = {
          strongholdId: String(s._id),
          strongholdName: s.name,
        };
      }
    });
    return out;
  }, [selectedEvent?.strongholds]);

  const myAllianceId = useMemo(
    () => getId(selectedEvent?.alliances?.[0]),
    [selectedEvent?.alliances],
  );

  const myAllianceCommanders = useMemo(() => {
    const source = recommendations?.length
      ? recommendations.map((r) => ({
          _id: r._id,
          name: r.name,
          alliance: r.alliance,
          cityLevel: r.cityLevel,
          blendedRank: r.blendedRank,
          battlePower: r.battlePower,
        }))
      : eventCommanders;
    return source.filter((c) => getId(c?.alliance) === myAllianceId);
  }, [recommendations, eventCommanders, myAllianceId]);

  const myAllianceCommanderOptions = useMemo(() => {
    return [...myAllianceCommanders].sort((a, b) => {
      const aPlaced = commanderPlacementById[String(a._id)] ? 1 : 0;
      const bPlaced = commanderPlacementById[String(b._id)] ? 1 : 0;
      if (aPlaced !== bPlaced) return aPlaced - bPlaced;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [myAllianceCommanders, commanderPlacementById]);

  const infiniteZombieWaveItemOptions = useMemo(() => {
    return [
      ...INFINITE_ZOMBIE_ITEMS.orange,
      ...INFINITE_ZOMBIE_ITEMS.purple,
      ...INFINITE_ZOMBIE_ITEMS.blue,
      ...INFINITE_ZOMBIE_ITEMS.green,
    ];
  }, []);

  const infiniteZombieItemMetaByValue = useMemo(() => {
    const out = {};
    infiniteZombieWaveItemOptions.forEach((item) => {
      out[item.value] = item;
    });
    return out;
  }, [infiniteZombieWaveItemOptions]);

  const normalizeItem = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (STRONGHOLD_ITEMS.some((x) => x.value === raw)) return raw;
    return "";
  };

  const formatScoreDisplay = (value) => {
    const digits = String(value || "").replace(/[^\d]/g, "");
    if (!digits) return "";
    return Number(digits).toLocaleString();
  };

  const parseScoreValue = (value) => {
    const digits = String(value || "").replace(/[^\d]/g, "");
    return digits ? Number(digits) : 0;
  };

  const getItemMeta = (value) => {
    const normalized = normalizeItem(value);
    return STRONGHOLD_ITEMS.find((x) => x.value === normalized) || null;
  };

  const editingStronghold = useMemo(() => {
    if (!editingStrongholdId) return null;
    return (
      (selectedEvent?.strongholds || []).find(
        (s) => String(s._id) === String(editingStrongholdId),
      ) || null
    );
  }, [editingStrongholdId, selectedEvent?.strongholds]);

  const eventAllianceFilterOptions = useMemo(() => {
    const byId = new Map();
    (events || []).forEach((eventRow) => {
      (eventRow?.alliances || []).forEach((allianceRef) => {
        const allianceId = String(allianceRef?._id || allianceRef || "");
        if (!allianceId || byId.has(allianceId)) return;
        byId.set(allianceId, allianceNameById[allianceId] || allianceId);
      });
    });

    return Array.from(byId.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [events, allianceNameById]);

  const eventAllianceFilterSelectOptions = useMemo(
    () => [{ id: "all", label: "All alliances" }, ...eventAllianceFilterOptions],
    [eventAllianceFilterOptions],
  );

  const eventSourceNationFilterOptions = useMemo(() => {
    const byId = new Map();
    (events || []).forEach((eventRow) => {
      const sourceNationId = String(eventRow?.nation?._id || eventRow?.nation || "");
      if (!sourceNationId || byId.has(sourceNationId)) return;
      byId.set(sourceNationId, nationNameById[sourceNationId] || sourceNationId);
    });

    return Array.from(byId.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [events, nationNameById]);

  const getEventSourceNationText = useCallback((eventRow) => {
    const sourceNationId = String(eventRow?.nation?._id || eventRow?.nation || "");
    if (!sourceNationId) return "";
    return nationNameById[sourceNationId] || sourceNationId;
  }, [nationNameById]);

  const getEventAllianceAcronymText = useCallback((eventRow) => {
    const labels = Array.from(
      new Set(
        (eventRow?.alliances || [])
          .map((allianceRef) => String(allianceRef?._id || allianceRef || ""))
          .filter(Boolean)
          .map((allianceId) => allianceNameById[allianceId] || allianceId),
      ),
    );

    if (!labels.length) return "";
    return labels.join("/");
  }, [allianceNameById]);

  const filteredEvents = useMemo(() => {
    return (events || []).filter((eventRow) => {
      const typeMatch =
        eventTypeFilter === "all" ||
        String(eventRow?.eventType || "triangleWar") === String(eventTypeFilter);
      if (!typeMatch) return false;

      const eventAllianceIds = new Set(
        (eventRow?.alliances || [])
          .map((allianceRef) => String(allianceRef?._id || allianceRef || ""))
          .filter(Boolean),
      );
      const allianceMatches = eventAllianceFilterId === "all"
        || eventAllianceIds.has(String(eventAllianceFilterId));
      if (!allianceMatches) return false;

      if (eventSourceNationFilterId === "all") return true;
      const sourceNationId = String(eventRow?.nation?._id || eventRow?.nation || "");
      return sourceNationId === String(eventSourceNationFilterId);
    });
  }, [events, eventTypeFilter, eventAllianceFilterId, eventSourceNationFilterId]);

  const sortedFilteredEvents = useMemo(() => {
    const toTimestamp = (eventRow) => {
      const startsAtMs = new Date(eventRow?.startsAt || 0).getTime();
      if (Number.isFinite(startsAtMs) && startsAtMs > 0) return startsAtMs;
      const createdAtMs = new Date(eventRow?.createdAt || 0).getTime();
      if (Number.isFinite(createdAtMs) && createdAtMs > 0) return createdAtMs;
      return 0;
    };

    return [...filteredEvents].sort((a, b) => {
      const diff = toTimestamp(b) - toTimestamp(a);
      if (diff !== 0) return diff;
      return String(formatEventDisplayLabel(a, eventTypeConfigById) || "").localeCompare(
        String(formatEventDisplayLabel(b, eventTypeConfigById) || ""),
      );
    });
  }, [filteredEvents, eventTypeConfigById]);

  const launcherEventOptions = useMemo(() => {
    const type = String(uploadLaunchConfig.eventType || "");
    const allianceId = String(uploadLaunchConfig.allianceId || "all");

    return (events || []).filter((eventRow) => {
      const typeMatch = !type || String(eventRow?.eventType || "triangleWar") === type;
      if (!typeMatch) return false;

      if (allianceId === "all") return true;
      const eventAllianceIds = new Set(
        (eventRow?.alliances || [])
          .map((allianceRef) => String(allianceRef?._id || allianceRef || ""))
          .filter(Boolean),
      );
      return eventAllianceIds.has(allianceId);
    });
  }, [events, uploadLaunchConfig.eventType, uploadLaunchConfig.allianceId]);

  const recommendTemplateId = useCallback((templates, criteria = {}) => {
    const list = Array.isArray(templates) ? templates : [];
    if (!list.length) return "";

    const visibleRows = Number(criteria.visibleRows || 0);
    const scope = String(criteria.resultsScope || "").trim();

    const scored = list.map((template) => {
      let score = 0;
      const profile = template?.selectionProfile || {};

      if (scope) {
        const tScope = String(profile.resultsScope || "");
        if (tScope === scope) score += 40;
        else if (tScope === "generic" || !tScope) score += 10;
      }

      if (visibleRows > 0) {
        const min = Number(profile.visibleRowsHintMin);
        const max = Number(profile.visibleRowsHintMax);
        if (Number.isFinite(min) && Number.isFinite(max) && max >= min) {
          if (visibleRows >= min && visibleRows <= max) score += 45;
          else score -= Math.min(30, Math.abs(visibleRows - (visibleRows < min ? min : max)) * 4);
        } else {
          const fallbackRows = Number(template?.layout?.rowsPerScreenshot || 0);
          if (fallbackRows > 0) {
            score += Math.max(0, 30 - Math.abs(fallbackRows - visibleRows) * 5);
          }
        }
      }

      if (template?.enabled !== false) score += 10;
      score += Number(template?.version || 1) * 0.5;

      return { id: String(template._id || ""), score };
    });

    scored.sort((a, b) => b.score - a.score);
    return scored[0]?.id || "";
  }, []);

  const analyticsNationOptions = useMemo(() => {
    return (nations || [])
      .filter(Boolean)
      .map((row) => ({
        _id: String(row?._id || ""),
        number: row?.number,
        name: row?.name || "",
        label: row?.number != null ? String(row.number) : String(row?.name || "Nation"),
      }))
      .filter((row) => Boolean(row._id))
      .sort((a, b) => Number(a.number || 0) - Number(b.number || 0));
  }, [nations]);

  const analyticsScopedNationIds = useMemo(() => {
    const selected = Array.isArray(analyticsFilters?.nationIds)
      ? analyticsFilters.nationIds.map((id) => String(id || "")).filter(Boolean)
      : [];
    if (isSuperUser) {
      if (selected.length > 0) return Array.from(new Set(selected));
      return nationId ? [nationId] : [];
    }
    if (selected.length > 0) return Array.from(new Set(selected));
    return [];
  }, [analyticsFilters?.nationIds, isSuperUser, nationId]);

  const analyticsAllianceOptions = useMemo(() => {
    const analyticsProvidedOptions = Array.isArray(analyticsData?.availableAlliances)
      ? analyticsData.availableAlliances
        .map((alliance) => ({
          _id: String(alliance?._id || ""),
          label: String(alliance?.label || alliance?.acronym || alliance?.name || alliance?._id || ""),
        }))
        .filter((alliance) => Boolean(alliance._id))
      : [];
    if (analyticsProvidedOptions.length > 0) {
      return analyticsProvidedOptions.sort((a, b) =>
        String(a.label || "").localeCompare(String(b.label || "")),
      );
    }

    const nationSet = new Set(analyticsScopedNationIds.map((id) => String(id)));
    return (alliances || [])
      .filter((alliance) => {
        const allianceNationId = String(alliance?.nation?._id || alliance?.nation || "");
        return allianceNationId && nationSet.has(allianceNationId);
      })
      .map((alliance) => ({
        _id: String(alliance?._id || ""),
        label: alliance?.acronym || alliance?.name || String(alliance?._id || ""),
      }))
      .filter((alliance) => Boolean(alliance._id))
      .sort((a, b) => String(a.label || "").localeCompare(String(b.label || "")));
  }, [alliances, analyticsData?.availableAlliances, analyticsScopedNationIds]);

  const loadParticipationAnalytics = useCallback(async () => {
    if (!canAccessAnalytics || !canReadEvents || !nationId) return;

    const effectiveNationIds = isSuperUser
      ? (analyticsScopedNationIds.length > 0 ? analyticsScopedNationIds : (nationId ? [nationId] : []))
      : analyticsScopedNationIds;

    setIsAnalyticsLoading(true);
    try {
      const params = new URLSearchParams();
      if (Array.isArray(analyticsFilters.eventTypes) && analyticsFilters.eventTypes.length > 0) {
        params.set("eventTypes", analyticsFilters.eventTypes.join(","));
      }
      if (analyticsFilters.startDate) params.set("startDate", analyticsFilters.startDate);
      if (analyticsFilters.endDate) params.set("endDate", analyticsFilters.endDate);
      if (effectiveNationIds.length > 0) params.set("nationIds", effectiveNationIds.join(","));
      if (Array.isArray(analyticsFilters.allianceIds) && analyticsFilters.allianceIds.length > 0) {
        params.set("allianceIds", analyticsFilters.allianceIds.join(","));
      }
      if (Array.isArray(analyticsFilters.cityRoles) && analyticsFilters.cityRoles.length > 0) {
        params.set("cityRoles", analyticsFilters.cityRoles.join(","));
      }
      if (Array.isArray(analyticsFilters.statuses) && analyticsFilters.statuses.length > 0) {
        params.set("statuses", analyticsFilters.statuses.join(","));
      }
      if (Array.isArray(analyticsFilters.eventIds) && analyticsFilters.eventIds.length > 0) {
        params.set("eventIds", analyticsFilters.eventIds.join(","));
      }
      if (analyticsFilters.includeLegacyMembers) {
        params.set("includeLegacyMembers", "true");
      }

      const response = await axiosInstance.get(`/events/analytics/participation?${params.toString()}`);
      const payload = response?.data || null;
      setAnalyticsData(payload);
      setAnalyticsFilters((prev) => {
        const validIds = new Set((payload?.availableEvents || payload?.events || []).map((evt) => String(evt?._id || "")));
        const current = Array.isArray(prev?.eventIds) ? prev.eventIds.map((id) => String(id || "")) : [];
        const nextEventIds = current.filter((id) => validIds.has(id));
        const currentAllianceIds = Array.isArray(prev?.allianceIds)
          ? prev.allianceIds.map((id) => String(id || "")).filter(Boolean)
          : [];
        const validAllianceIdSet = new Set(
          (payload?.availableAlliances || [])
            .map((alliance) => String(alliance?._id || ""))
            .filter(Boolean),
        );
        const defaultAllianceIds = Array.from(validAllianceIdSet);
        const nextAllianceIdsBase = currentAllianceIds.length > 0
          ? currentAllianceIds.filter((id) => validAllianceIdSet.has(id))
          : defaultAllianceIds;
        const nextAllianceIds = nextAllianceIdsBase.length > 0 || defaultAllianceIds.length === 0
          ? nextAllianceIdsBase
          : defaultAllianceIds;
        if (
          nextEventIds.length === current.length &&
          nextEventIds.every((id, idx) => id === current[idx]) &&
          nextAllianceIds.length === currentAllianceIds.length &&
          nextAllianceIds.every((id, idx) => id === currentAllianceIds[idx])
        ) {
          return prev;
        }
        return {
          ...prev,
          eventIds: nextEventIds,
          allianceIds: nextAllianceIds,
        };
      });
    } catch (error) {
      console.error("Error loading participation analytics:", error);
      setAnalyticsData(null);
    } finally {
      setIsAnalyticsLoading(false);
    }
  }, [canAccessAnalytics, canReadEvents, nationId, isSuperUser, analyticsScopedNationIds, analyticsFilters]);

  const loadEvents = useCallback(async () => {
    if (!canReadEvents || !nationId) return;
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/events?nationId=${nationId}`);
      const found = (response.data || []).map((row) => normalizeEventDateTime(row));
      setEvents(found);
    } catch (error) {
      console.error("Error loading events:", error);
      window.alert(
        typeof error === "string" ? error : "Failed to load events.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [canReadEvents, nationId]);

  const loadAlliances = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/alliances");
      setAlliances(response.data || []);
    } catch (error) {
      console.error("Error loading alliances:", error);
    }
  }, []);

  const loadNations = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/nations");
      setNations(response.data || []);
    } catch (error) {
      console.error("Error loading nations:", error);
    }
  }, []);

  const loadOcrTemplates = useCallback(async (eventTypeForTemplate) => {
    if (!isSuperUser) return;
    if (!eventTypeForTemplate) {
      setOcrTemplates([]);
      return;
    }
    try {
      const response = await axiosInstance.get(`/events/admin/ocr-templates?eventType=${eventTypeForTemplate}`);
      const list = response?.data?.templates || [];
      setOcrTemplates(list);
      setSelectedTemplateId((prev) => {
        if (prev && list.some((t) => String(t._id) === String(prev))) return prev;
        return list[0]?._id || "";
      });
    } catch (error) {
      console.error("Error loading OCR templates:", error);
      setOcrTemplates([]);
    }
  }, [isSuperUser]);

  const loadEventTypeMeta = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/events/meta/event-types");
      const nextMeta = response?.data;
      if (!nextMeta || !Array.isArray(nextMeta.eventTypes)) return;
      if (!nextMeta.eventTypes.length) return;

      setEventTypeMeta({
        defaultEventType:
          nextMeta.defaultEventType || nextMeta.eventTypes[0]?.id || "triangleWar",
        eventTypes: nextMeta.eventTypes,
      });
    } catch (error) {
      console.error("Error loading event type metadata:", error);
    }
  }, []);

  const loadEventDetail = useCallback(async (eventId) => {
    if (!eventId) {
      setSelectedEvent(null);
      setScoreboard([]);
      setScoreboardsByDimension({ nation: [], alliance: [], individual: [] });
      setScoreboardSourcesByDimension({ nation: "", alliance: "", individual: "" });
      setRecommendations([]);
      setCommanders([]);
      return;
    }

    setIsLoadingDetail(true);
    try {
      const [eventRes, nationScoreRes, allianceScoreRes, individualScoreRes, commanderRes] = await Promise.all([
        axiosInstance.get(`/events/${eventId}`),
        axiosInstance.get(`/events/${eventId}/scoreboard?dimension=nation`),
        axiosInstance.get(`/events/${eventId}/scoreboard?dimension=alliance`),
        axiosInstance.get(`/events/${eventId}/scoreboard?dimension=individual&scoreboardType=all`),
        axiosInstance.get(`/events/${eventId}/commanders?limit=1200`),
      ]);
      const eventData = normalizeEventDateTime(eventRes.data);
      const nextScoreboards = {
        nation: nationScoreRes.data?.scoreboard || [],
        alliance: allianceScoreRes.data?.scoreboard || [],
        individual: individualScoreRes.data?.scoreboard || [],
      };
      setSelectedEvent(eventData);
      setScoreboard(nextScoreboards.alliance);
      setScoreboardsByDimension(nextScoreboards);
      setScoreboardSourcesByDimension({
        nation: nationScoreRes.data?.scoreSource || "",
        alliance: allianceScoreRes.data?.scoreSource || "",
        individual: individualScoreRes.data?.scoreSource || "",
      });
      setRecommendations([]);
      setCommanders(commanderRes?.data?.commanders || []);

      setBattleForm((prev) => ({
        ...prev,
        strongholdId:
          prev.strongholdId || eventData?.strongholds?.[0]?._id || "",
      }));
    } catch (error) {
      console.error("Error loading event detail:", error);
      window.alert(
        typeof error === "string" ? error : "Failed to load event detail.",
      );
    } finally {
      setIsLoadingDetail(false);
    }
  }, []);

  const loadEventRecommendations = useCallback(async (eventId) => {
    if (!eventId) {
      setRecommendations([]);
      return;
    }
    try {
      const response = await axiosInstance.get(`/events/${eventId}/recommendations`);
      setRecommendations(response?.data?.commanders || []);
    } catch (error) {
      console.error("Error loading event recommendations:", error);
      setRecommendations([]);
    }
  }, []);

  useEffect(() => {
    if (!canReadEvents) return;
    loadEventTypeMeta();
    loadEvents();
    loadAlliances();
    loadNations();
  }, [canReadEvents, loadEventTypeMeta, loadEvents, loadAlliances, loadNations]);

  useEffect(() => {
    if (!canReadEvents || !selectedEventId || activeTab !== "recommendations") return;
    if ((recommendations || []).length > 0) return;
    loadEventRecommendations(selectedEventId);
  }, [activeTab, canReadEvents, loadEventRecommendations, recommendations, selectedEventId]);

  useEffect(() => {
    if (!reportBattleId || !selectedEventId) {
      setReportBattleDetail(null);
      setIsLoadingReportBattleDetail(false);
      return undefined;
    }

    let cancelled = false;
    setReportBattleDetail(null);
    setIsLoadingReportBattleDetail(true);
    axiosInstance.get(`/events/${selectedEventId}/battles/${reportBattleId}`)
      .then((response) => {
        if (cancelled) return;
        setReportBattleDetail(response?.data?.battle || null);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Error loading battle report detail:", error);
        setReportBattleDetail(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingReportBattleDetail(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reportBattleId, selectedEventId]);

  useEffect(() => {
    setSelectedEventId(routeSelectedEventId);
    if (routeSelectedEventId) {
      setActiveSection("events");
      setActiveTab("results");
      return;
    }
    setSelectedEvent(null);
    setScoreboard([]);
    setScoreboardsByDimension({ nation: [], alliance: [], individual: [] });
    setScoreboardSourcesByDimension({ nation: "", alliance: "", individual: "" });
    setRecommendations([]);
    setCommanders([]);
  }, [routeSelectedEventId]);

  useEffect(() => {
    if (!nationId) return;
    setAnalyticsFilters((prev) => {
      const nextNationIds = isSuperUser
        ? ((Array.isArray(prev?.nationIds) && prev.nationIds.length > 0)
          ? prev.nationIds.map((id) => String(id || "")).filter(Boolean)
          : [nationId])
        : [];

      const normalizedNationIds = Array.from(new Set(nextNationIds));
      const nationScopeKey = normalizedNationIds.slice().sort().join(",");
      const allowedAllianceIds = (alliances || [])
        .filter((alliance) => {
          const allianceNationId = String(alliance?.nation?._id || alliance?.nation || "");
          return allianceNationId && normalizedNationIds.includes(allianceNationId);
        })
        .map((alliance) => String(alliance?._id || ""))
        .filter(Boolean);
      const allowedSet = new Set(allowedAllianceIds);

      const scopeChanged = analyticsAllianceScopeRef.current !== nationScopeKey;
      const currentAllianceIds = Array.isArray(prev?.allianceIds)
        ? prev.allianceIds.map((id) => String(id || "")).filter(Boolean)
        : [];
      const scopedCurrentAllianceIds = currentAllianceIds.filter((id) => allowedSet.has(id));
      const nextAllianceIds = scopeChanged ? [] : scopedCurrentAllianceIds;

      const nextCityRoles = Array.isArray(prev?.cityRoles) && prev.cityRoles.length > 0
        ? prev.cityRoles
        : ["primary"];
      const nextStatuses = Array.isArray(prev?.statuses) && prev.statuses.length > 0
        ? prev.statuses
        : ["active"];
      const nextEventIds = Array.isArray(prev?.eventIds)
        ? prev.eventIds.map((id) => String(id || "")).filter(Boolean)
        : [];

      const didChange = JSON.stringify({
        nationIds: normalizedNationIds,
        allianceIds: nextAllianceIds,
        cityRoles: nextCityRoles,
        statuses: nextStatuses,
        eventIds: nextEventIds,
      }) !== JSON.stringify({
        nationIds: Array.isArray(prev?.nationIds) ? prev.nationIds : [],
        allianceIds: Array.isArray(prev?.allianceIds) ? prev.allianceIds : [],
        cityRoles: Array.isArray(prev?.cityRoles) ? prev.cityRoles : [],
        statuses: Array.isArray(prev?.statuses) ? prev.statuses : [],
        eventIds: Array.isArray(prev?.eventIds) ? prev.eventIds : [],
      });

      analyticsAllianceScopeRef.current = nationScopeKey;
      if (!didChange) return prev;

      return {
        ...prev,
        nationIds: normalizedNationIds,
        allianceIds: nextAllianceIds,
        cityRoles: nextCityRoles,
        statuses: nextStatuses,
        eventIds: nextEventIds,
      };
    });
  }, [nationId, isSuperUser, alliances]);

  useEffect(() => {
    if (!canReadEvents || !canAccessAnalytics || !nationId) return;
    if (activeSection !== "analytics") return;
    loadParticipationAnalytics();
  }, [canReadEvents, canAccessAnalytics, nationId, activeSection, loadParticipationAnalytics]);

  useEffect(() => {
    if (!selectedEventId) return;
    setFocusedIslandId("");
    setIsEventAllianceEditorOpen(false);
    loadEventDetail(selectedEventId);
  }, [selectedEventId, loadEventDetail]);

  useEffect(() => {
    const next = Array.from(new Set((selectedEvent?.alliances || []).map((a) => String(a?._id || a || "")).filter(Boolean)));
    const slotCount = Math.max(Number(selectedEventTypeConfig?.requiredAllianceCount || 0), next.length);
    const slots = next.slice(0, slotCount || next.length);
    if (slotCount > 0) {
      while (slots.length < slotCount) slots.push("");
    }

    setEventAllianceDraftIds(next);
    setEventAllianceSlotIds(slots);
  }, [selectedEvent?._id, selectedEvent?.alliances, selectedEventTypeConfig?.requiredAllianceCount]);

  useEffect(() => {
    if (!templateAdminEventType) {
      const fallbackType =
        selectedEvent?.eventType ||
        eventTypeMeta?.defaultEventType ||
        eventTypeMeta?.eventTypes?.[0]?.id ||
        "";
      if (fallbackType) setTemplateAdminEventType(String(fallbackType));
    }
  }, [templateAdminEventType, selectedEvent?.eventType, eventTypeMeta]);

  useEffect(() => {
    if (!isSuperUser) return;
    const targetEventType =
      activeSection === "templates"
        ? templateAdminEventType
        : selectedEvent?.eventType;
    if (!targetEventType) return;
    loadOcrTemplates(targetEventType);
  }, [isSuperUser, activeSection, templateAdminEventType, selectedEvent?.eventType, loadOcrTemplates]);

  useEffect(() => {
    if (!pendingTemplateAutoSelect) return;
    if (!selectedEventId || String(selectedEventId) !== String(pendingTemplateAutoSelect.eventId)) return;
    if (!ocrTemplates.length) return;

    const recommended = recommendTemplateId(ocrTemplates, pendingTemplateAutoSelect);
    if (recommended) {
      setSelectedTemplateId(recommended);
    }
    setPendingTemplateAutoSelect(null);
  }, [pendingTemplateAutoSelect, selectedEventId, ocrTemplates, recommendTemplateId]);

  useEffect(() => {
    if (eventAllianceFilterId === "all") return;
    const exists = eventAllianceFilterOptions.some(
      (option) => String(option.id) === String(eventAllianceFilterId),
    );
    if (!exists) setEventAllianceFilterId("all");
  }, [eventAllianceFilterOptions, eventAllianceFilterId]);

  useEffect(() => {
    if (eventSourceNationFilterId === "all") return;
    const exists = eventSourceNationFilterOptions.some(
      (option) => String(option.id) === String(eventSourceNationFilterId),
    );
    if (!exists) setEventSourceNationFilterId("all");
  }, [eventSourceNationFilterOptions, eventSourceNationFilterId]);

  useEffect(() => {
    setUploadLaunchConfig((prev) => {
      const currentEventId = String(prev.eventId || "");
      if (!currentEventId) {
        return {
          ...prev,
          eventId: String(launcherEventOptions[0]?._id || ""),
        };
      }

      const exists = launcherEventOptions.some(
        (eventRow) => String(eventRow?._id || "") === currentEventId,
      );
      if (exists) return prev;

      return {
        ...prev,
        eventId: String(launcherEventOptions[0]?._id || ""),
      };
    });
  }, [launcherEventOptions]);

  useEffect(() => {
    const knownTypeIds = new Set(
      (eventTypeMeta?.eventTypes || []).map((type) => String(type.id || "")),
    );
    if (!knownTypeIds.size) return;

    if (!knownTypeIds.has(String(eventType))) {
      setEventType(
        eventTypeMeta?.defaultEventType || eventTypeMeta?.eventTypes?.[0]?.id || "triangleWar",
      );
    }

    if (eventTypeFilter !== "all" && !knownTypeIds.has(String(eventTypeFilter))) {
      setEventTypeFilter("all");
    }
  }, [eventType, eventTypeFilter, eventTypeMeta]);

  useEffect(() => {
    setSelectedAllianceIds((prev) => {
      const next = Array.from({ length: maxAllianceSlots }, (_, idx) => prev[idx] || "");
      return next;
    });
  }, [maxAllianceSlots]);

  useEffect(() => {
    setSelectedParticipantNationIds((prev) => {
      const next = Array.from(
        { length: maxNationSlots },
        (_, idx) => prev[idx] || (idx === 0 ? nationId : ""),
      );
      if (next.length) next[0] = nationId || next[0] || "";
      return next;
    });
  }, [maxNationSlots, nationId]);

  const buildDefaultZombiePlan = useCallback(() => {
    const boardSlots = Array.from(
      { length: INFINITE_ZOMBIE_BOARD_COORDS.length },
      (_, idx) => ({
        slotId: `S${idx + 1}`,
        x: INFINITE_ZOMBIE_BOARD_COORDS[idx]?.x ?? 0,
        y: INFINITE_ZOMBIE_BOARD_COORDS[idx]?.y ?? 0,
        towerType: "",
      }),
    );
    const waveItems = Array.from(
      { length: INFINITE_ZOMBIE_WAVE_COUNT },
      (_, idx) => ({
        wave: idx + 1,
        item: "",
      }),
    );
    return {
      boardSlots,
      startItems: { orange: "", purple: "", blue: "", green: "" },
      waveItems,
    };
  }, []);

  useEffect(() => {
    if (!selectedEvent || selectedEvent.eventType !== "infiniteZombie") return;

    setZombieMapZoom(1);
    setZombieMapPan({ x: 0, y: 0 });
    setIsZombieMapDragging(false);
    zombieMapPointersRef.current.clear();
    zombieMapPinchRef.current.active = false;
    zombieMapSuppressClickRef.current = false;
    zombieMapDragRef.current = {
      pointerId: null,
      dragging: false,
      moved: false,
      captured: false,
      startX: 0,
      startY: 0,
      originX: 0,
      originY: 0,
    };
    const fallback = buildDefaultZombiePlan();
    const incoming = selectedEvent.infiniteZombie || {};
    const incomingSlots = Array.isArray(incoming.boardSlots)
      ? incoming.boardSlots
      : [];

    const maxIncomingX = Math.max(
      0,
      ...incomingSlots
        .map((s) => Number(s?.x || 0))
        .filter((n) => Number.isFinite(n)),
    );
    const maxIncomingY = Math.max(
      0,
      ...incomingSlots
        .map((s) => Number(s?.y || 0))
        .filter((n) => Number.isFinite(n)),
    );
    const isLegacyPackedGrid = maxIncomingX <= 10 && maxIncomingY <= 10;

    const slotById = new Map(incomingSlots.map((s) => [String(s.slotId), s]));
    const legacyS1 = Number(slotById.get("S1")?.x || 0);
    const legacyS2 = Number(slotById.get("S2")?.x || 0);
    const legacyS6 = Number(slotById.get("S6")?.x || 0);
    const isLegacyV1Layout =
      legacyS1 > 0 &&
      legacyS1 < 25 &&
      legacyS2 > 0 &&
      legacyS2 < 30 &&
      legacyS6 > 60;

    const boardSlots = fallback.boardSlots.map((slot) => {
      const match = incomingSlots.find(
        (x) => String(x.slotId) === String(slot.slotId),
      );
      if (!match) return slot;

      const matchX = Number(match.x);
      const matchY = Number(match.y);

      return {
        ...slot,
        x:
          !isLegacyPackedGrid && !isLegacyV1Layout && Number.isFinite(matchX)
            ? matchX
            : slot.x,
        y:
          !isLegacyPackedGrid && !isLegacyV1Layout && Number.isFinite(matchY)
            ? matchY
            : slot.y,
        towerType: String(match.towerType || ""),
      };
    });

    const startItems = {
      orange: String(incoming?.startItems?.orange || ""),
      purple: String(incoming?.startItems?.purple || ""),
      blue: String(incoming?.startItems?.blue || ""),
      green: String(incoming?.startItems?.green || ""),
    };

    const waveMap = new Map(
      (incoming.waveItems || [])
        .map((x) => ({ wave: Number(x.wave), item: String(x.item || "") }))
        .filter((x) => Number.isFinite(x.wave)),
    );

    const waveItems = fallback.waveItems.map((w) => ({
      wave: w.wave,
      item: waveMap.get(w.wave)?.item || "",
    }));

    setZombiePlan({ boardSlots, startItems, waveItems });
    setActiveZombieSlotId((prev) => {
      if (prev && boardSlots.some((s) => s.slotId === prev)) return prev;
      return boardSlots[0]?.slotId || "S1";
    });
  }, [buildDefaultZombiePlan, selectedEvent]);

  const updateSelectedAlliance = (index, value) => {
    setSelectedAllianceIds((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const getAvailableCreateAlliances = (index) => {
    const selectedElsewhere = new Set(
      (selectedAllianceIds || [])
        .map((id, idx) => (idx === index ? "" : String(id || "")))
        .filter(Boolean),
    );

    return alliances.filter((alliance) => {
      const allianceId = String(alliance?._id || "");
      if (!allianceId) return false;
      return !selectedElsewhere.has(allianceId);
    });
  };

  const updateEventAllianceSlot = (index, value) => {
    setEventAllianceSlotIds((prev) => {
      const next = [...prev];
      next[index] = value;
      const uniqueIds = Array.from(new Set(next.map((id) => String(id || "")).filter(Boolean)));
      setEventAllianceDraftIds(uniqueIds);
      return next;
    });
  };

  const handleCancelEventAllianceEdit = () => {
    const next = Array.from(new Set((selectedEvent?.alliances || []).map((a) => String(a?._id || a || "")).filter(Boolean)));
    const slotCount = Math.max(Number(selectedEventTypeConfig?.requiredAllianceCount || 0), next.length);
    const slots = next.slice(0, slotCount || next.length);
    if (slotCount > 0) {
      while (slots.length < slotCount) slots.push("");
    }

    setEventAllianceDraftIds(next);
    setEventAllianceSlotIds(slots);
    setIsEventAllianceEditorOpen(false);
  };

  const getAvailableEventAlliances = (index) => {
    const selectedElsewhere = new Set(
      (eventAllianceSlotIds || [])
        .map((id, idx) => (idx === index ? "" : String(id || "")))
        .filter(Boolean),
    );

    return alliances.filter((alliance) => {
      const allianceId = String(alliance?._id || "");
      if (!allianceId) return false;
      return !selectedElsewhere.has(allianceId);
    });
  };

  const openStrongholdEditor = (stronghold) => {
    const commanderId = getId(stronghold?.defendingCommander);
    const normalizedItem = normalizeItem(stronghold?.item);
    const safeItem =
      !commanderId && normalizedItem && normalizedItem !== "guidedBomb"
        ? ""
        : normalizedItem;

    setEditingStrongholdId(String(stronghold?._id || ""));
    setSlotEditor({
      defendingCommander: commanderId,
      item: safeItem,
    });
  };

  const closeStrongholdEditor = () => {
    setEditingStrongholdId("");
    setSlotEditor({ defendingCommander: "", item: "" });
  };

  const handleSaveStrongholdEditor = async () => {
    if (!editingStronghold) return;

    const patch = {};
    const currentCommander = getId(editingStronghold.defendingCommander);
    const currentItem = normalizeItem(editingStronghold.item);

    if (slotEditor.defendingCommander !== currentCommander) {
      patch.defendingCommander = slotEditor.defendingCommander || null;
    }

    const selectedItem = normalizeItem(slotEditor.item);
    if (selectedItem !== currentItem) {
      patch.item = selectedItem || null;
    }

    if (!Object.keys(patch).length) {
      closeStrongholdEditor();
      return;
    }

    await handleStrongholdUpdate(editingStronghold._id, patch);
    closeStrongholdEditor();
  };

  const handleQuickCreateNation = async (targetIndex = null, preferredNumber = "") => {
    if (!canWriteNations) {
      window.alert("You do not have permission to create nations.");
      return null;
    }

    const numberInput = window.prompt(
      "Enter new nation number (e.g., 1234)",
      preferredNumber ? String(preferredNumber) : "",
    );
    if (numberInput == null) return null;

    const nationNumber = Number(String(numberInput).trim());
    if (!Number.isFinite(nationNumber) || nationNumber <= 0) {
      window.alert("Nation number must be a positive number.");
      return null;
    }

    try {
      const created = await axiosInstance.post("/nations", { number: nationNumber });
      await loadNations();

      const nationId = String(created?.data?._id || "");
      if (targetIndex != null && nationId) {
        setSelectedParticipantNationIds((prev) => {
          const next = [...prev];
          next[targetIndex] = nationId;
          return next;
        });
      }

      window.alert(`Nation ${nationNumber} created.`);
      return created?.data || null;
    } catch (error) {
      console.error("Error creating nation:", error);
      window.alert(typeof error === "string" ? error : "Failed to create nation.");
      return null;
    }
  };

  const handleQuickCreateAlliance = async (slotIndex) => {
    if (!canWriteAlliances) {
      window.alert("You do not have permission to create alliances.");
      return;
    }

    const slotNationGuess = String(
      selectedParticipantNationIds?.[slotIndex] ||
      selectedParticipantNationIds?.find(Boolean) ||
      nationId ||
      "",
    );

    const defaultNationNumber = nations.find((n) => String(n._id) === slotNationGuess)?.number || "";
    const nationInput = window.prompt(
      "Nation number for the new alliance (creates nation if missing)",
      defaultNationNumber ? String(defaultNationNumber) : "",
    );
    if (nationInput == null) return;

    const nationNumber = Number(String(nationInput).trim());
    if (!Number.isFinite(nationNumber) || nationNumber <= 0) {
      window.alert("Nation number must be a positive number.");
      return;
    }

    let nation = (nations || []).find((n) => Number(n?.number) === nationNumber) || null;
    if (!nation) {
      const shouldCreateNation = window.confirm(
        `Nation ${nationNumber} does not exist yet. Create it now?`,
      );
      if (!shouldCreateNation) return;
      nation = await handleQuickCreateNation(null, String(nationNumber));
      if (!nation) return;
    }

    const acronymInput = window.prompt("Alliance acronym (3 letters/numbers)", "");
    if (acronymInput == null) return;
    const acronym = String(acronymInput).trim().toUpperCase();
    if (!/^[A-Z0-9]{3}$/.test(acronym)) {
      window.alert("Alliance acronym must be exactly 3 alphanumeric characters.");
      return;
    }

    const nameInput = window.prompt("Alliance name", acronym);
    if (nameInput == null) return;
    const name = String(nameInput).trim();
    if (!name) {
      window.alert("Alliance name is required.");
      return;
    }

    try {
      const created = await axiosInstance.post(`/nations/${nation._id}/alliances/`, {
        name,
        acronym,
        territoryColor: "#6b7280",
      });

      await loadAlliances();
      const allianceId = String(created?.data?._id || "");

      if (allianceId) {
        setSelectedAllianceIds((prev) => {
          const next = [...prev];
          next[slotIndex] = allianceId;
          return next;
        });

      }

      window.alert(`Alliance ${acronym} created.`);
    } catch (error) {
      console.error("Error creating alliance:", error);
      window.alert(typeof error === "string" ? error : "Failed to create alliance.");
    }
  };

  const handleCreate = async (options = {}) => {
    const requiredAllianceCount = Number(
      activeCreateTypeConfig?.requiredAllianceCount || 0,
    );
    const requiredNationCount = Number(
      activeCreateTypeConfig?.requiredNationCount || 0,
    );
    const cleanAllianceIds = selectedAllianceIds
      .slice(0, requiredAllianceCount)
      .map((x) => String(x || "").trim())
      .filter(Boolean);
    const uniqueAllianceIds = [...new Set(cleanAllianceIds)];
    const cleanNationIds = selectedParticipantNationIds
      .slice(0, requiredNationCount)
      .map((x) => String(x || "").trim())
      .filter(Boolean);
    const uniqueNationIds = [...new Set(cleanNationIds)];

    if (!nationId)
      return window.alert(
        "Missing nation context. Please re-login and try again.",
      );
    if (requiredAllianceCount > 0 && uniqueAllianceIds.length > requiredAllianceCount)
      return window.alert(
        `Please select up to ${requiredAllianceCount} alliances.`,
      );
    if (requiredNationCount > 0 && uniqueNationIds.length > requiredNationCount)
      return window.alert(
        `Please select up to ${requiredNationCount} participant nations.`,
      );

    setIsCreating(true);
    try {
      const resolvedEventDate = options.eventDate || eventDate;
      const resolvedEventTime24 = isFixedFullDayEventType(eventType)
        ? ""
        : (options.eventTime24 || eventTime24);
      const created = await axiosInstance.post("/events", {
        nationId,
        eventType,
        resultMode: activeCreateTypeConfig?.defaultResultMode || "alliance",
        resultModes: activeCreateTypeConfig?.supportedResultModes || ["alliance"],
        startsAt: buildStartsAtIso(resolvedEventDate, resolvedEventTime24),
        startTime24: normalizeTime24Input(resolvedEventTime24),
        maxDefenders: Number(maxDefenders || 70),
        maxDefendersPerIsland: 10,
        alliances: uniqueAllianceIds,
        participantNationIds: uniqueNationIds,
      });
      setEventDate("");
      setEventTime24("");
      setSelectedAllianceIds(Array.from({ length: maxAllianceSlots }, () => ""));
      setSelectedParticipantNationIds(
        Array.from({ length: maxNationSlots }, (_, idx) => (idx === 0 ? nationId : "")),
      );
      setIsCreatePanelOpen(false);
      await loadEvents();
      const createdId = created?.data?._id || "";
      if (createdId) openEventDetail(createdId);

      const shouldOpenUpload = Boolean(options.openUploadAfterCreate || createAndOpenUpload);
      if (shouldOpenUpload && createdId) {
        setActiveTab("results");
        setResultsPanelOpen((prev) => ({ ...prev, entry: true }));
        setResultEntryMode("upload");
        setPendingTemplateAutoSelect({
          eventId: createdId,
          eventType,
          visibleRows: uploadLaunchConfig.visibleRows,
          resultsScope: uploadLaunchConfig.resultsScope,
        });
      }
      setCreateAndOpenUpload(false);
    } catch (error) {
      console.error("Error creating event:", error);
      window.alert(
        typeof error === "string" ? error : "Failed to create event.",
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleSaveEventMeta = async ({ eventDate: nextEventDate, startTime24 }) => {
    if (!selectedEventId) return;
    const normalizedTime = normalizeTime24Input(startTime24);
    if (String(startTime24 || "").trim() && !normalizedTime) {
      window.alert("Event time must be in 24-hour format HH:mm.");
      return;
    }

    setIsSavingEventMeta(true);
    try {
      await axiosInstance.patch(`/events/${selectedEventId}`, {
        startsAt: buildStartsAtIso(nextEventDate, normalizedTime),
        startTime24: normalizedTime,
      });
      await loadEventDetail(selectedEventId);
      await loadEvents();
    } catch (error) {
      console.error("Error saving event metadata:", error);
      window.alert(typeof error === "string" ? error : "Failed to save event metadata.");
    } finally {
      setIsSavingEventMeta(false);
    }
  };

  const handleSaveEventDateEditor = async () => {
    const draft = eventDateEditor || {};
    const targetEventId = String(draft.eventId || "").trim();
    if (!targetEventId) return;
    if (!draft.eventDate) {
      window.alert("Event date is required.");
      return;
    }
    const isFixedFullDay = isFixedFullDayEventType(draft.eventType);
    const normalizedTime = isFixedFullDay ? "" : normalizeTime24Input(draft.startTime24);
    if (!isFixedFullDay && String(draft.startTime24 || "").trim() && !normalizedTime) {
      window.alert("Event time must be in 24-hour format HH:mm.");
      return;
    }

    setIsSavingEventMeta(true);
    try {
      await axiosInstance.patch(`/events/${targetEventId}`, {
        startsAt: buildStartsAtIso(draft.eventDate, normalizedTime),
        startTime24: normalizedTime,
      });
      if (String(selectedEventId || "") === targetEventId) {
        await loadEventDetail(targetEventId);
      }
      await loadEvents();
      setEventDateEditor(null);
    } catch (error) {
      console.error("Error saving event date:", error);
      window.alert(typeof error === "string" ? error : "Failed to save event date.");
    } finally {
      setIsSavingEventMeta(false);
    }
  };

  const handleSaveEventAlliances = async () => {
    if (!selectedEventId) return;
    const maxAllowed = Number(selectedEventTypeConfig?.requiredAllianceCount || 0);
    const uniqueIds = Array.from(new Set((eventAllianceDraftIds || []).map((id) => String(id || "")).filter(Boolean)));

    if (maxAllowed > 0) {
      if (uniqueIds.length > maxAllowed) {
        window.alert(`This event type supports up to ${maxAllowed} alliances.`);
        return;
      }
    }

    setIsSavingEventAlliances(true);
    try {
      await axiosInstance.patch(`/events/${selectedEventId}`, { alliances: uniqueIds });
      await loadEventDetail(selectedEventId);
      await loadEvents();
    } catch (error) {
      console.error("Error saving event alliances:", error);
      window.alert(typeof error === "string" ? error : "Failed to save event alliances.");
    } finally {
      setIsSavingEventAlliances(false);
    }
  };

  const applyLocalStrongholdPatch = (strongholdId, patch) => {
    setSelectedEvent((prev) => {
      if (!prev) return prev;
      const nextStrongholds = (prev.strongholds || []).map((s) =>
        String(s._id) === String(strongholdId) ? { ...s, ...patch } : s,
      );
      return { ...prev, strongholds: nextStrongholds };
    });
  };

  const upsertLocalBattle = (battleId, patch) => {
    if (!battleId) return;
    setSelectedEvent((prev) => {
      if (!prev) return prev;
      const battles = Array.isArray(prev.battles) ? prev.battles : [];
      const nextBattles = battles.some((battle) => String(battle?._id || "") === String(battleId))
        ? battles.map((battle) => (
            String(battle?._id || "") === String(battleId)
              ? { ...battle, ...patch, _id: battle._id || battleId }
              : battle
          ))
        : [...battles, { ...patch, _id: battleId }];
      return { ...prev, battles: nextBattles };
    });
  };

  const removeLocalBattle = (battleId) => {
    if (!battleId) return;
    setSelectedEvent((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        battles: (prev.battles || []).filter((battle) => String(battle?._id || "") !== String(battleId)),
      };
    });
  };

  const handleStrongholdUpdate = async (strongholdId, patch) => {
    if (!selectedEventId) return;
    try {
      const currentStronghold = (selectedEvent?.strongholds || []).find(
        (s) => String(s._id) === String(strongholdId),
      );
      const nextPatch = { ...patch };
      const hasCommanderPatch = Object.prototype.hasOwnProperty.call(
        nextPatch,
        "defendingCommander",
      );
      const hasItemPatch = Object.prototype.hasOwnProperty.call(
        nextPatch,
        "item",
      );
      const commanderId =
        hasCommanderPatch && nextPatch?.defendingCommander
          ? String(nextPatch.defendingCommander)
          : "";

      if (commanderId) {
        const existingPlacement = commanderPlacementById[commanderId];
        if (
          existingPlacement &&
          String(existingPlacement.strongholdId) !== String(strongholdId)
        ) {
          const targetName =
            strongholdNameById[String(strongholdId)] || "target stronghold";
          const ok = window.confirm(
            `${commanderNameById[commanderId] || "Commander"} is already placed at ${existingPlacement.strongholdName}. Move to ${targetName}?`,
          );
          if (!ok) return;

          // Move flow: unassign previous slot first, then assign new slot.
          await axiosInstance.patch(
            `/events/${selectedEventId}/strongholds/${existingPlacement.strongholdId}`,
            { defendingCommander: null },
          );
          applyLocalStrongholdPatch(existingPlacement.strongholdId, {
            defendingCommander: null,
            item: null,
          });
        }
      }

      if (hasCommanderPatch && !hasItemPatch && currentStronghold) {
        const currentItem = normalizeItem(currentStronghold.item);
        const nextCommanderId = nextPatch.defendingCommander
          ? String(nextPatch.defendingCommander)
          : "";

        if (!nextCommanderId && currentItem && currentItem !== "guidedBomb") {
          nextPatch.item = null;
        }
      }

      await axiosInstance.patch(
        `/events/${selectedEventId}/strongholds/${strongholdId}`,
        nextPatch,
      );
      applyLocalStrongholdPatch(strongholdId, nextPatch);
    } catch (error) {
      console.error("Error updating stronghold:", error);
      window.alert(
        typeof error === "string" ? error : "Failed to update stronghold.",
      );
      await loadEventDetail(selectedEventId);
    }
  };

  const saveInfiniteZombiePlan = useCallback(
    async (nextPlan) => {
      if (!selectedEventId) return;
      setIsZombieSaving(true);
      try {
        const payload = {
          boardSlots: (nextPlan.boardSlots || []).map((slot) => ({
            slotId: slot.slotId,
            x: Number(slot.x || 0),
            y: Number(slot.y || 0),
            towerType: slot.towerType || null,
          })),
          startItems: {
            orange: String(nextPlan?.startItems?.orange || ""),
            purple: String(nextPlan?.startItems?.purple || ""),
            blue: String(nextPlan?.startItems?.blue || ""),
            green: String(nextPlan?.startItems?.green || ""),
          },
          waveItems: (nextPlan.waveItems || []).map((w) => ({
            wave: Number(w.wave),
            item: String(w.item || ""),
          })),
        };
        await axiosInstance.patch(
          `/events/${selectedEventId}/infinite-zombie`,
          payload,
        );
      } catch (error) {
        console.error("Error saving infinite zombie plan:", error);
        window.alert(
          typeof error === "string"
            ? error
            : "Failed to save infinite zombie plan.",
        );
      } finally {
        setIsZombieSaving(false);
      }
    },
    [selectedEventId],
  );

  const updateZombieTower = async (slotId, towerType) => {
    const next = {
      ...zombiePlan,
      boardSlots: (zombiePlan.boardSlots || []).map((slot) =>
        String(slot.slotId) === String(slotId)
          ? { ...slot, towerType: towerType || "" }
          : slot,
      ),
    };
    setZombiePlan(next);
    await saveInfiniteZombiePlan(next);
  };

  const updateZombieStartItem = async (tier, value) => {
    const next = {
      ...zombiePlan,
      startItems: {
        ...zombiePlan.startItems,
        [tier]: value || "",
      },
    };
    setZombiePlan(next);
    await saveInfiniteZombiePlan(next);
  };

  const updateZombieWaveItem = async (wave, value) => {
    const next = {
      ...zombiePlan,
      waveItems: (zombiePlan.waveItems || []).map((entry) =>
        Number(entry.wave) === Number(wave)
          ? { ...entry, item: value || "" }
          : entry,
      ),
    };
    setZombiePlan(next);
    await saveInfiniteZombiePlan(next);
  };

  const clampZombieMapPan = useCallback(
    (pan, zoomLevel = zombieMapZoom) => {
      const mapEl = zombieMapRef.current;
      if (!mapEl || zoomLevel <= 1) return { x: 0, y: 0 };

      const width = mapEl.clientWidth || 0;
      const height = mapEl.clientHeight || 0;
      const maxX = ((zoomLevel - 1) * width) / 2;
      const maxY = ((zoomLevel - 1) * height) / 2;

      return {
        x: Math.max(-maxX, Math.min(maxX, pan.x || 0)),
        y: Math.max(-maxY, Math.min(maxY, pan.y || 0)),
      };
    },
    [zombieMapZoom],
  );

  const handleZombieMapWheel = (e) => {
    e.preventDefault();
    const direction = e.deltaY < 0 ? 1 : -1;
    setZombieMapZoom((prev) => {
      const next = Math.max(
        0.85,
        Math.min(2.25, Number((prev + direction * 0.1).toFixed(2))),
      );
      setZombieMapPan((currentPan) => clampZombieMapPan(currentPan, next));
      return next;
    });
  };

  const handleZombieMapPointerDown = (e) => {
    zombieMapPointersRef.current.set(e.pointerId, {
      x: e.clientX,
      y: e.clientY,
    });

    const pointers = Array.from(zombieMapPointersRef.current.values());
    if (pointers.length >= 2) {
      const [a, b] = pointers;
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      zombieMapPinchRef.current = {
        active: true,
        startDistance: Math.max(1, distance),
        startZoom: zombieMapZoom,
        startPan: { ...zombieMapPan },
        startMidpoint: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
      };
      zombieMapSuppressClickRef.current = true;
      setIsZombieMapDragging(true);
      return;
    }

    if (zombieMapZoom <= 1) return;
    zombieMapDragRef.current = {
      pointerId: e.pointerId,
      dragging: true,
      moved: false,
      captured: false,
      startX: e.clientX,
      startY: e.clientY,
      originX: zombieMapPan.x,
      originY: zombieMapPan.y,
    };
  };

  const handleZombieMapPointerMove = (e) => {
    if (zombieMapPointersRef.current.has(e.pointerId)) {
      zombieMapPointersRef.current.set(e.pointerId, {
        x: e.clientX,
        y: e.clientY,
      });
    }

    const pointers = Array.from(zombieMapPointersRef.current.values());
    if (zombieMapPinchRef.current.active && pointers.length >= 2) {
      const [a, b] = pointers;
      const distance = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y));
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const pinch = zombieMapPinchRef.current;

      const nextZoom = Math.max(
        0.85,
        Math.min(
          2.25,
          Number(
            (pinch.startZoom * (distance / pinch.startDistance)).toFixed(2),
          ),
        ),
      );
      const nextPan = clampZombieMapPan(
        {
          x: pinch.startPan.x + (mid.x - pinch.startMidpoint.x),
          y: pinch.startPan.y + (mid.y - pinch.startMidpoint.y),
        },
        nextZoom,
      );

      setZombieMapZoom(nextZoom);
      setZombieMapPan(nextPan);
      return;
    }

    const drag = zombieMapDragRef.current;
    if (!drag.dragging || drag.pointerId !== e.pointerId) return;

    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    const distance = Math.hypot(dx, dy);

    if (!drag.moved && distance > 8) {
      drag.moved = true;
      drag.captured = true;
      zombieMapSuppressClickRef.current = true;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      setIsZombieMapDragging(true);
    }

    if (!drag.moved) return;

    setZombieMapPan(
      clampZombieMapPan({
        x: drag.originX + dx,
        y: drag.originY + dy,
      }),
    );
  };

  const handleZombieMapPointerUp = (e) => {
    zombieMapPointersRef.current.delete(e.pointerId);

    const remainingPointers = Array.from(zombieMapPointersRef.current.values());
    if (zombieMapPinchRef.current.active && remainingPointers.length < 2) {
      zombieMapPinchRef.current.active = false;
      if (remainingPointers.length === 1 && zombieMapZoom > 1) {
        const [p] = remainingPointers;
        zombieMapDragRef.current = {
          pointerId: Array.from(zombieMapPointersRef.current.keys())[0],
          dragging: true,
          moved: false,
          captured: false,
          startX: p.x,
          startY: p.y,
          originX: zombieMapPan.x,
          originY: zombieMapPan.y,
        };
      }
    }

    const drag = zombieMapDragRef.current;
    if (drag.pointerId === e.pointerId && drag.captured) {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    }

    if (drag.pointerId === e.pointerId) {
      zombieMapDragRef.current = {
        pointerId: null,
        dragging: false,
        moved: false,
        captured: false,
        startX: 0,
        startY: 0,
        originX: 0,
        originY: 0,
      };
    }

    if (zombieMapPointersRef.current.size === 0) {
      setIsZombieMapDragging(false);
      setTimeout(() => {
        zombieMapSuppressClickRef.current = false;
      }, 0);
    }
  };

  useEffect(() => {
    setZombieMapPan((currentPan) => clampZombieMapPan(currentPan));
  }, [zombieMapZoom, clampZombieMapPan]);

  const activeZombieSlot = useMemo(
    () =>
      (zombiePlan.boardSlots || []).find(
        (slot) => String(slot.slotId) === String(activeZombieSlotId),
      ) || null,
    [activeZombieSlotId, zombiePlan.boardSlots],
  );

  const zombieTowerLabelByValue = useMemo(() => {
    const out = {};
    INFINITE_ZOMBIE_TOWERS.forEach((t) => {
      out[t.value] = t.label;
    });
    return out;
  }, []);

  const zombieTowerShortByValue = useMemo(
    () => ({
      "": "",
      machineGun: "MG",
      rocket: "R",
      emp: "E",
      laser: "L",
    }),
    [],
  );

  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  const exportInfiniteZombieSharePng = async () => {
    try {
      const canvas = document.createElement("canvas");
      const width = 1600;
      const height = 1800;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.fillStyle = "#0f172a";
      ctx.fillRect(0, 0, width, height);

      ctx.fillStyle = "#f8fafc";
      ctx.font = "bold 46px system-ui";
      ctx.fillText(
        `Infinite Zombies Â· ${selectedEvent?.name || "Plan"}`,
        46,
        68,
      );
      ctx.fillStyle = "#94a3b8";
      ctx.font = "24px system-ui";
      ctx.fillText(new Date().toLocaleString(), 46, 102);

      const boardX = 46;
      const boardY = 130;
      const boardW = 980;
      const boardH = Math.round((boardW * 917) / 998);
      const sideX = boardX + boardW + 24;
      const sideW = width - sideX - 46;

      const boardImg = await loadImage(zombieBoardMap);
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(boardX, boardY, boardW, boardH, 14);
      ctx.clip();
      ctx.drawImage(boardImg, boardX, boardY, boardW, boardH);
      ctx.restore();

      const towerImageByValue = {};
      await Promise.all(
        Object.entries(INFINITE_ZOMBIE_TOWER_IMAGES).map(async ([key, src]) => {
          try {
            towerImageByValue[key] = await loadImage(src);
          } catch {
            towerImageByValue[key] = null;
          }
        }),
      );

      (zombiePlan.boardSlots || []).forEach((slot) => {
        const x = boardX + (Number(slot.x || 0) / 100) * boardW;
        const y = boardY + (Number(slot.y || 0) / 100) * boardH;
        const r = 18;

        if (slot.towerType && towerImageByValue[slot.towerType]) {
          ctx.save();
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(
            towerImageByValue[slot.towerType],
            x - r,
            y - r,
            r * 2,
            r * 2,
          );
          ctx.restore();
        } else {
          const g = ctx.createRadialGradient(x - 5, y - 5, 3, x, y, r);
          g.addColorStop(0, "#60a5fa");
          g.addColorStop(1, "#1d4ed8");
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      ctx.strokeStyle = "rgba(148,163,184,0.45)";
      ctx.lineWidth = 2;
      ctx.strokeRect(boardX, boardY, boardW, boardH);

      ctx.fillStyle = "#e2e8f0";
      ctx.font = "bold 28px system-ui";
      ctx.fillText("Item Picks", sideX, boardY + 4);

      const itemImageByValue = {};
      await Promise.all(
        Object.entries(infiniteZombieItemMetaByValue).map(
          async ([value, meta]) => {
            if (!meta?.icon) return;
            try {
              itemImageByValue[value] = await loadImage(meta.icon);
            } catch {
              itemImageByValue[value] = null;
            }
          },
        ),
      );

      const drawItemRow = (label, value, y) => {
        const meta = infiniteZombieItemMetaByValue[value || ""];
        ctx.fillStyle = "#1e293b";
        ctx.fillRect(sideX, y - 28, sideW, 52);
        ctx.strokeStyle = "#334155";
        ctx.strokeRect(sideX, y - 28, sideW, 52);

        ctx.fillStyle = "#cbd5e1";
        ctx.font = "bold 18px system-ui";
        ctx.fillText(label, sideX + 12, y + 2);

        if (meta?.icon && itemImageByValue[value]) {
          ctx.drawImage(itemImageByValue[value], sideX + 88, y - 20, 36, 36);
        }
        ctx.fillStyle = meta?.color || "#94a3b8";
        ctx.font = "16px system-ui";
        ctx.fillText(meta?.label || "No pick", sideX + 132, y + 3);
      };

      let cursorY = boardY + 44;
      ["orange", "purple", "blue", "green"].forEach((tier) => {
        drawItemRow(
          `Start Â· ${tier.toUpperCase()}`,
          zombiePlan.startItems?.[tier],
          cursorY,
        );
        cursorY += 60;
      });

      cursorY += 12;
      (zombiePlan.waveItems || []).forEach((entry) => {
        drawItemRow(`Wave ${entry.wave}`, entry.item, cursorY);
        cursorY += 56;
      });

      const blob = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/png"),
      );
      if (!blob) return;

      const safeName = String(selectedEvent?.name || "infinite-zombie")
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, "-");

      const file = new File([blob], `${safeName}-share.png`, {
        type: "image/png",
      });
      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({
          title: `Infinite Zombies Â· ${selectedEvent?.name || "Plan"}`,
          text: "Infinite Zombies board + item picks",
          files: [file],
        });
        return;
      }

      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${safeName}-share.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (error) {
      console.error("Failed to share/export Infinite Zombies image", error);
    }
  };

  const parseStrongholdNumber = (name = "") => {
    const match = String(name).match(/([A-Z])(\d+)/i);
    if (!match) return { island: "", number: 999 };
    return { island: String(match[1]).toUpperCase(), number: Number(match[2]) };
  };

  const CITY_TO_TROOP_TIER = [
    { min: 1, max: 3, tier: "T1" },
    { min: 4, max: 6, tier: "T2" },
    { min: 7, max: 9, tier: "T3" },
    { min: 10, max: 12, tier: "T4" },
    { min: 13, max: 15, tier: "T5" },
    { min: 16, max: 18, tier: "T6" },
    { min: 19, max: 21, tier: "T7" },
    { min: 22, max: 25, tier: "T8" },
    { min: 26, max: 29, tier: "T9" },
    { min: 30, max: 33, tier: "T10" },
    { min: 34, max: 36, tier: "T11" },
    { min: 37, max: 39, tier: "T12" },
    { min: 40, max: 41, tier: "T13" },
  ];

  // BP per unit (Troops_Stats averages by tier from the AoZ workbook)
  const TROOP_BP_PER_UNIT_BY_TIER = {
    T1: 1.0,
    T2: 1.5,
    T3: 2.1,
    T4: 2.8,
    T5: 3.7086,
    T6: 4.6286,
    T7: 5.6657,
    T8: 6.8886,
    T9: 8.3314,
    T10: 9.9743,
    T11: 11.9371,
    T12: 14.2986,
    T13: 16.98,
  };

  // Buildings table (garage fleet size bonuses), level 1..41
  const GARAGE_FLEET_BONUS_MAIN = {
    1: 2000,
    2: 4100,
    3: 6300,
    4: 8600,
    5: 11000,
    6: 13500,
    7: 16100,
    8: 18800,
    9: 21600,
    10: 24600,
    11: 27800,
    12: 31200,
    13: 34800,
    14: 38600,
    15: 42600,
    16: 46800,
    17: 51200,
    18: 55800,
    19: 60600,
    20: 65600,
    21: 70800,
    22: 76200,
    23: 81800,
    24: 87600,
    25: 93600,
    26: 99800,
    27: 106200,
    28: 112800,
    29: 119600,
    30: 126600,
    31: 133800,
    32: 141200,
    33: 148800,
    34: 156600,
    35: 164600,
    36: 170600,
    37: 176600,
    38: 182600,
    39: 182600,
    40: 182600,
    41: 182600,
  };

  // Extra garage fleet bonus, level 1..41
  const GARAGE_FLEET_BONUS_EXTRA = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
    6: 0,
    7: 0,
    8: 0,
    9: 0,
    10: 0,
    11: 0,
    12: 0,
    13: 0,
    14: 0,
    15: 0,
    16: 0,
    17: 0,
    18: 0,
    19: 0,
    20: 0,
    21: 0,
    22: 0,
    23: 0,
    24: 0,
    25: 0,
    26: 0,
    27: 0,
    28: 0,
    29: 0,
    30: 0,
    31: 0,
    32: 0,
    33: 0,
    34: 0,
    35: 0,
    36: 0,
    37: 0,
    38: 0,
    39: 6000,
    40: 12000,
    41: 18000,
  };

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const inferHighestTroopTier = (cityLevelRaw) => {
    const cityLevel = clamp(Number(cityLevelRaw || 1), 1, 41);
    const found = CITY_TO_TROOP_TIER.find(
      (x) => cityLevel >= x.min && cityLevel <= x.max,
    );
    return found?.tier || "T1";
  };

  const estimateFleetBp = (commander) => {
    if (!commander) return null;

    const cityLevel = clamp(Number(commander.cityLevel || 1), 1, 41);
    const officerPower = Number(commander?.rankingData?.officer?.power || 0);
    const titanPower = Number(commander?.rankingData?.titan?.power || 0);
    const warplanePower = Number(commander?.rankingData?.warplane?.power || 0);

    // Fleet size proxy model (aligned to Fleetsize Analysis structure + agreed assumptions)
    const commanderBase = 10000;
    const meritRankBonus = Math.round(27500 * clamp(cityLevel / 35, 0.2, 1));
    const garageMain = Number(GARAGE_FLEET_BONUS_MAIN[cityLevel] || 0);
    const garageExtra = Number(GARAGE_FLEET_BONUS_EXTRA[cityLevel] || 0);
    const reactorBonus = Math.round(12000 * clamp(cityLevel / 39, 0.35, 1));
    const commandantBadgeBonus = Math.round(
      1520 * clamp(cityLevel / 39, 0.4, 1),
    );
    const academyLeadershipBonus =
      cityLevel >= 20 ? 21000 : Math.round(21000 * (cityLevel / 20));
    const islandBonus = 6000;
    const groupBuyingBonus = 2000;

    // Assume Morgan + Saki appointed; infer strength from officer power.
    const officerScale = clamp(officerPower / 20000000, 0.35, 1.25);
    const morganBonus = Math.round(72728 * officerScale);
    const sakiBonus = Math.round(44100 * officerScale);

    // Assume fleet size buffs are active for defense (per user requirement).
    const expansionBuffRate = 0.25;
    const expansionBuffBonus = Math.round(
      (commanderBase + garageMain + reactorBonus + academyLeadershipBonus) *
        expansionBuffRate,
    );

    const fleetSizeUnits = Math.max(
      0,
      commanderBase +
        meritRankBonus +
        garageMain +
        garageExtra +
        reactorBonus +
        commandantBadgeBonus +
        academyLeadershipBonus +
        morganBonus +
        sakiBonus +
        islandBonus +
        expansionBuffBonus +
        groupBuyingBonus,
    );

    const highestTier = inferHighestTroopTier(cityLevel);
    const bpPerUnit = Number(TROOP_BP_PER_UNIT_BY_TIER[highestTier] || 0);
    const troopsBp = fleetSizeUnits * bpPerUnit;

    // User rule: one sent warplane ~= total warplane power minus 4 parked planes at 35k each.
    const warplaneSentBp = Math.max(0, warplanePower - 4 * 35000);

    // User rule: both titans are sent; use full titan power.
    const fleetBp = Math.round(troopsBp + titanPower + warplaneSentBp);
    return fleetBp > 0 ? fleetBp : null;
  };

  const toHeatColor = (intensity, isEmpty) => {
    if (isEmpty) return "rgba(142, 165, 186, 0.18)";
    const clamped = Math.max(0, Math.min(1, intensity));
    // blue -> teal -> warm red as strength increases
    const hue = 210 - clamped * 200;
    const sat = 75;
    const light = 48 - clamped * 16;
    return `hsl(${hue}, ${sat}%, ${light}%)`;
  };

  const getIslandStrongholds = (letter) => {
    return (selectedEvent?.strongholds || [])
      .filter((s) => parseStrongholdNumber(s.name).island === letter)
      .sort(
        (a, b) =>
          parseStrongholdNumber(a.name).number -
          parseStrongholdNumber(b.name).number,
      );
  };

  const buildSlotOrder = (mode) => {
    const idx = (arr, num) =>
      arr.find((s) => parseStrongholdNumber(s.name).number === num)?._id;
    const numbersToIds = (arr, nums) =>
      nums.map((n) => idx(arr, n)).filter(Boolean);
    const interleave = (groups) => {
      const queues = groups.map((g) => [...g]);
      const out = [];
      let progressed = true;
      while (progressed) {
        progressed = false;
        for (const q of queues) {
          if (q.length) {
            out.push(q.shift());
            progressed = true;
          }
        }
      }
      return out;
    };
    const shuffle = (arr) => {
      const copy = [...arr];
      for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      return copy;
    };

    const A = getIslandStrongholds("A");
    const B = getIslandStrongholds("B");
    const C = getIslandStrongholds("C");
    const D = getIslandStrongholds("D");
    const E = getIslandStrongholds("E");
    const F = getIslandStrongholds("F");
    const G = getIslandStrongholds("G");

    const byKey = { A, B, C, D, E, F, G };
    const priority = ["A", "B", "C", "D", "E", "F", "G"]; // A > B/C/D > E/F/G
    const entrySlots = [1, 2, 3, 4];
    const nonEntrySlots = [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];

    if (mode === "rude") {
      // Round-robin hard wall across enemy-entry islands first, but randomize within those lanes.
      const frontline = interleave(
        ["F", "G", "E"].map((k) => numbersToIds(byKey[k], shuffle(entrySlots))),
      );
      const frontlineInner = interleave(
        ["F", "G", "E"].map((k) =>
          numbersToIds(byKey[k], shuffle(nonEntrySlots)),
        ),
      );
      const rest = interleave(
        ["A", "B", "C", "D"].map((k) =>
          numbersToIds(byKey[k], shuffle([...entrySlots, ...nonEntrySlots])),
        ),
      );
      return [...new Set([...frontline, ...frontlineInner, ...rest])];
    }

    if (mode === "experimental") {
      // Attrition + trap pressure: distribute strongest in entry lanes round-robin by priority bands.
      const tier1 = interleave(
        ["A"].map((k) => numbersToIds(byKey[k], shuffle(entrySlots))),
      );
      const tier2 = interleave(
        ["B", "C", "D"].map((k) => numbersToIds(byKey[k], shuffle(entrySlots))),
      );
      const tier3 = interleave(
        ["E", "F", "G"].map((k) => numbersToIds(byKey[k], shuffle(entrySlots))),
      );
      const inner = interleave(
        priority.map((k) => numbersToIds(byKey[k], shuffle(nonEntrySlots))),
      );
      return [...new Set([...tier1, ...tier2, ...tier3, ...inner])];
    }

    // standard: mixed placements across islands with randomized slot order to avoid rigid patterns.
    const buildStandardIslandOrder = (k) => {
      const non = shuffle(nonEntrySlots);
      const ent = shuffle(entrySlots);
      const mixedNums = [
        non[0],
        ent[0],
        non[1],
        non[2],
        ent[1],
        non[3],
        non[4],
        ent[2],
        non[5],
        non[6],
        non[7],
        ent[3],
        non[8],
        non[9],
        non[10],
        non[11],
      ].filter((n) => Number.isFinite(n));
      return numbersToIds(byKey[k], mixedNums);
    };
    const standard = interleave(
      priority.map((k) => buildStandardIslandOrder(k)),
    );
    return [...new Set(standard)];
  };

  const handleAutoPopulate = async () => {
    if (!selectedEventId || isAutoPopulating) return;
    setIsAutoPopulating(true);
    try {
      const minLevel = Number(autoPopulateMinCityLevel || 20);
      const eligible = [...myAllianceCommanderOptions]
        .filter((c) => Number(c.cityLevel || 0) >= minLevel)
        .sort((a, b) => {
          const aRank = Number(a.blendedRank || 99999);
          const bRank = Number(b.blendedRank || 99999);
          if (aRank !== bRank) return aRank - bRank;
          return Number(b.battlePower || 0) - Number(a.battlePower || 0);
        });

      if (!eligible.length) {
        window.alert("No eligible commanders for selected minimum city level.");
        return;
      }

      const maxTotal = Number(selectedEvent?.maxDefenders || 70);
      const maxPerIsland = Number(selectedEvent?.maxDefendersPerIsland || 10);
      const strongholds = selectedEvent?.strongholds || [];
      const strongholdById = Object.fromEntries(
        strongholds.map((s) => [String(s._id), s]),
      );
      const slotOrder = buildSlotOrder(autoPopulateMode);

      const islandAssignedCounts = {};
      let totalAssigned = 0;
      for (const s of strongholds) {
        if (!s.defendingCommander) continue;
        totalAssigned += 1;
        const key = String(s.islandId || "");
        islandAssignedCounts[key] = Number(islandAssignedCounts[key] || 0) + 1;
      }

      if (autoPopulateForceReset) {
        const assigned = strongholds.filter((s) => !!s.defendingCommander);
        for (const s of assigned) {
          await axiosInstance.patch(
            `/events/${selectedEventId}/strongholds/${s._id}`,
            { defendingCommander: null },
          );
          applyLocalStrongholdPatch(s._id, {
            defendingCommander: null,
            item: null,
          });
        }
        totalAssigned = 0;
        Object.keys(islandAssignedCounts).forEach((k) => {
          islandAssignedCounts[k] = 0;
        });
      }

      const alreadyUsed = autoPopulateForceReset
        ? new Set()
        : new Set(
            strongholds
              .filter((s) => !!s.defendingCommander)
              .map((s) => String(s.defendingCommander)),
          );
      const available = eligible.filter((c) => !alreadyUsed.has(String(c._id)));

      let cursor = 0;
      let assignedThisRun = 0;

      for (const shId of slotOrder) {
        if (cursor >= available.length) break;
        if (totalAssigned >= maxTotal) break;

        const current = strongholdById[String(shId)];
        if (!current) continue;
        if (!autoPopulateForceReset && current.defendingCommander) continue;

        const islandKey = String(current.islandId || "");
        const islandCount = Number(islandAssignedCounts[islandKey] || 0);
        if (islandCount >= maxPerIsland) continue;

        const cmd = available[cursor++];
        await axiosInstance.patch(
          `/events/${selectedEventId}/strongholds/${shId}`,
          { defendingCommander: cmd._id },
        );
        applyLocalStrongholdPatch(shId, {
          defendingCommander: cmd._id,
          item: null,
        });

        totalAssigned += 1;
        islandAssignedCounts[islandKey] = islandCount + 1;
        assignedThisRun += 1;
      }

      if (!assignedThisRun) {
        window.alert(
          "No placements applied. Islands may already be at cap or no eligible empty slots remain.",
        );
      }

      setIsAutoPopulateOpen(false);
    } catch (error) {
      console.error("Auto populate failed:", error);
      window.alert(typeof error === "string" ? error : "Auto populate failed.");
      await loadEventDetail(selectedEventId);
    } finally {
      setIsAutoPopulating(false);
    }
  };

  const handleBattleSubmit = async () => {
    if (!selectedEventId) return;
    const previousBattle = editingBattleId
      ? (selectedEvent?.battles || []).find((battle) => String(battle?._id || "") === String(editingBattleId))
      : null;
    const payload = {
      ...battleForm,
      pointsAwarded:
        battleForm.pointsAwarded === ""
          ? null
          : Number(battleForm.pointsAwarded),
      attackingCommander: battleForm.attackingCommander || null,
      defendingCommander: battleForm.defendingCommander || null,
      screenshotDataUrl: battleForm.screenshotDataUrl || null,
    };

    try {
      let response;
      if (editingBattleId) {
        response = await axiosInstance.patch(
          `/events/${selectedEventId}/battles/${editingBattleId}`,
          payload,
        );
      } else {
        response = await axiosInstance.post(`/events/${selectedEventId}/battles`, payload);
      }
      const savedBattle = response?.data || {};
      const savedBattleId = String(savedBattle?._id || editingBattleId || "");
      if (savedBattleId) {
        upsertLocalBattle(savedBattleId, savedBattle);
      }
      if (previousBattle?.result === "attacker_win" && previousBattle?.strongholdId) {
        applyLocalStrongholdPatch(previousBattle.strongholdId, { ownerAlliance: null });
      }
      if (savedBattle?.result === "attacker_win" && savedBattle?.strongholdId) {
        applyLocalStrongholdPatch(savedBattle.strongholdId, { ownerAlliance: savedBattle.attackingAlliance || null });
      }
      setBattleForm((prev) => ({
        ...prev,
        attackingCommander: "",
        defendingCommander: "",
        pointsAwarded: "",
        notes: "",
        screenshotDataUrl: "",
        source: "manual",
      }));
      setEditingBattleId("");
      setIsBattleEntryModalOpen(false);
    } catch (error) {
      console.error("Error saving battle:", error);
      window.alert(
        typeof error === "string" ? error : "Failed to save battle.",
      );
    }
  };

  const startEditBattle = (battle) => {
    setEditingBattleId(battle._id);
    setIsBattleEntryModalOpen(true);
    setBattleForm({
      strongholdId: String(battle.strongholdId || ""),
      attackingAlliance: String(battle.attackingAlliance || ""),
      attackingCommander: String(battle.attackingCommander || ""),
      defendingAlliance: String(battle.defendingAlliance || ""),
      defendingCommander: String(battle.defendingCommander || ""),
      result: battle.result || "attacker_win",
      pointsAwarded: battle.pointsAwarded ?? "",
      source: battle.source || "manual",
      notes: battle.notes || "",
      screenshotDataUrl: battle.screenshotDataUrl || "",
    });
  };

  const openManualBattleModal = () => {
    setEditingBattleId("");
    setBattleForm((prev) => ({
      ...prev,
      attackingCommander: "",
      defendingCommander: "",
      result: "attacker_win",
      pointsAwarded: "",
      source: "manual",
      notes: "",
      screenshotDataUrl: "",
    }));
    setIsBattleEntryModalOpen(true);
  };

  const closeBattleEntryModal = () => {
    setEditingBattleId("");
    setIsBattleEntryModalOpen(false);
  };

  const getBattlesByIds = useCallback((battleIds = []) => {
    const idSet = new Set((Array.isArray(battleIds) ? battleIds : []).map((value) => String(value || "")).filter(Boolean));
    return (selectedEvent?.battles || [])
      .filter((battle) => idSet.has(String(battle?._id || "")))
      .sort((left, right) => (getBattleTimestampMs(left) || 0) - (getBattleTimestampMs(right) || 0));
  }, [selectedEvent?.battles]);

  const buildStrikeEvidenceRefs = useCallback((battleIds = []) => (
    getBattlesByIds(battleIds).map((battle, index) => {
      const timeLabel = formatBattleLogTime(battle.at || battle.battleTime);
      return {
        reasonModel: "EventBattle",
        reasonRef: battle._id,
        label: `${index + 1}. ${timeLabel} ${formatBattleLocation(battle)}`.trim(),
        summary: `${getBattleSideLabel(battle, "attacker")} vs ${getBattleSideLabel(battle, "defender")}`,
        battleTime: battle.at || battle.battleTime || battle.startsAt || null,
      };
    })
  ), [formatBattleLocation, formatBattleLogTime, getBattlesByIds, getBattleSideLabel]);

  const buildBubbleDropStrikeSummary = useCallback((battleIds = [], group = null) => {
    const battles = getBattlesByIds(battleIds);
    if (!battles.length) return { summaryLabel: "", summaryDetail: "", reasonNote: "" };
    const startingBattle = battles[0];
    const startingUnits = getBattleCityOwnerDefenderUnits(startingBattle);
    const count = Number(group?.battleCount || battles.length || 0);
    const summaryLabel = String(group?.ruleLabel || "Bubble drop").trim() || "Bubble drop";
    const defenseLabel = count === 1 ? "1 defense" : `${count.toLocaleString()} defenses`;
    const summaryDetail = `${defenseLabel}, starting with ${formatStrikeEvidenceUnits(startingUnits)} troops`;
    return {
      summaryLabel,
      summaryDetail,
      reasonNote: `${summaryLabel}, ${summaryDetail}`,
    };
  }, [getBattlesByIds]);

  const openStrikeModal = (battle, scopeOptions = null) => {
    const commanderRefs = getBattleStrikeCommanderRefs(battle);
    if (!commanderRefs.length) {
      window.alert("This battle does not have any resolved commanders to strike.");
      return;
    }
    const bubbleDropGroupOverride = scopeOptions?.battleKeys ? scopeOptions : scopeOptions?.bubbleDropGroup;
    const explicitAutoReviewBattleIds = Array.isArray(scopeOptions?.autoReviewBattleIds)
      ? scopeOptions.autoReviewBattleIds
      : null;
    const preferredCommanderId = String(scopeOptions?.preferredCommanderId || "").trim();
    const defaultCommander = preferredCommanderId
      ? commanderRefs.find((ref) => String(ref.commanderId || "") === preferredCommanderId)
      : null;
    const matchingRules = getConfiguredStrikeMatches(
      battle,
      strikeSettings,
      {
        attackerNationRole: getBattleAttackerNationRole(battle),
        defenderNationRole: getBattleDefenderNationRole(battle),
        bubbleDropGroups: getBubbleDropGroupsForBattle(battle),
      },
    );
    const defaultRule = matchingRules[0] || null;
    const autoReviewBattleIds = explicitAutoReviewBattleIds || getBubbleDropScopeBattleIds(battle, bubbleDropGroupOverride);
    const hasRelatedBattles = autoReviewBattleIds.length > 1;
    const strikeBattleTime = formatBattleLogTime(battle.at || battle.battleTime);
    const isBubbleDropRule = String(defaultRule?.ruleType || "") === "bubble_drop" || String(defaultRule?.code || "") === String(bubbleDropGroupOverride?.ruleCode || "");
    const bubbleSummary = isBubbleDropRule
      ? buildBubbleDropStrikeSummary(autoReviewBattleIds, bubbleDropGroupOverride || getPrimaryBubbleDropGroup(battle))
      : { summaryLabel: "", summaryDetail: "", reasonNote: "" };
    const evidenceRefs = buildStrikeEvidenceRefs(autoReviewBattleIds.length ? autoReviewBattleIds : [battle._id]);
    setStrikeBattleId(String(battle?._id || ""));
    setStrikeForm({
      commanderId: (defaultCommander || commanderRefs[0]).commanderId,
      reasonCode: defaultRule?.code || "",
      warningLevelDelta: String(defaultRule?.warningLevelDelta || 1),
      reasonNote: String(bubbleSummary.reasonNote || defaultRule?.reasonNoteTemplate || battle?.strikeReason || battle?.riskReason || "").trim(),
      summaryLabel: bubbleSummary.summaryLabel || defaultRule?.label || "",
      summaryDetail: bubbleSummary.summaryDetail || "",
      evidenceRefs,
      autoReviewBattleIds,
      autoReviewRelatedBattles: hasRelatedBattles,
      autoReviewRelatedReviewNote: hasRelatedBattles
        ? `Received strike from related battle ${strikeBattleTime}.`
        : "",
    });
  };

  const closeStrikeModal = () => {
    if (isApplyingStrike) return;
    setStrikeBattleId("");
    setExistingEventCommanderStrikes([]);
    setIsLoadingExistingEventStrikes(false);
    setStrikeForm({
      commanderId: "",
      reasonCode: "",
      warningLevelDelta: "1",
      reasonNote: "",
      summaryLabel: "",
      summaryDetail: "",
      evidenceRefs: [],
      autoReviewBattleIds: [],
      autoReviewRelatedBattles: false,
      autoReviewRelatedReviewNote: "",
    });
  };

  const handleApplyStrike = async () => {
    if (!selectedStrikeBattle?._id) return;
    const commanderId = String(strikeForm.commanderId || "").trim();
    if (!commanderId) {
      window.alert("Select a commander for the strike.");
      return;
    }
    const warningLevelDelta = Math.trunc(Number(strikeForm.warningLevelDelta || 1));
    if (!Number.isInteger(warningLevelDelta) || warningLevelDelta < 1 || warningLevelDelta > 10) {
      window.alert("Warning level increase must be between 1 and 10.");
      return;
    }
    const reasonNote = String(strikeForm.reasonNote || "").trim();
    if (!reasonNote) {
      window.alert("Add a reason note before applying a strike.");
      return;
    }
    if (existingEventCommanderStrikes.length > 0) {
      const confirmed = window.confirm(
        `${selectedStrikeCommanderLabel} already has ${existingEventCommanderStrikes.length === 1 ? "a strike" : `${existingEventCommanderStrikes.length} strikes`} applied from this event. Are you sure you want to apply another strike?`,
      );
      if (!confirmed) return;
    }

    setIsApplyingStrike(true);
    try {
      const response = await axiosInstance.post("/commander-strikes", {
        commanderId,
        warningLevelDelta,
        reasonModel: "EventBattle",
        reasonRef: selectedStrikeBattle._id,
        reasonNote,
        reasonCode: strikeForm.reasonCode,
        reasonLabel: (strikeSettings?.reasons || []).find((reason) => String(reason.code || "") === String(strikeForm.reasonCode || ""))?.label || "",
        summaryLabel: strikeForm.summaryLabel,
        summaryDetail: strikeForm.summaryDetail,
        evidenceRefs: strikeForm.evidenceRefs,
        autoReviewBattleIds: strikeForm.autoReviewRelatedBattles
          ? strikeForm.autoReviewBattleIds
          : [selectedStrikeBattle._id],
        autoReviewRelatedReviewNote: strikeForm.autoReviewRelatedReviewNote,
      });
      const updatedCommander = response?.data?.commander;
      setCommanders((prev) => prev.map((commander) => (
        String(commander?._id || "") === commanderId
          ? {
            ...commander,
            warningLevel: Number(updatedCommander?.warningLevel ?? commander.warningLevel ?? 0),
          }
          : commander
      )));
      const battleReviews = Array.isArray(response?.data?.battleReviews)
        ? response.data.battleReviews
        : [response?.data?.battleReview].filter(Boolean);
      battleReviews
        .filter((battleReview) => battleReview?.reviewed && battleReview?.battleId)
        .forEach((battleReview) => {
          upsertLocalBattle(battleReview.battleId, {
            review: {
              ...(((selectedEvent?.battles || []).find((battle) => String(battle?._id || "") === String(battleReview.battleId)) || {}).review || {}),
              ...battleReview,
            },
          });
        });
      setStrikeBattleId("");
      setExistingEventCommanderStrikes([]);
      setIsLoadingExistingEventStrikes(false);
      setStrikeForm({
        commanderId: "",
        reasonCode: "",
        warningLevelDelta: "1",
        reasonNote: "",
        summaryLabel: "",
        summaryDetail: "",
        evidenceRefs: [],
        autoReviewBattleIds: [],
        autoReviewRelatedBattles: false,
        autoReviewRelatedReviewNote: "",
      });
    } catch (error) {
      console.error("Error applying strike:", error);
      window.alert(error?.response?.data?.message || "Failed to apply strike.");
    } finally {
      setIsApplyingStrike(false);
    }
  };

  const openBattleReviewModal = (battle) => {
    if (!battle?._id) return;
    setReviewBattleId(String(battle._id));
    setReviewForm({
      reviewNote: String(battle?.review?.reviewNote || ""),
    });
  };

  const closeBattleReviewModal = () => {
    if (reviewingBattleId) return;
    setReviewBattleId("");
    setReviewForm({ reviewNote: "" });
  };

  const handleSaveBattleReview = async () => {
    if (!selectedEventId || !selectedReviewBattle?._id) return;
    const battleId = String(selectedReviewBattle._id);
    setReviewingBattleId(battleId);
    try {
      const response = await axiosInstance.patch(`/events/${selectedEventId}/battles/${battleId}/review`, {
        strikeCandidateOverride: false,
        reviewNote: reviewForm.reviewNote,
      });
      upsertLocalBattle(battleId, {
        review: {
          ...(selectedReviewBattle.review || {}),
          ...(response?.data?.review || {}),
        },
      });
      setReviewBattleId("");
      setReviewForm({ reviewNote: "" });
    } catch (error) {
      console.error("Error reviewing battle:", error);
      window.alert(error?.response?.data?.message || "Failed to save battle review.");
    } finally {
      setReviewingBattleId("");
    }
  };

  const handleDeleteBattle = async (battleId) => {
    if (!selectedEventId) return;
    if (!window.confirm("Delete this battle entry?")) return;
    const existingBattle = (selectedEvent?.battles || [])
      .find((battle) => String(battle?._id || "") === String(battleId));
    try {
      await axiosInstance.delete(
        `/events/${selectedEventId}/battles/${battleId}`,
      );
      removeLocalBattle(battleId);
      if (existingBattle?.result === "attacker_win" && existingBattle?.strongholdId) {
        applyLocalStrongholdPatch(existingBattle.strongholdId, { ownerAlliance: null });
      }
      if (String(reportBattleId || "") === String(battleId)) setReportBattleId("");
      if (String(strikeBattleId || "") === String(battleId)) closeStrikeModal();
      if (String(reviewBattleId || "") === String(battleId)) closeBattleReviewModal();
      if (String(editingBattleId || "") === String(battleId)) {
        setEditingBattleId("");
        setIsBattleEntryModalOpen(false);
      }
    } catch (error) {
      console.error("Error deleting battle:", error);
      window.alert(
        typeof error === "string" ? error : "Failed to delete battle.",
      );
    }
  };

  const handleCopyBattleReportRequest = async (battle) => {
    const battleId = String(battle?._id || "");
    const text = buildBattleReportRequestText(battle);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopiedReportRequestBattleId(battleId);
      window.setTimeout(() => {
        setCopiedReportRequestBattleId((current) => (current === battleId ? "" : current));
      }, 1800);
    } catch (error) {
      console.error("Error copying report request:", error);
      window.alert("Failed to copy report request.");
    }
  };

  const resetResultForm = useCallback(() => {
    const defaultCommanderId = String(eventCommanders?.[0]?._id || "");
    const defaultCommanderAllianceId = String(
      eventCommanders?.[0]?.alliance?._id || eventCommanders?.[0]?.alliance || "",
    );
    setEditingResultId("");
    setResultForm({
      commanderId: defaultCommanderId,
      allianceId: defaultCommanderAllianceId,
      score: "",
      source: "manual",
      notes: "",
    });
  }, [eventCommanders]);

  const openResultEntryModal = useCallback(() => {
    resetResultForm();
    setResultEntryMode("manual");
    setIsResultEntryModalOpen(true);
  }, [resetResultForm]);

  const closeResultEntryModal = useCallback(() => {
    setIsResultEntryModalOpen(false);
    resetResultForm();
  }, [resetResultForm]);

  useEffect(() => {
    resetResultForm();
    setResultEntryMode("manual");
    setUploadAllianceId("");
    setUploadFiles([]);
    setUploadReviewRows([]);
    setUploadCommanderOptions([]);
    setExpandedRowPreview(null);
    setSelectedTemplateId("");
    setTemplateUploadFiles([]);
  }, [selectedEventId, resetResultForm]);

  useEffect(() => {
    if (isSuperUser) return;
    if (resultEntryMode !== "manual") setResultEntryMode("manual");
  }, [isSuperUser, resultEntryMode]);

  useEffect(() => {
    if (activeSection === "analytics" && canAccessAnalytics) return;
    if (isSuperUser) return;
    if (activeSection === "analytics" || activeSection === "templates" || activeSection === "orphans") {
      setActiveSection("events");
    }
  }, [isSuperUser, canAccessAnalytics, activeSection]);

  useEffect(() => {
    if (!expandedRowPreview) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setExpandedRowPreview(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [expandedRowPreview]);

  const startUploadLauncherSession = () => {
    if (!uploadLaunchConfig.eventId) {
      // No matching event yet: route user into create flow with upload intent.
      const nextEventType = uploadLaunchConfig.eventType || eventTypeMeta?.defaultEventType || eventType;
      setEventType(nextEventType);
      setEventDate(uploadLaunchConfig.eventDate || "");
      setEventTime24(isFixedFullDayEventType(nextEventType) ? "" : (uploadLaunchConfig.eventTime24 || ""));
      setCreateAndOpenUpload(true);
      setIsUploadLauncherOpen(false);
      setIsCreatePanelOpen(true);
      return;
    }

    openEventDetail(uploadLaunchConfig.eventId);
    setActiveTab("results");
    setResultsPanelOpen((prev) => ({ ...prev, entry: true }));
    setResultEntryMode("upload");
    setIsUploadLauncherOpen(false);

    setPendingTemplateAutoSelect({
      eventId: uploadLaunchConfig.eventId,
      eventType: uploadLaunchConfig.eventType,
      visibleRows: uploadLaunchConfig.visibleRows,
      resultsScope: uploadLaunchConfig.resultsScope,
    });
  };

  const handleResultCommanderChange = (commanderId) => {
    const commander = commanders.find(
      (row) => String(row._id) === String(commanderId),
    );
    const commanderAllianceId = String(commander?.alliance?._id || commander?.alliance || "");
    const allianceIsInEvent = eventAlliances.some((a) => String(a._id) === commanderAllianceId);

    setResultForm((prev) => ({
      ...prev,
      commanderId,
      allianceId: allianceIsInEvent ? commanderAllianceId : (prev.allianceId || String(eventAlliances?.[0]?._id || "")),
    }));
  };

  const handleResultSubmit = async () => {
    if (!selectedEventId) return;
    if (!resultForm.commanderId) {
      window.alert("Pick a commander first.");
      return;
    }

    const payload = {
      commanderId: resultForm.commanderId,
      allianceId: resultForm.allianceId || null,
      score: parseScoreValue(resultForm.score),
      source: resultForm.source || "manual",
      notes: resultForm.notes || "",
    };

    try {
      if (editingResultId) {
        await axiosInstance.patch(
          `/events/${selectedEventId}/participant-results/${editingResultId}`,
          {
            score: payload.score,
            source: payload.source,
            notes: payload.notes,
          },
        );
      } else {
        await axiosInstance.post(
          `/events/${selectedEventId}/participant-results`,
          payload,
        );
      }
      await loadEventDetail(selectedEventId);
      resetResultForm();
      setIsResultEntryModalOpen(false);
    } catch (error) {
      console.error("Error saving participant result:", error);
      window.alert(
        typeof error === "string" ? error : "Failed to save participant result.",
      );
    }
  };

  const formatScoreForDisplay = (value) => {
    const n = Number(String(value ?? "").replace(/[^0-9]/g, ""));
    if (!Number.isFinite(n) || n <= 0) return "";
    return n.toLocaleString();
  };

  const parseScoreInput = (value) => {
    const digits = String(value || "").replace(/[^0-9]/g, "");
    return digits ? Number(digits) : 0;
  };

  const readFilesAsDataUrls = async (files) => {
    const list = Array.from(files || []);
    const toDataUrl = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

    return Promise.all(list.map((file) => toDataUrl(file)));
  };

  const estimateDataUrlBytes = (dataUrl) => {
    const base64 = String(dataUrl || "").split(",")[1] || "";
    return Math.floor((base64.length * 3) / 4);
  };

  const optimizeImageDataUrl = async (dataUrl, options = {}) => {
    const maxWidth = Number(options.maxWidth || 2200);
    const maxHeight = Number(options.maxHeight || 2200);
    const quality = Number(options.quality || 0.82);

    const img = await loadImageFromDataUrl(dataUrl);
    const width = Number(img.naturalWidth || img.width || 0);
    const height = Number(img.naturalHeight || img.height || 0);
    if (!width || !height) return dataUrl;

    const scale = Math.min(1, maxWidth / width, maxHeight / height);
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return dataUrl;

    ctx.drawImage(img, 0, 0, targetW, targetH);
    return canvas.toDataURL("image/jpeg", Math.max(0.45, Math.min(0.95, quality)));
  };

  const readFilesAsOptimizedDataUrls = async (files, options = {}) => {
    const raw = await readFilesAsDataUrls(files);
    const optimized = await Promise.all(
      raw.map(async (dataUrl) => {
        try {
          return await optimizeImageDataUrl(dataUrl, options);
        } catch (_err) {
          return dataUrl;
        }
      }),
    );
    return optimized;
  };

  const loadImageFromDataUrl = (dataUrl) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = dataUrl;
    });

  const buildRowCropPreview = async ({ screenshotDataUrl, rowIndex, layout }) => {
    if (!screenshotDataUrl) return "";

    try {
      const img = await loadImageFromDataUrl(screenshotDataUrl);
      const width = Number(img.naturalWidth || img.width || 0);
      const height = Number(img.naturalHeight || img.height || 0);
      if (!width || !height) return screenshotDataUrl;

      if (layout && Number.isInteger(Number(rowIndex))) {
        const rowArea = layout?.rowArea;
        const rowsPerScreenshot = Math.max(1, Number(layout?.rowsPerScreenshot || 1));
        const rowGapRatio = Math.max(0, Number(layout?.rowGapRatio || 0));

        if (rowArea && Number(rowArea.w) > 0 && Number(rowArea.h) > 0) {
          const rowHeightRaw = rowArea.h / rowsPerScreenshot;
          const rowHeightNorm = Math.max(0.001, rowHeightRaw - rowHeightRaw * rowGapRatio);

          const xNorm = Math.max(0, Number(rowArea.x || 0));
          const yNorm = Math.max(0, Number(rowArea.y || 0) + Number(rowIndex || 0) * rowHeightRaw);
          const wNorm = Math.max(0.001, Number(rowArea.w || 0));
          const hNorm = Math.max(0.001, rowHeightNorm);

          const sx = Math.max(0, Math.floor(xNorm * width));
          const sy = Math.max(0, Math.floor(yNorm * height));
          const sw = Math.max(1, Math.min(width - sx, Math.floor(wNorm * width)));
          const sh = Math.max(1, Math.min(height - sy, Math.floor(hNorm * height)));

          const canvas = document.createElement("canvas");
          canvas.width = sw;
          canvas.height = sh;
          const ctx = canvas.getContext("2d");
          if (!ctx) return screenshotDataUrl;

          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
          return canvas.toDataURL("image/jpeg", 0.85);
        }
      }

      // Fallback: compact full-screenshot preview
      const targetW = 220;
      const targetH = Math.max(1, Math.floor((height / width) * targetW));
      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext("2d");
      if (!ctx) return screenshotDataUrl;
      ctx.drawImage(img, 0, 0, targetW, targetH);
      return canvas.toDataURL("image/jpeg", 0.8);
    } catch (_err) {
      return screenshotDataUrl;
    }
  };

  const handleTemplateUploadFilesChange = useCallback(async (files) => {
    const next = Array.from(files || []).slice(-1);
    setTemplateUploadFiles(next);

    if (!next.length) {
      setTemplateDraft((prev) => ({
        ...prev,
        sourceImage: {
          dataUrl: "",
          fileName: "",
          mimeType: "",
          width: null,
          height: null,
        },
        annotations: [],
      }));
      return;
    }

    const [dataUrl] = await readFilesAsDataUrls(next);
    const file = next[0];

    const imgMeta = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve({ width: null, height: null });
      img.src = dataUrl;
    });

    setTemplateDraft((prev) => ({
      ...prev,
      sourceImage: {
        dataUrl,
        fileName: file?.name || "",
        mimeType: file?.type || "",
        width: imgMeta.width,
        height: imgMeta.height,
      },
      annotations: [],
    }));
    setIsTemplateImageUploadOpen(false);
  }, []);

  const handleProcessUploadScreenshots = async () => {
    if (!isSuperUser) return false;
    if (!selectedEventId) return false;
    if (!uploadAllianceId) {
      window.alert("Select one of the participating alliances first.");
      return false;
    }
    if (!uploadFiles.length) {
      window.alert("Add one or more screenshots first.");
      return false;
    }

    setIsProcessingUploads(true);
    try {
      let screenshots = await readFilesAsOptimizedDataUrls(uploadFiles, {
        maxWidth: 2200,
        maxHeight: 2200,
        quality: 0.82,
      });

      let totalBytes = screenshots.reduce((sum, item) => sum + estimateDataUrlBytes(item), 0);
      const TARGET_MAX_BYTES = 18 * 1024 * 1024;

      if (totalBytes > TARGET_MAX_BYTES) {
        screenshots = await Promise.all(
          screenshots.map((dataUrl) =>
            optimizeImageDataUrl(dataUrl, { maxWidth: 1800, maxHeight: 1800, quality: 0.68 }),
          ),
        );
        totalBytes = screenshots.reduce((sum, item) => sum + estimateDataUrlBytes(item), 0);
      }

      if (totalBytes > TARGET_MAX_BYTES) {
        window.alert("Screenshots are still too large to upload (likely API gateway limit). Try fewer images at once or crop before uploading.");
        return false;
      }

      const response = await axiosInstance.post(
        `/events/${selectedEventId}/participant-results/extract`,
        {
          allianceId: uploadAllianceId,
          templateId: selectedTemplateId || null,
          screenshots,
        },
      );

      setUploadCommanderOptions(Array.isArray(response?.data?.commanders) ? response.data.commanders : []);

      const activeTemplate = selectedTemplateId
        ? ocrTemplates.find((t) => String(t._id) === String(selectedTemplateId))
        : null;
      const templateLayout = activeTemplate?.layout || null;

      const extractedRows = response?.data?.extractedRows || [];
      const rows = await Promise.all(
        extractedRows.map(async (row, idx) => ({
          id: `${row.screenshotIndex}-${idx}-${row.rawName}`,
          screenshotIndex: row.screenshotIndex,
          rawName: row.rawName,
          score: row.score || 0,
          commanderId: row.proposedCommanderId || "",
          commanderLookup: row.proposedCommanderName || row.rawName || "",
          confidence: row.confidence || 0,
          rowIndex: row.rowIndex,
          previewDataUrl: await buildRowCropPreview({
            screenshotDataUrl: screenshots[Number(row.screenshotIndex || 0)] || "",
            rowIndex: Number.isFinite(Number(row.rowIndex)) ? Number(row.rowIndex) : null,
            layout: templateLayout,
          }),
        })),
      );
      setUploadReviewRows(rows);
      if (!rows.length) {
        window.alert("No score rows detected. Try clearer screenshots or manual entry.");
      }
      return true;
    } catch (error) {
      console.error("Error processing uploaded screenshots:", error);
      setUploadCommanderOptions([]);
      setExpandedRowPreview(null);
      window.alert(
        typeof error === "string" ? error : "Failed to process screenshots.",
      );
      return false;
    } finally {
      setIsProcessingUploads(false);
    }
  };

  const handleApplyUploadResults = async () => {
    if (!isSuperUser) return;
    if (!selectedEventId) return;
    const rows = uploadReviewRows
      .filter((row) => row.commanderId && Number(row.score || 0) > 0)
      .map((row) => ({
        commanderId: row.commanderId,
        allianceId: uploadAllianceId,
        score: Number(row.score || 0),
        source: "screenshot",
        notes: `Uploaded screenshot #${Number(row.screenshotIndex || 0) + 1}`,
      }));

    if (!rows.length) {
      window.alert("No valid mapped rows to apply yet.");
      return;
    }

    setIsApplyingUploadRows(true);
    try {
      await axiosInstance.post(
        `/events/${selectedEventId}/participant-results/bulk`,
        {
          allianceId: uploadAllianceId,
          rows,
        },
      );
      await loadEventDetail(selectedEventId);
      setUploadReviewRows([]);
      setUploadFiles([]);
      setExpandedRowPreview(null);
      window.alert(`Applied ${rows.length} results.`);
    } catch (error) {
      console.error("Error applying upload-mapped rows:", error);
      window.alert(
        typeof error === "string" ? error : "Failed to apply uploaded results.",
      );
    } finally {
      setIsApplyingUploadRows(false);
    }
  };

  const openTemplateAdmin = () => {
    if (!isSuperUser) return;
    const baseEventType =
      selectedEvent?.eventType ||
      templateAdminEventType ||
      eventTypeMeta?.defaultEventType ||
      eventTypeMeta?.eventTypes?.[0]?.id ||
      "";

    const current =
      ocrTemplates.find((t) => String(t._id) === String(selectedTemplateId)) ||
      ocrTemplates[0];

    if (current) {
      setTemplateDraft({
        _id: String(current._id || ""),
        name: current.name || "",
        eventType: current.eventType || baseEventType || "",
        enabled: current.enabled !== false,
        description: current.description || "",
        version: Number(current.version || 1),
        rowsPerScreenshot: Number(current.layout?.rowsPerScreenshot || 10),
        rowGapRatio: Number(current.layout?.rowGapRatio || 0),
        selectionProfile: normalizeSelectionProfileDraft(current.selectionProfile || {}),
        sourceImage: current.sourceImage || {
          dataUrl: current.referenceImageDataUrl || "",
          fileName: "",
          mimeType: "",
          width: null,
          height: null,
        },
        annotations:
          Array.isArray(current.annotations) && current.annotations.length
            ? current.annotations.map((row, idx) => ({
                id: row.id || `ann-${idx}`,
                label: row.label,
                box: row.box,
              }))
            : layoutToAnnotations(current.layout),
      });
      setTemplateUploadFiles([]);
    } else {
      setTemplateDraft({
        _id: "",
        name: "",
        eventType: baseEventType || "",
        enabled: true,
        description: "",
        version: 1,
        rowsPerScreenshot: 10,
        rowGapRatio: 0.02,
        selectionProfile: normalizeSelectionProfileDraft({ resultsScope: "commander", layoutFamily: "leaderboard" }),
        sourceImage: {
          dataUrl: "",
          fileName: "",
          mimeType: "",
          width: null,
          height: null,
        },
        annotations: [],
      });
      setTemplateUploadFiles([]);
    }

    setIsTemplateAdminOpen(true);
  };

  const saveTemplateDraft = async () => {
    if (!isSuperUser) return;
    if (!templateDraft.name) {
      window.alert("Template name is required.");
      return;
    }

    const layout = annotationsToLayout(
      templateDraft.annotations,
      templateDraft.rowsPerScreenshot,
      templateDraft.rowGapRatio,
    );

    if (!layout) {
      window.alert("Please label at least Row area, Commander name, and Points.");
      return;
    }

    const payload = {
      name: templateDraft.name,
      eventType:
        templateDraft.eventType ||
        selectedEvent?.eventType ||
        templateAdminEventType ||
        eventTypeMeta?.defaultEventType ||
        "",
      enabled: templateDraft.enabled,
      description: templateDraft.description || "",
      version: Number(templateDraft.version || 1),
      referenceImageDataUrl: templateDraft.sourceImage?.dataUrl || "",
      sourceImage: templateDraft.sourceImage || null,
      annotations: templateDraft.annotations || [],
      selectionProfile: buildSelectionProfilePayload(templateDraft.selectionProfile || {}),
      layout,
      annotationSchemaVersion: 1,
    };

    setIsSavingTemplate(true);
    try {
      if (templateDraft._id) {
        await axiosInstance.patch(
          `/events/admin/ocr-templates/${templateDraft._id}`,
          payload,
        );
      } else {
        await axiosInstance.post(`/events/admin/ocr-templates`, payload);
      }
      await loadOcrTemplates(payload.eventType);
      setIsTemplateAdminOpen(false);
    } catch (error) {
      console.error("Error saving OCR template:", error);
      window.alert(
        typeof error === "string" ? error : "Failed to save OCR template.",
      );
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const deleteTemplateDraft = async () => {
    if (!templateDraft._id) return;
    if (!window.confirm("Delete this OCR template?")) return;
    try {
      await axiosInstance.delete(`/events/admin/ocr-templates/${templateDraft._id}`);
      await loadOcrTemplates(
        selectedEvent?.eventType || templateAdminEventType || "",
      );
      setIsTemplateAdminOpen(false);
    } catch (error) {
      console.error("Error deleting OCR template:", error);
      window.alert(
        typeof error === "string" ? error : "Failed to delete OCR template.",
      );
    }
  };

  const startEditResult = (row) => {
    setEditingResultId(String(row?._id || ""));
    setResultForm({
      commanderId: String(row?.commander || ""),
      allianceId: String(row?.alliance || ""),
      score: formatScoreDisplay(row?.score ?? ""),
      source: row?.source || "manual",
      notes: row?.notes || "",
    });
    setResultsPanelOpen((prev) => ({ ...prev, entry: true, individual: true }));
    setActiveTab("results");
    setResultEntryMode("manual");
    setIsResultEntryModalOpen(true);
  };

  const toggleResultsPanel = (key) => {
    setResultsPanelOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDeleteResult = async (resultId) => {
    if (!selectedEventId || !resultId) return;
    if (!window.confirm("Delete this participant result?")) return;
    try {
      await axiosInstance.delete(
        `/events/${selectedEventId}/participant-results/${resultId}`,
      );
      await loadEventDetail(selectedEventId);
    } catch (error) {
      console.error("Error deleting participant result:", error);
      window.alert(
        typeof error === "string" ? error : "Failed to delete participant result.",
      );
    }
  };

  const uploadCommanderCandidates = useMemo(() => {
    const source = uploadCommanderOptions.length ? uploadCommanderOptions : commanders;
    return source
      .slice()
      .sort((a, b) => {
        const aAlliance = String(a?.alliance?._id || a?.alliance || "");
        const bAlliance = String(b?.alliance?._id || b?.alliance || "");
        const aPreferred = aAlliance === String(uploadAllianceId) ? 1 : 0;
        const bPreferred = bAlliance === String(uploadAllianceId) ? 1 : 0;
        if (aPreferred !== bPreferred) return bPreferred - aPreferred;
        return String(a?.name || "").localeCompare(String(b?.name || ""));
      })
      .map((c) => {
        const normalized = normalizeCommanderOption(c, allianceNameById);
        if (!normalized) return null;
        return {
          commanderId: normalized.value,
          commanderName: normalized.commanderName,
          label: normalized.label,
        };
      })
      .filter(Boolean);
  }, [uploadCommanderOptions, commanders, uploadAllianceId, allianceNameById]);

  const sectionTabs = [
    { value: "events", label: "Events" },
    ...(canAccessAnalytics ? [{ value: "analytics", label: "Analytics" }] : []),
    ...(isSuperUser ? [{ value: "templates", label: "Templates" }] : []),
    ...(isSuperUser ? [{ value: "orphans", label: "Orphan Admin" }] : []),
  ];

  const openEventDetail = useCallback((eventId) => {
    const id = String(eventId || "").trim();
    if (!id) return;
    navigate(`/events/${id}`);
  }, [navigate]);

  const openEventDateEditor = useCallback((eventItem) => {
    const id = String(eventItem?._id || "").trim();
    if (!id) return;
    const fixedFullDay = isFixedFullDayEventType(eventItem?.eventType);
    setEventDateEditor({
      eventId: id,
      eventType: String(eventItem?.eventType || ""),
      label: formatEventDisplayLabel(eventItem, eventTypeConfigById),
      eventDate: toDateInputValue(eventItem?.startsAt),
      startTime24: fixedFullDay
        ? ""
        : (normalizeTime24Input(eventItem?.startTime24) || deriveTime24FromStartsAt(eventItem?.startsAt)),
    });
  }, [eventTypeConfigById]);

  const closeEventDateEditor = useCallback(() => {
    if (isSavingEventMeta) return;
    setEventDateEditor(null);
  }, [isSavingEventMeta]);

  const returnToEventIndex = useCallback(() => {
    navigate("/events");
  }, [navigate]);

  const getEventIndexMeta = useCallback((eventItem) => {
    const battles = Array.isArray(eventItem?.battles) ? eventItem.battles : [];
    const participantResults = Array.isArray(eventItem?.participantResults) ? eventItem.participantResults : [];
    const officialResults = participantResults.filter((row) => isOfficialScoreboardRow(row));
    const battleCount = Number(eventItem?.battleCount ?? eventItem?.battlesCount ?? battles.length ?? 0);
    const scoreboardCount = Number(eventItem?.scoreboardCount ?? officialResults.length ?? 0);
    const allianceText = getEventAllianceAcronymText(eventItem);
    const sourceNationText = getEventSourceNationText(eventItem);
    const scoreByNation = new Map();
    const addNationScore = (nationLabel, points) => {
      const label = String(nationLabel || "").trim();
      const numericPoints = Number(points || 0);
      if (!label || !Number.isFinite(numericPoints) || numericPoints <= 0) return;
      scoreByNation.set(label, Number(scoreByNation.get(label) || 0) + numericPoints);
    };

    officialResults.forEach((row) => {
      const dimension = String(row?.sourceContext?.dimension || row?.sourceContext?.scoreboardDimension || "").toLowerCase();
      const sourceKey = String(row?.sourceKey || "").toLowerCase();
      const looksLikeNationRow = dimension === "nation"
        || sourceKey.includes("nation")
        || (!row?.commander && !row?.alliance);
      if (!looksLikeNationRow) return;
      const nationId = String(row?.nation?._id || row?.nation || row?.subjectNation?._id || row?.subjectNation || "");
      const nationLabel = nationNameById[nationId]
        || getScoreboardNationFallback(row)
        || (row?.nation?.number != null ? formatNationNumberLabel(row.nation.number) : "")
        || nationId;
      addNationScore(nationLabel, getScoreboardRowPoints(row));
    });

    if (!scoreByNation.size) {
      participantResults.forEach((row) => {
        if (isOfficialScoreboardRow(row)) return;
        const nationId = String(row?.subjectNation?._id || row?.subjectNation || row?.nation?._id || row?.nation || "");
        const nationLabel = nationNameById[nationId] || nationId;
        addNationScore(nationLabel, row?.score);
      });
    }

    const nationScores = Array.from(scoreByNation.entries())
      .map(([label, points]) => ({ label, points }))
      .sort((a, b) => {
        const leftHome = String(a.label) === String(sourceNationText);
        const rightHome = String(b.label) === String(sourceNationText);
        if (leftHome !== rightHome) return leftHome ? -1 : 1;
        return Number(b.points || 0) - Number(a.points || 0);
      })
      .slice(0, 2);

    return {
      battleCount,
      scoreboardCount,
      allianceText,
      sourceNationText,
      nationScores,
    };
  }, [getEventAllianceAcronymText, getEventSourceNationText, nationNameById]);

  const hasEventIndexFilters = eventAllianceFilterId !== "all"
    || eventTypeFilter !== "all"
    || eventSourceNationFilterId !== "all";

  const clearEventIndexFilters = useCallback(() => {
    setEventAllianceFilterId("all");
    setEventTypeFilter("all");
    setEventSourceNationFilterId("all");
  }, []);

  const handleSectionChange = (next) => {
    if (isEventDetailRoute) returnToEventIndex();
    if (next === "events") {
      setActiveSection("events");
      if (activeTab === "analytics") setActiveTab(workspaceEventTabs[0]?.id || "results");
      return;
    }
    if (next === "analytics") {
      setActiveSection("analytics");
      setActiveTab("analytics");
      return;
    }
    if (next === "templates") {
      setActiveSection("templates");
      return;
    }
    if (next === "orphans") {
      setActiveSection("orphans");
    }
  };

  if (isAuthInitializing) {
    return (
      <PageShell className="events-shell" maxWidth={false} containerSx={{ px: { xs: 1, md: 2 }, maxWidth: "none" }}>
        <div className="events-page">
          <div className="events-auth-state">
            <h2>Checking your session</h2>
            <p>Events will load after your permissions are confirmed.</p>
          </div>
        </div>
      </PageShell>
    );
  }

  if (!canReadEvents) {
    return (
      <PageShell className="events-shell" maxWidth={false} containerSx={{ px: { xs: 1, md: 2 }, maxWidth: "none" }}>
        <div className="events-page">
          <div className="events-auth-state">
            <h2>{authInitializationError ? "Session check failed" : "Not authorized"}</h2>
            <p>
              {authInitializationError || "You are not authorized to access events."}
            </p>
            {authInitializationError && (
              <button type="button" onClick={retryAuthBootstrap}>
                Retry session check
              </button>
            )}
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell className="events-shell" maxWidth={false} containerSx={{ px: { xs: 1, md: 2 }, maxWidth: "none" }}>
      <div className="events-page">
      {!isEventDetailRoute && (
      <div className="events-submenu-row">
        <AdminTabs
          tabs={sectionTabs}
          value={activeSection}
          onChange={handleSectionChange}
          className="events-submenu events-submenu-top"
        />

        {canWriteEvents && (
          <div className="events-submenu-actions">
            <AppButton
              kind={isCreatePanelOpen ? "primary" : "secondary"}
              className="events-header-action"
              onClick={() => setIsCreatePanelOpen((prev) => !prev)}
            >
              {isCreatePanelOpen ? "Close Create Event" : "Create Event"}
            </AppButton>
          </div>
        )}
      </div>
      )}

      {canWriteEvents && isCreatePanelOpen && (
        <div
          className="events-create-modal-backdrop"
          onClick={() => setIsCreatePanelOpen(false)}
        >
          <div
            className="events-card events-create-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="events-create-modal-header">
              <h3>Create Event</h3>
              <button
                className="events-create-modal-close"
                onClick={() => setIsCreatePanelOpen(false)}
                aria-label="Close create event panel"
              >
                âœ•
              </button>
            </div>
            <div className="events-grid">
            <label>
              Event Type
              <select
                value={eventType}
                onChange={(e) => {
                  const nextType = e.target.value;
                  setEventType(nextType);
                  if (isFixedFullDayEventType(nextType)) setEventTime24("");
                }}
              >
                {(eventTypeMeta?.eventTypes || []).map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Event Date
              <input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
              />
            </label>
            {!activeCreateIsFixedFullDay && (
              <label>
                Event Time (24h, optional)
                <input
                  type="time"
                  step="60"
                  value={eventTime24}
                  onChange={(e) => setEventTime24(e.target.value)}
                />
              </label>
            )}
            {eventType === "triangleWar" && (
              <label>
                Max Defenders
                <input
                  type="number"
                  min="1"
                  max="200"
                  value={maxDefenders}
                  onChange={(e) => setMaxDefenders(e.target.value)}
                />
              </label>
            )}
          </div>

          {Number(activeCreateTypeConfig?.requiredNationCount || 0) > 0 && (
            <div className="events-alliance-section">
              <div className="events-alliance-section-header events-section-header-with-action">
              <span>Nation Selection</span>
              {canWriteNations && (
                <button
                  className="events-secondary-btn"
                  onClick={() => handleQuickCreateNation()}
                >
                  + Nation
                </button>
              )}
            </div>
              <div className="events-alliance-grid">
                {[...Array(Number(activeCreateTypeConfig?.requiredNationCount || 0)).keys()].map(
                  (idx) => (
                    <div className="events-alliance-card" key={`nation-${idx}`}>
                      <h4>{idx === 0 ? "My Nation" : `Nation ${idx + 1}`}</h4>
                      <label className="events-field-inline">
                        <span>Nation</span>
                        <select
                          value={selectedParticipantNationIds[idx] || ""}
                          onChange={(e) =>
                            setSelectedParticipantNationIds((prev) => {
                              const next = [...prev];
                              next[idx] = e.target.value;
                              return next;
                            })
                          }
                          disabled={idx === 0}
                        >
                          <option value="">Select nation</option>
                          {nationOptions.map((n) => (
                            <option key={n._id} value={n._id}>
                              N{n.number}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}

          {Number(activeCreateTypeConfig?.requiredAllianceCount || 0) > 0 && (
            <div className="events-alliance-section">
              <div className="events-alliance-section-header">Alliance Selection</div>
              <div className="events-alliance-grid">
                {[...Array(Number(activeCreateTypeConfig?.requiredAllianceCount || 0)).keys()].map(
                  (idx) => {
                    const availableAlliances = getAvailableCreateAlliances(idx);
                    return (
                      <div className="events-alliance-card" key={idx}>
                        <h4>{idx === 0 ? "My Alliance" : `Alliance ${idx + 1}`}</h4>

                        {canWriteAlliances && (
                          <div className="events-inline-actions">
                            <button
                              className="events-secondary-btn"
                              onClick={() => handleQuickCreateAlliance(idx)}
                            >
                              + Alliance (or Nation + Alliance)
                            </button>
                          </div>
                        )}

                        <div className="events-search-select-field">
                          <SingleSelectDropdown
                            label="Alliance"
                            options={availableAlliances}
                            value={selectedAllianceIds[idx] || ""}
                            onChange={(allianceId) => updateSelectedAlliance(idx, allianceId)}
                            getOptionLabel={(option) =>
                              `${option?.nation?.number || "?"} Â· ${option?.acronym || option?.name || ""}`
                            }
                            getOptionValue={(option) => option._id}
                            helperText="Search and select one alliance"
                            placeholder="Search alliances"
                          />
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          )}

          <div className="events-create-actions">
            <button
              className="events-primary-btn"
              onClick={() => {
                setCreateAndOpenUpload(false);
                handleCreate({ openUploadAfterCreate: false, eventTime24 });
              }}
              disabled={isCreating}
            >
              {isCreating ? "Creating..." : "Create Event"}
            </button>
            {isSuperUser && (
              <button
                className="events-primary-btn"
                onClick={() => {
                  setCreateAndOpenUpload(true);
                  handleCreate({
                    openUploadAfterCreate: true,
                    eventDate: uploadLaunchConfig.eventDate || eventDate,
                    eventTime24: uploadLaunchConfig.eventTime24 || eventTime24,
                  });
                }}
                disabled={isCreating}
              >
                {isCreating ? "Creating..." : "Create & Upload Results"}
              </button>
            )}
          </div>
        </div>
        </div>
      )}

      {canWriteEvents && eventDateEditor && (
        <div
          className="events-create-modal-backdrop"
          onClick={closeEventDateEditor}
        >
          <div
            className="events-card events-create-modal events-date-edit-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="events-create-modal-header">
              <h3>Edit Event Date</h3>
              <button
                className="events-create-modal-close"
                onClick={closeEventDateEditor}
                aria-label="Close event date editor"
                disabled={isSavingEventMeta}
              >
                x
              </button>
            </div>
            <div className="events-grid">
              <label>
                Event
                <input value={eventDateEditor.label || "Event"} readOnly />
              </label>
              <label>
                Date
                <input
                  type="date"
                  value={eventDateEditor.eventDate || ""}
                  onChange={(e) =>
                    setEventDateEditor((prev) => ({
                      ...(prev || {}),
                      eventDate: e.target.value,
                    }))
                  }
                />
              </label>
              {!isFixedFullDayEventType(eventDateEditor.eventType) && (
                <label>
                  Time (24h, optional)
                  <input
                    type="time"
                    step="60"
                    value={eventDateEditor.startTime24 || ""}
                    onChange={(e) =>
                      setEventDateEditor((prev) => ({
                        ...(prev || {}),
                        startTime24: e.target.value,
                      }))
                    }
                  />
                </label>
              )}
            </div>
            <div className="events-create-actions">
              <AppButton
                type="button"
                kind="secondary"
                onClick={closeEventDateEditor}
                disabled={isSavingEventMeta}
              >
                Cancel
              </AppButton>
              <AppButton
                type="button"
                onClick={handleSaveEventDateEditor}
                disabled={isSavingEventMeta}
              >
                {isSavingEventMeta ? "Saving..." : "Save Date"}
              </AppButton>
            </div>
          </div>
        </div>
      )}

      {isSuperUser && isUploadLauncherOpen && (
        <div
          className="events-create-modal-backdrop"
          onClick={() => setIsUploadLauncherOpen(false)}
        >
          <div
            className="events-card events-create-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="events-create-modal-header">
              <h3>Upload Event Results</h3>
              <button
                className="events-create-modal-close"
                onClick={() => setIsUploadLauncherOpen(false)}
                aria-label="Close upload launcher"
              >
                âœ•
              </button>
            </div>

            <div className="events-grid">
              <label>
                Event Type
                <select
                  value={uploadLaunchConfig.eventType}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setUploadLaunchConfig((prev) => {
                      const allianceFilter = String(prev.allianceId || "all");
                      const candidates = (events || []).filter((x) => {
                        const typeMatch = String(x?.eventType || "triangleWar") === String(nextType);
                        if (!typeMatch) return false;
                        if (allianceFilter === "all") return true;
                        const eventAllianceIds = new Set(
                          (x?.alliances || [])
                            .map((allianceRef) => String(allianceRef?._id || allianceRef || ""))
                            .filter(Boolean),
                        );
                        return eventAllianceIds.has(allianceFilter);
                      });

                      return {
                        ...prev,
                        eventType: nextType,
                        eventId: String(candidates[0]?._id || ""),
                        eventTime24: isFixedFullDayEventType(nextType) ? "" : prev.eventTime24,
                      };
                    });
                  }}
                >
                  {(eventTypeMeta?.eventTypes || []).map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </label>

              <div className="events-search-select-field">
                <SingleSelectDropdown
                  label="Alliance Filter"
                  options={eventAllianceFilterSelectOptions}
                  value={uploadLaunchConfig.allianceId || "all"}
                  onChange={(nextAllianceId) => {
                    const resolvedAllianceId = nextAllianceId || "all";
                    setUploadLaunchConfig((prev) => {
                      const currentType = String(prev.eventType || "");
                      const candidates = (events || []).filter((eventRow) => {
                        const typeMatch =
                          !currentType ||
                          String(eventRow?.eventType || "triangleWar") === currentType;
                        if (!typeMatch) return false;
                        if (resolvedAllianceId === "all") return true;
                        const eventAllianceIds = new Set(
                          (eventRow?.alliances || [])
                            .map((allianceRef) => String(allianceRef?._id || allianceRef || ""))
                            .filter(Boolean),
                        );
                        return eventAllianceIds.has(String(resolvedAllianceId));
                      });

                      return {
                        ...prev,
                        allianceId: resolvedAllianceId,
                        eventId: String(candidates[0]?._id || ""),
                      };
                    });
                  }}
                  getOptionLabel={(option) => option.label || ""}
                  getOptionValue={(option) => option.id}
                  helperText="Search alliances or keep all"
                  placeholder="Search alliances"
                />
              </div>

              <label>
                Event
                <select
                  value={uploadLaunchConfig.eventId}
                  onChange={(e) =>
                    setUploadLaunchConfig((prev) => ({
                      ...prev,
                      eventId: e.target.value,
                    }))
                  }
                >
                  <option value="">Select event</option>
                  {launcherEventOptions.map((evt) => {
                    const allianceText = getEventAllianceAcronymText(evt);
                    const sourceNationText = getEventSourceNationText(evt);
                    const dateText = evt?.startsAt ? formatEventDate(evt.startsAt) : "No date";
                    const timeText = String(evt?.startTime24 || "").trim();
                    const whenText = timeText ? `${dateText} ${timeText}` : dateText;
                    const baseName = formatEventDisplayLabel(evt, eventTypeConfigById);
                    const nationBadge = sourceNationText ? ` [${sourceNationText}]` : "";
                    const labelBase = `${baseName || whenText}${nationBadge}`;
                    const optionLabel = allianceText
                      ? `${labelBase} | [${allianceText}]`
                      : labelBase;

                    return (
                      <option key={evt._id} value={evt._id}>
                        {optionLabel}
                      </option>
                    );
                  })}
                </select>
              </label>

              <label>
                Result Scope
                <select
                  value={uploadLaunchConfig.resultsScope}
                  onChange={(e) =>
                    setUploadLaunchConfig((prev) => ({
                      ...prev,
                      resultsScope: e.target.value,
                    }))
                  }
                >
                  <option value="commander">Commander leaderboard</option>
                  <option value="alliance">Alliance leaderboard</option>
                  <option value="nation">Nation leaderboard</option>
                  <option value="mixed">Mixed</option>
                  <option value="generic">Generic</option>
                </select>
              </label>

              <label>
                Event Date (for create flow)
                <input
                  type="date"
                  value={uploadLaunchConfig.eventDate}
                  onChange={(e) =>
                    setUploadLaunchConfig((prev) => ({
                      ...prev,
                      eventDate: e.target.value,
                    }))
                  }
                />
              </label>

              {!isFixedFullDayEventType(uploadLaunchConfig.eventType) && (
                <label>
                  Event Time (24h, optional)
                  <input
                    type="time"
                    step="60"
                    value={uploadLaunchConfig.eventTime24 || ""}
                    onChange={(e) =>
                      setUploadLaunchConfig((prev) => ({
                        ...prev,
                        eventTime24: e.target.value,
                      }))
                    }
                  />
                </label>
              )}

              <label>
                Event Name (optional, for create flow)
                <input
                  value={uploadLaunchConfig.eventName}
                  onChange={(e) =>
                    setUploadLaunchConfig((prev) => ({
                      ...prev,
                      eventName: e.target.value,
                    }))
                  }
                />
              </label>

              <label>
                Visible Commanders (optional)
                <input
                  type="number"
                  min={1}
                  value={uploadLaunchConfig.visibleRows}
                  onChange={(e) =>
                    setUploadLaunchConfig((prev) => ({
                      ...prev,
                      visibleRows: e.target.value,
                    }))
                  }
                />
              </label>
            </div>

            {!uploadLaunchConfig.eventId && (
              <p className="events-template-help">
                No existing event selected. Continue to create an event first, then youâ€™ll land directly in upload mode.
              </p>
            )}

            <button className="events-primary-btn" onClick={startUploadLauncherSession}>
              {uploadLaunchConfig.eventId ? "Open Upload Workspace" : "Continue to Create Event"}
            </button>
          </div>
        </div>
      )}

      {activeSection === "events" && !isEventDetailRoute && (
      <>
      <div className="events-card events-filter-card">
        <div className="events-filter-header">
          <div>
            <h2>Find Events</h2>
            <p>Narrow the event list by alliance, event type, or source nation.</p>
          </div>
          <button
            type="button"
            className="events-filter-clear"
            onClick={clearEventIndexFilters}
            disabled={!hasEventIndexFilters}
          >
            Clear filters
          </button>
        </div>
        <div className="events-select-row">
          <label className="events-select-field events-select-field-alliance">
            <span>Alliance</span>
            <select
              value={eventAllianceFilterId}
              onChange={(e) => setEventAllianceFilterId(e.target.value)}
            >
              <option value="all">All alliances</option>
              {eventAllianceFilterOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="events-select-field events-select-field-type">
            <span>Event Type</span>
            <select
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
            >
              <option value="all">All</option>
              {(eventTypeMeta?.eventTypes || []).map((type) => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
          </label>
          <label className="events-select-field events-select-field-type">
            <span>Source Nation</span>
            <select
              value={eventSourceNationFilterId}
              onChange={(e) => setEventSourceNationFilterId(e.target.value)}
            >
              <option value="all">All nations</option>
              {eventSourceNationFilterOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="events-card events-index-card">
        <div className="events-index-header">
          <div>
            <h2>Events</h2>
            <p>{isLoading ? "Loading events..." : `${sortedFilteredEvents.length.toLocaleString()} event${sortedFilteredEvents.length === 1 ? "" : "s"} found`}</p>
          </div>
        </div>

        {sortedFilteredEvents.length === 0 ? (
          <div className="events-empty-state">
            <strong>No events match these filters.</strong>
            <span>Change filters or create a new event.</span>
          </div>
        ) : (
          <div className="events-index-list">
            {sortedFilteredEvents.map((eventItem) => {
              const meta = getEventIndexMeta(eventItem);
              const typeLabel = eventTypeConfigById[eventItem.eventType]?.label || String(eventItem.eventType || "Event");
              const nationLabel = meta.sourceNationText || getEventSourceNationText(eventItem) || "Nation not set";
              const dateLabel = eventItem?.startsAt ? formatEventDate(eventItem.startsAt) : "No date";
              const timeLabel = !isFixedFullDayEventType(eventItem.eventType) && eventItem.startTime24
                ? ` - ${eventItem.startTime24}`
                : "";
              const listEventLabel = `${typeLabel} ${dateLabel}${timeLabel}`;
              const showAllianceText = !isFixedFullDayEventType(eventItem.eventType) && Boolean(meta.allianceText);
              return (
                <div
                  key={eventItem._id}
                  className="events-index-row"
                  onClick={() => openEventDetail(eventItem._id)}
                >
                  <div className="events-index-row-main">
                    <span className="events-index-type">{listEventLabel}</span>
                  </div>
                  <div className="events-index-score">
                    {meta.nationScores.length ? (
                      meta.nationScores.map((scoreRow) => (
                        <span key={scoreRow.label}>
                          <strong>{scoreRow.label}:</strong> {Number(scoreRow.points || 0).toLocaleString()}
                        </span>
                      ))
                    ) : (
                      <span className="events-index-score-empty">No official score</span>
                    )}
                  </div>
                  <div className="events-index-row-meta">
                    <span>{nationLabel}</span>
                    {showAllianceText && (
                      <span className="events-index-alliance-text" title={meta.allianceText}>
                        {meta.allianceText}
                      </span>
                    )}
                    <span>{meta.battleCount.toLocaleString()} battle{meta.battleCount === 1 ? "" : "s"}</span>
                    <span>{meta.scoreboardCount.toLocaleString()} score row{meta.scoreboardCount === 1 ? "" : "s"}</span>
                  </div>
                  <div className="events-index-actions">
                    {canWriteEvents && (
                      <button
                        type="button"
                        className="events-index-open"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEventDateEditor(eventItem);
                        }}
                      >
                        Edit
                      </button>
                    )}
                    <button
                      type="button"
                      className="events-index-open"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEventDetail(eventItem._id);
                      }}
                    >
                      Open
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </>
      )}

      {activeSection === "analytics" && (
        <EventAnalyticsPanel
          isSuperUser={isSuperUser}
          analyticsFilters={analyticsFilters}
          setAnalyticsFilters={setAnalyticsFilters}
          analyticsSearch={analyticsSearch}
          setAnalyticsSearch={setAnalyticsSearch}
          eventTypeMeta={eventTypeMeta}
          analyticsNationOptions={analyticsNationOptions}
          analyticsAllianceOptions={analyticsAllianceOptions}
          loadParticipationAnalytics={loadParticipationAnalytics}
          isAnalyticsLoading={isAnalyticsLoading}
          analyticsData={analyticsData}
          analyticsIntersectionIds={analyticsIntersectionIds}
          analyticsHeatmapRows={analyticsHeatmapRows}
          analyticsCommanderRows={analyticsCommanderRows}
          formatEventDate={formatEventDate}
        />
      )}

      {activeSection === "orphans" && isSuperUser && (
        <div className="events-card events-panel-shell">
          <CommanderOrphanAdminView compact />
        </div>
      )}

      {activeSection === "templates" && (
        <div className="events-card events-panel-shell events-template-page">
          {!isSuperUser ? (
            <p>Template admin is available to super users only.</p>
          ) : (
            <>
              <div className="events-panel-header">
                <h4>Template Admin</h4>
                <span>Manage OCR templates by event type.</span>
              </div>

              <div className="events-grid events-battle-form">
                <label>
                  Event Type
                  <select
                    value={templateAdminEventType}
                    onChange={(e) => {
                      const nextType = e.target.value;
                      setTemplateAdminEventType(nextType);
                      setSelectedTemplateId("");
                      setTemplateDraft((prev) => ({
                        ...prev,
                        _id: "",
                        eventType: nextType,
                      }));
                    }}
                  >
                    {(eventTypeMeta?.eventTypes || []).map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Existing Template
                  <select
                    value={templateDraft._id}
                    onChange={(e) => {
                      const chosen = ocrTemplates.find(
                        (t) => String(t._id) === String(e.target.value),
                      );
                      if (!chosen) {
                        setTemplateDraft({
                          _id: "",
                          name: "",
                          eventType: templateAdminEventType || "",
                          enabled: true,
                          description: "",
                          version: 1,
                          rowsPerScreenshot: 10,
                          rowGapRatio: 0.02,
                          selectionProfile: normalizeSelectionProfileDraft({ resultsScope: "commander", layoutFamily: "leaderboard" }),
                          sourceImage: {
                            dataUrl: "",
                            fileName: "",
                            mimeType: "",
                            width: null,
                            height: null,
                          },
                          annotations: [],
                        });
                        setTemplateUploadFiles([]);
                        return;
                      }

                      setTemplateDraft({
                        _id: String(chosen._id || ""),
                        name: chosen.name || "",
                        eventType:
                          chosen.eventType ||
                          templateAdminEventType ||
                          "",
                        enabled: chosen.enabled !== false,
                        description: chosen.description || "",
                        version: Number(chosen.version || 1),
                        rowsPerScreenshot: Number(
                          chosen.layout?.rowsPerScreenshot || 10,
                        ),
                        rowGapRatio: Number(
                          chosen.layout?.rowGapRatio || 0,
                        ),
                        selectionProfile: normalizeSelectionProfileDraft(chosen.selectionProfile || {}),
                        sourceImage: chosen.sourceImage || {
                          dataUrl:
                            chosen.referenceImageDataUrl || "",
                          fileName: "",
                          mimeType: "",
                          width: null,
                          height: null,
                        },
                        annotations:
                          Array.isArray(chosen.annotations) &&
                          chosen.annotations.length
                            ? chosen.annotations.map((row, idx) => ({
                                id: row.id || `ann-${idx}`,
                                label: row.label,
                                box: row.box,
                              }))
                            : layoutToAnnotations(chosen.layout),
                      });
                      setTemplateUploadFiles([]);
                    }}
                  >
                    <option value="">New template</option>
                    {ocrTemplates.map((t) => (
                      <option key={t._id} value={t._id}>
                        {t.name} v{t.version || 1}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Template Name
                  <input
                    value={templateDraft.name}
                    onChange={(e) =>
                      setTemplateDraft((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Version
                  <input
                    type="number"
                    value={templateDraft.version}
                    onChange={(e) =>
                      setTemplateDraft((prev) => ({
                        ...prev,
                        version: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Enabled
                  <select
                    value={templateDraft.enabled ? "true" : "false"}
                    onChange={(e) =>
                      setTemplateDraft((prev) => ({
                        ...prev,
                        enabled: e.target.value === "true",
                      }))
                    }
                  >
                    <option value="true">Enabled</option>
                    <option value="false">Disabled</option>
                  </select>
                </label>

                <label className="events-span-2">
                  Description
                  <input
                    value={templateDraft.description}
                    onChange={(e) =>
                      setTemplateDraft((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  <span className="events-field-with-info">
                    Rows per Screenshot
                    <span
                      className="events-info-icon"
                      title="How many repeated leaderboard rows are visible in the screenshot."
                      aria-label="Rows help"
                    >
                      â„¹ï¸
                    </span>
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={200}
                    value={templateDraft.rowsPerScreenshot}
                    onChange={(e) =>
                      setTemplateDraft((prev) => ({
                        ...prev,
                        rowsPerScreenshot: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Row Gap Ratio
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    max={0.5}
                    value={templateDraft.rowGapRatio}
                    onChange={(e) =>
                      setTemplateDraft((prev) => ({
                        ...prev,
                        rowGapRatio: e.target.value,
                      }))
                    }
                  />
                </label>

                <div className="events-template-admin-actions events-span-2">
                  <button onClick={() => setIsTemplateImageUploadOpen(true)}>
                    Upload / Replace Base Image
                  </button>
                  {!!templateDraft.sourceImage?.dataUrl && (
                    <span className="events-template-base-meta">
                      {templateDraft.sourceImage.fileName || "base image ready"}
                    </span>
                  )}
                </div>
              </div>

              <TemplateBoxAnnotator
                imageSrc={templateDraft.sourceImage?.dataUrl}
                annotations={templateDraft.annotations}
                onChange={(next) =>
                  setTemplateDraft((prev) => ({
                    ...prev,
                    annotations: next,
                  }))
                }
                labelOptions={TEMPLATE_LABEL_OPTIONS}
              />

              <p className="events-template-help">
                Draw boxes directly on the screenshot. Required labels: Row area,
                Commander name, Points.
              </p>

              <div className="events-template-admin-actions">
                <button
                  onClick={saveTemplateDraft}
                  disabled={isSavingTemplate}
                >
                  {isSavingTemplate ? "Saving..." : "Save Template"}
                </button>
                {!!templateDraft._id && (
                  <button
                    className="events-delete-btn"
                    onClick={deleteTemplateDraft}
                  >
                    Delete Template
                  </button>
                )}
              </div>

              <ImageUploadModal
                open={isTemplateImageUploadOpen}
                title="Upload template base screenshot"
                subtitle="Upload a clear screenshot, then draw bounding boxes and label them."
                files={templateUploadFiles}
                onFilesChange={handleTemplateUploadFilesChange}
                onClose={() => setIsTemplateImageUploadOpen(false)}
                multiple={false}
                maxFiles={1}
              />
            </>
          )}
        </div>
      )}


      {activeSection === "events" && isEventDetailRoute && isLoadingDetail && !selectedEvent && (
        <div className="events-card events-detail-loading">
          <button type="button" className="events-detail-back" onClick={returnToEventIndex}>
            Back to Events
          </button>
          <div>Loading event detail...</div>
        </div>
      )}

      {activeSection === "events" && selectedEvent && (
        <div className="events-card">
          <div className="events-detail-header">
            <button type="button" className="events-detail-back" onClick={returnToEventIndex}>
              Back to Events
            </button>
            <div className="events-detail-title-block">
              <span className="events-detail-kicker">
                {eventTypeConfigById[selectedEvent.eventType]?.label || selectedEvent.eventType || "Event"}
                {selectedEvent?.startsAt ? ` ${formatEventDate(selectedEvent.startsAt)}` : ""}
                {String(selectedEvent?.startTime24 || "").trim() ? ` ${selectedEvent.startTime24}` : ""}
                {selectedEventNationMatchupLabel ? ` - ${selectedEventNationMatchupLabel}` : ""}
              </span>
            </div>
          </div>
          {isLoadingDetail ? (
            <div>Loading detail...</div>
          ) : selectedEvent.eventType === "infiniteZombie" ? (
            <div className="events-zombie-layout">
              <div className="events-zombie-header-row">
                <div className="events-zombie-header-actions">
                  <button onClick={exportInfiniteZombieSharePng}>
                    Share Image
                  </button>
                  <span className="events-zombie-save-state">
                    {isZombieSaving ? "Savingâ€¦" : "Saved"}
                  </span>
                </div>
              </div>

              <div className="events-zombie-sections">
                <section className="events-zombie-card">
                  <div
                    ref={zombieMapRef}
                    className={`events-zombie-map ${
                      isZombieMapDragging ? "dragging" : ""
                    }`}
                    onWheel={handleZombieMapWheel}
                    onPointerDown={handleZombieMapPointerDown}
                    onPointerMove={handleZombieMapPointerMove}
                    onPointerUp={handleZombieMapPointerUp}
                    onPointerCancel={handleZombieMapPointerUp}
                    onClick={() => setActiveZombieSlotId("")}
                  >
                    <div
                      className="events-zombie-map-stage"
                      style={{
                        transform: `translate(${zombieMapPan.x}px, ${zombieMapPan.y}px) scale(${zombieMapZoom})`,
                        transition: isZombieMapDragging
                          ? "none"
                          : "transform 120ms ease-out",
                        backgroundImage: `linear-gradient(rgba(7, 12, 21, 0.18), rgba(7, 12, 21, 0.18)), url(${zombieBoardMap})`,
                      }}
                    >
                      {(zombiePlan.boardSlots || []).map((slot) => (
                        <button
                          key={slot.slotId}
                          className={`events-zombie-pad ${
                            String(slot.slotId) === String(activeZombieSlotId)
                              ? "active"
                              : ""
                          } ${slot.towerType ? "filled" : ""}`}
                          style={{ left: `${slot.x}%`, top: `${slot.y}%` }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (zombieMapSuppressClickRef.current) {
                              e.preventDefault();
                              return;
                            }
                            setActiveZombieSlotId(slot.slotId);
                          }}
                          title={`${slot.slotId} Â· ${
                            zombieTowerLabelByValue[slot.towerType || ""] ||
                            "Empty"
                          }`}
                        >
                          {slot.towerType ? (
                            <img
                              className="events-zombie-pad-image"
                              src={INFINITE_ZOMBIE_TOWER_IMAGES[slot.towerType]}
                              alt={
                                zombieTowerLabelByValue[slot.towerType] ||
                                slot.towerType
                              }
                            />
                          ) : (
                            <span className="events-zombie-pad-tower">
                              {zombieTowerShortByValue[slot.towerType || ""] ||
                                ""}
                            </span>
                          )}
                        </button>
                      ))}
                      {activeZombieSlot && (
                        <div
                          className="events-zombie-slot-flyout"
                          style={{
                            left: `${activeZombieSlot.x}%`,
                            top: `${activeZombieSlot.y}%`,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {INFINITE_ZOMBIE_TOWERS.filter((t) => t.value).map(
                            (tower) => (
                              <button
                                key={tower.value}
                                className={`events-zombie-slot-flyout-btn ${
                                  activeZombieSlot.towerType === tower.value
                                    ? "active"
                                    : ""
                                }`}
                                title={tower.label}
                                onClick={() =>
                                  updateZombieTower(
                                    activeZombieSlot.slotId,
                                    tower.value,
                                  )
                                }
                              >
                                <img
                                  src={
                                    INFINITE_ZOMBIE_TOWER_IMAGES[tower.value]
                                  }
                                  alt={tower.label}
                                />
                              </button>
                            ),
                          )}
                          <button
                            className="events-zombie-slot-flyout-clear"
                            onClick={() =>
                              updateZombieTower(activeZombieSlot.slotId, "")
                            }
                          >
                            Remove tower
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="events-zombie-slot-editor">
                    <strong>
                      {activeZombieSlot?.slotId || "Slot"} Â·{" "}
                      {zombieTowerLabelByValue[
                        activeZombieSlot?.towerType || ""
                      ] || "Empty"}
                    </strong>
                  </div>
                </section>

                <section className="events-zombie-card">
                  <h4>Items by Wave</h4>
                  <p className="events-zombie-sub">
                    Start: pick 1 item per tier. Waves: pick 1 item each wave.
                  </p>

                  <div className="events-zombie-start-grid">
                    {Object.entries(INFINITE_ZOMBIE_ITEMS).map(
                      ([tier, items]) => (
                        <label key={tier} className="events-zombie-item-row">
                          <span
                            className={`events-zombie-tier events-zombie-tier-${tier}`}
                          >
                            {tier}
                          </span>
                          <div className="events-zombie-item-input-wrap">
                            {zombiePlan.startItems?.[tier] ? (
                              <img
                                className="events-zombie-item-icon"
                                src={
                                  infiniteZombieItemMetaByValue[
                                    zombiePlan.startItems[tier]
                                  ]?.icon
                                }
                                alt={
                                  infiniteZombieItemMetaByValue[
                                    zombiePlan.startItems[tier]
                                  ]?.label || "selected item"
                                }
                              />
                            ) : (
                              <div className="events-zombie-item-icon events-zombie-item-icon-empty" />
                            )}
                            <select
                              value={zombiePlan.startItems?.[tier] || ""}
                              onChange={(e) =>
                                updateZombieStartItem(tier, e.target.value)
                              }
                              style={{
                                color: zombiePlan.startItems?.[tier]
                                  ? infiniteZombieItemMetaByValue[
                                      zombiePlan.startItems[tier]
                                    ]?.color || undefined
                                  : undefined,
                              }}
                            >
                              <option value="">Select start item</option>
                              {items.map((item) => (
                                <option
                                  key={item.value}
                                  value={item.value}
                                  style={{ color: item.color }}
                                >
                                  {item.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </label>
                      ),
                    )}
                  </div>

                  <div className="events-zombie-wave-list">
                    {(zombiePlan.waveItems || []).map((entry) => (
                      <label
                        key={entry.wave}
                        className="events-zombie-item-row"
                      >
                        <span>Wave {entry.wave}</span>
                        <div className="events-zombie-item-input-wrap">
                          {entry.item ? (
                            <img
                              className="events-zombie-item-icon"
                              src={
                                infiniteZombieItemMetaByValue[entry.item]?.icon
                              }
                              alt={
                                infiniteZombieItemMetaByValue[entry.item]
                                  ?.label || "selected item"
                              }
                            />
                          ) : (
                            <div className="events-zombie-item-icon events-zombie-item-icon-empty" />
                          )}
                          <select
                            value={entry.item || ""}
                            onChange={(e) =>
                              updateZombieWaveItem(entry.wave, e.target.value)
                            }
                            style={{
                              color: entry.item
                                ? infiniteZombieItemMetaByValue[entry.item]
                                    ?.color || undefined
                                : undefined,
                            }}
                          >
                            <option value="">No pick</option>
                            {infiniteZombieWaveItemOptions.map((item) => (
                              <option
                                key={item.value}
                                value={item.value}
                                style={{ color: item.color }}
                              >
                                {item.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </label>
                    ))}
                  </div>
                </section>
              </div>
            </div>
          ) : (
            <>
              {Number(selectedEventTypeConfig?.requiredAllianceCount || 0) > 0 && canWriteEvents && (
                <div className="events-alliance-section events-event-alliance-manager">
                  <div className="events-alliance-section-header events-section-header-with-action">
                    <span>Participating Alliances</span>
                    <div className="events-alliance-editor-actions">
                      {isEventAllianceEditorOpen && (
                        <span>
                          {`Select up to ${Number(selectedEventTypeConfig?.requiredAllianceCount || 0)} alliance(s)`}
                        </span>
                      )}
                      {!isEventAllianceEditorOpen && (
                        <AppButton
                          kind="secondary"
                          className="events-secondary-btn"
                          onClick={() => setIsEventAllianceEditorOpen(true)}
                        >
                          Edit Alliances
                        </AppButton>
                      )}
                    </div>
                  </div>

                  <div className="events-selected-alliance-pills" aria-live="polite">
                    {(eventAllianceDraftIds || []).length ? (
                      eventAllianceDraftIds.map((id) => {
                        const alliance = alliances.find((a) => String(a._id) === String(id));
                        return (
                          <span key={id} className="events-selected-alliance-pill">
                            {alliance
                              ? `${alliance?.nation?.number || "?"} Â· ${alliance.acronym || alliance.name}`
                              : id}
                          </span>
                        );
                      })
                    ) : (
                      <span className="events-selected-alliance-pill events-selected-alliance-pill-empty">No alliances selected yet</span>
                    )}
                  </div>

                  {isEventAllianceEditorOpen && (
                    <>
                      <div className="events-alliance-grid">
                    {[...Array(Number(selectedEventTypeConfig?.requiredAllianceCount || 0)).keys()].map((idx) => {
                      const availableAlliances = getAvailableEventAlliances(idx);
                      return (
                        <div className="events-alliance-card" key={`event-alliance-${idx}`}>
                          <h4>{idx === 0 ? "My Alliance" : `Alliance ${idx + 1}`}</h4>

                          {canWriteAlliances && (
                            <div className="events-inline-actions">
                              <button
                                className="events-secondary-btn"
                                onClick={() => handleQuickCreateAlliance(idx)}
                              >
                                + Alliance (or Nation + Alliance)
                              </button>
                            </div>
                          )}

                          <div className="events-search-select-field">
                            <SingleSelectDropdown
                              label="Alliance"
                              options={availableAlliances}
                              value={eventAllianceSlotIds[idx] || ""}
                              onChange={(allianceId) => updateEventAllianceSlot(idx, allianceId)}
                              getOptionLabel={(option) =>
                                `${option?.nation?.number || "?"} Â· ${option?.acronym || option?.name || ""}`
                              }
                              getOptionValue={(option) => option._id}
                              helperText="Search and select one alliance"
                              placeholder="Search alliances"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="events-actions-row">
                    <button
                      type="button"
                      className="events-secondary-btn"
                      onClick={handleCancelEventAllianceEdit}
                      disabled={isSavingEventAlliances}
                    >
                      Cancel
                    </button>
                    <button
                      className="events-primary-btn"
                      onClick={handleSaveEventAlliances}
                      disabled={isSavingEventAlliances}
                    >
                      {isSavingEventAlliances ? "Saving..." : "Save Alliances"}
                    </button>
                  </div>
                    </>
                  )}
                </div>
              )}

              {activeSection === "events" && (
                <div className="events-workspace-nav-row">
                  <EventsWorkspaceTabs
                    activeTab={activeTab}
                    tabs={workspaceEventTabs}
                    onSelect={(tab) => {
                      setActiveTab(tab);
                      if (tab === "results") setResultEntryMode("manual");
                      if (tab === "upload") setResultEntryMode("upload");
                    }}
                  />
                  <div className="events-workspace-nav-actions">
                    {canWriteEvents && activeTab === "results" && (
                      <AppButton onClick={openResultEntryModal}>
                        {editingResultId ? "Edit Result" : "Add Result"}
                      </AppButton>
                    )}
                  </div>
                </div>
              )}

              {activeSection === "events" && activeTab === "overview" && (
                <EventOverviewPanel
                  selectedEvent={selectedEvent}
                  eventDisplayLabel={formatEventDisplayLabel(selectedEvent, eventTypeConfigById)}
                  eventTypeLabel={eventTypeConfigById[selectedEvent.eventType]?.label}
                  nationLabels={selectedEventNationLabels}
                  formatEventDate={formatEventDate}
                  canEdit={canWriteEvents}
                  onSaveMeta={handleSaveEventMeta}
                  isSavingMeta={isSavingEventMeta}
                />
              )}

              {activeSection === "events" && activeTab === "planning" && (
                selectedPlannerPluginId === "triangleStrongholdPlanner" ? (
                <EventPlanningPanel
                  defenseSummary={`${(selectedEvent.strongholds || []).filter((s) => !!s.defendingCommander).length}/${selectedEvent.maxDefenders || 70} total Â· Max per island: ${selectedEvent.maxDefendersPerIsland || 10}`}
                  autoPopulateControls={canWriteEvents ? (
                    <div className="events-autopop-wrap">
                      <button
                        className="events-autopop-btn"
                        onClick={() => setIsAutoPopulateOpen((p) => !p)}
                      >
                        {isAutoPopulateOpen
                          ? "Close Auto Populate"
                          : "Auto Populate"}
                      </button>
                      {isAutoPopulateOpen && (
                        <div className="events-autopop-panel">
                          <label>
                            Strategy
                            <select
                              value={autoPopulateMode}
                              onChange={(e) =>
                                setAutoPopulateMode(e.target.value)
                              }
                            >
                              <option value="rude">Rude</option>
                              <option value="standard">Standard</option>
                              <option value="experimental">Experimental</option>
                            </select>
                          </label>
                          <label>
                            Min City Level (C20+)
                            <input
                              type="range"
                              min="20"
                              max="41"
                              value={autoPopulateMinCityLevel}
                              onChange={(e) =>
                                setAutoPopulateMinCityLevel(
                                  Number(e.target.value),
                                )
                              }
                            />
                            <span>C{autoPopulateMinCityLevel}</span>
                          </label>
                          <label className="events-autopop-checkbox">
                            <input
                              type="checkbox"
                              checked={autoPopulateForceReset}
                              onChange={(e) =>
                                setAutoPopulateForceReset(e.target.checked)
                              }
                            />
                            Force reset existing placements
                          </label>
                          <button
                            className="events-autopop-apply"
                            onClick={handleAutoPopulate}
                            disabled={isAutoPopulating}
                          >
                            {isAutoPopulating
                              ? "Applyingâ€¦"
                              : "Apply Auto Populate"}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : null}
                >
                  {!focusedIslandId ? (
                    <div className="events-island-overview-map events-island-overview-shape">
                      {(() => {
                        const islandByName = new Map(
                          (strongholdsByIsland || []).map((x) => [
                            String(x.island?.name || "").toUpperCase(),
                            x,
                          ]),
                        );
                        const order = ["A", "B", "C", "D", "E", "F", "G"];
                        const allFilledStrengths = (
                          selectedEvent?.strongholds || []
                        )
                          .map((s) => {
                            const commanderId = String(
                              s.defendingCommander || "",
                            );
                            return commanderId
                              ? Number(commanderPowerById[commanderId] || 0)
                              : 0;
                          })
                          .filter((v) => v > 0);
                        const globalMaxStrength = Math.max(
                          1,
                          ...allFilledStrengths,
                        );

                        return order.map((key) => {
                          const entry = islandByName.get(key);
                          if (!entry) return null;
                          const assigned = (entry.strongholds || []).filter(
                            (s) => !!s.defendingCommander,
                          ).length;

                          const cells = (entry.strongholds || [])
                            .slice()
                            .sort(
                              (a, b) =>
                                parseStrongholdNumber(a.name).number -
                                parseStrongholdNumber(b.name).number,
                            )
                            .map((s) => {
                              const commanderId = String(
                                s.defendingCommander || "",
                              );
                              const isEmpty = !commanderId;
                              const strength = isEmpty
                                ? 0
                                : Number(commanderPowerById[commanderId] || 0);
                              const intensity = strength / globalMaxStrength;
                              return {
                                id: s._id,
                                bg: toHeatColor(intensity, isEmpty),
                                filled: !isEmpty,
                              };
                            });

                          return (
                            <button
                              key={entry.island._id}
                              className={`events-island-node events-island-${key.toLowerCase()}`}
                              onClick={() =>
                                setFocusedIslandId(String(entry.island._id))
                              }
                            >
                              <div className="events-island-node-header">
                                <strong>{entry.island.name}</strong>
                                <span>
                                  {assigned}/
                                  {selectedEvent.maxDefendersPerIsland || 10}
                                </span>
                              </div>
                              <div
                                className="events-island-mini-grid"
                                aria-hidden
                              >
                                {cells.map((cell) => (
                                  <span
                                    key={cell.id}
                                    className={`events-island-mini-cell ${cell.filled ? "filled" : "empty"}`}
                                    style={{ background: cell.bg }}
                                  />
                                ))}
                              </div>
                            </button>
                          );
                        });
                      })()}
                    </div>
                  ) : (
                    <div>
                      <div className="events-island-zoom-header">
                        <button
                          className="events-back-btn"
                          onClick={() => setFocusedIslandId("")}
                        >
                          â† Back to island map
                        </button>
                      </div>
                      {strongholdsByIsland
                        .filter(
                          ({ island }) =>
                            String(island._id) === String(focusedIslandId),
                        )
                        .map(({ island, strongholds }) => {
                          const assigned = strongholds.filter(
                            (s) => !!s.defendingCommander,
                          ).length;
                          return (
                            <div
                              key={island._id}
                              className="events-island-card"
                            >
                              <h4>
                                {island.name}{" "}
                                <span>
                                  {assigned}/
                                  {selectedEvent.maxDefendersPerIsland || 10}
                                </span>
                              </h4>
                              <div className="events-island-slots">
                                {strongholds.map((sh) => (
                                  <div
                                    key={sh._id}
                                    className={`events-slot ${sh.defendingCommander ? "filled" : "empty"} ${canWriteEvents ? "editable" : ""}`}
                                    onClick={() =>
                                      canWriteEvents && openStrongholdEditor(sh)
                                    }
                                    role={canWriteEvents ? "button" : undefined}
                                    tabIndex={canWriteEvents ? 0 : undefined}
                                    onKeyDown={(e) => {
                                      if (!canWriteEvents) return;
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        openStrongholdEditor(sh);
                                      }
                                    }}
                                  >
                                    <div className="events-slot-title-row">
                                      <div className="events-slot-title">
                                        {sh.name}
                                      </div>
                                      {canWriteEvents && (
                                        <button
                                          className="events-slot-edit-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openStrongholdEditor(sh);
                                          }}
                                          aria-label={`Edit ${sh.name}`}
                                        >
                                          âœï¸
                                        </button>
                                      )}
                                    </div>
                                    {(() => {
                                      const commanderId = getId(
                                        sh.defendingCommander,
                                      );
                                      const commander =
                                        commanderById[commanderId];
                                      const defenderLabel = commanderId
                                        ? commander?.name ||
                                          commanderNameById[commanderId] ||
                                          "Assigned"
                                        : "Unassigned";
                                      const battlePower = commander
                                        ? Number(
                                            commander.battlePower ||
                                              commanderPowerById[commanderId] ||
                                              0,
                                          )
                                        : 0;
                                      const fleetEstimate =
                                        estimateFleetBp(commander);
                                      const fleetBpEstimate = fleetEstimate
                                        ? fleetEstimate.toLocaleString()
                                        : battlePower > 0
                                          ? Math.round(
                                              battlePower * 0.22,
                                            ).toLocaleString()
                                          : "TBD";
                                      const itemMeta = getItemMeta(sh.item);

                                      return (
                                        <>
                                          <div className="events-slot-defender">
                                            {defenderLabel}
                                          </div>
                                          <div
                                            className={`events-slot-meta ${itemMeta ? "events-slot-meta-value" : "events-slot-meta-placeholder"}`}
                                            style={
                                              itemMeta
                                                ? { color: itemMeta.color }
                                                : undefined
                                            }
                                          >
                                            {itemMeta?.label || "no item"}
                                          </div>
                                          <div className="events-slot-meta events-slot-meta-value">
                                            {fleetBpEstimate}
                                          </div>
                                        </>
                                      );
                                    })()}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}

                  {canWriteEvents && editingStronghold && (
                    <div
                      className="events-slot-modal-backdrop"
                      onClick={closeStrongholdEditor}
                    >
                      <div
                        className="events-slot-modal"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="events-slot-modal-header">
                          <strong>{editingStronghold.name}</strong>
                          <button
                            className="events-slot-modal-close"
                            onClick={closeStrongholdEditor}
                          >
                            âœ•
                          </button>
                        </div>

                        <label>
                          Commander
                          <select
                            value={slotEditor.defendingCommander}
                            onChange={(e) => {
                              const nextCommander = e.target.value;
                              setSlotEditor((prev) => {
                                const next = {
                                  ...prev,
                                  defendingCommander: nextCommander,
                                };
                                if (
                                  !nextCommander &&
                                  next.item &&
                                  next.item !== "guidedBomb"
                                )
                                  next.item = "";
                                return next;
                              });
                            }}
                          >
                            <option value="">No defender</option>
                            {myAllianceCommanderOptions.map((c) => {
                              const placedAt =
                                commanderPlacementById[String(c._id)]
                                  ?.strongholdName;
                              return (
                                <option key={c._id} value={c._id}>
                                  {c.name}
                                  {c.cityLevel ? ` (C${c.cityLevel})` : ""}
                                  {placedAt ? ` â€” ${placedAt}` : ""}
                                </option>
                              );
                            })}
                          </select>
                        </label>

                        <label>
                          Item
                          <select
                            value={slotEditor.item}
                            onChange={(e) =>
                              setSlotEditor((prev) => ({
                                ...prev,
                                item: e.target.value,
                              }))
                            }
                            style={
                              slotEditor.item
                                ? { color: getItemMeta(slotEditor.item)?.color }
                                : undefined
                            }
                          >
                            <option value="">No item</option>
                            {STRONGHOLD_ITEMS.map((item) => {
                              const disabled =
                                item.value !== "guidedBomb" &&
                                !slotEditor.defendingCommander;
                              return (
                                <option
                                  key={item.value}
                                  value={item.value}
                                  disabled={disabled}
                                  style={{ color: item.color }}
                                >
                                  {item.label}
                                </option>
                              );
                            })}
                          </select>
                        </label>

                        <div className="events-slot-modal-actions">
                          <button
                            className="events-cancel-btn"
                            onClick={closeStrongholdEditor}
                          >
                            Cancel
                          </button>
                          <button onClick={handleSaveStrongholdEditor}>
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </EventPlanningPanel>
                ) : selectedPlannerPluginId === "eliteWarRealtimePlanner" ? (
                  <EliteWarRealtimePlanner
                    selectedEvent={selectedEvent}
                    canWriteEvents={canWriteEvents}
                    onPlannerStatePatch={(nextPlannerState) =>
                      setSelectedEvent((prev) => (
                        prev ? { ...prev, plannerState: nextPlannerState } : prev
                      ))
                    }
                  />
                ) : selectedPlannerPluginId === "nationIntelRealtimePlanner" ? (
                  <NationIntelRealtimePlanner
                    selectedEvent={selectedEvent}
                    eventCommanderSelectOptions={eventCommanderSelectOptions}
                  />
                ) : PlannerComponent ? (
                  <PlannerComponent selectedEvent={selectedEvent} />
                ) : (
                  <EventPlanningPanel
                    defenseSummary="Planner not available for this event type."
                    autoPopulateControls={null}
                  >
                    <div className="events-empty-state">
                      No planner plugin is configured for this event type.
                    </div>
                  </EventPlanningPanel>
                )
              )}

              {activeSection === "events" && activeTab === "battles" && (
                <EventBattlesPanel>
                  {canWriteEvents && (
                    <div className="events-battle-log-toolbar">
                      <button
                        type="button"
                        className="events-btn events-btn-secondary"
                        onClick={openManualBattleModal}
                      >
                        Add Battle Manually
                      </button>
                    </div>
                  )}

                  <div className="events-battle-log-summary">
                    <div>
                      <strong>Filtered results</strong>
                      <span>{filteredBattleLogSummary.totalCount.toLocaleString()} of {battleLogLeadershipSummary.totalCount.toLocaleString()} battles</span>
                      <span>{formatBattlePoints(filteredBattleLogSummary.exactGained + filteredBattleLogSummary.estimatedGained, "replay_estimate")} alliance points</span>
                      <span>{formatSignedBattlePoints(filteredBattleLogSummary.exactNet + filteredBattleLogSummary.estimatedNet, "replay_estimate")} net</span>
                    </div>
                    <div>
                      <strong>Exact battle impact</strong>
                      <span>{formatBattlePoints(battleLogLeadershipSummary.exactGained)} gained</span>
                      <span>{formatBattlePoints(battleLogLeadershipSummary.exactOpponentPoints)} opponent</span>
                      <span>{formatSignedBattlePoints(battleLogLeadershipSummary.exactNet)} net</span>
                    </div>
                    <div>
                      <strong>Estimated battle impact</strong>
                      <span>{formatBattlePoints(battleLogLeadershipSummary.estimatedGained, "replay_estimate")} gained</span>
                      <span>{formatBattlePoints(battleLogLeadershipSummary.estimatedOpponentPoints, "replay_estimate")} opponent</span>
                      <span>{formatSignedBattlePoints(battleLogLeadershipSummary.estimatedNet, "replay_estimate")} net</span>
                    </div>
                    <div>
                      <strong>Risk queue</strong>
                      <span>{battleLogLeadershipSummary.highRiskCount.toLocaleString()} negative high/critical</span>
                      <span>{battleLogLeadershipSummary.strikeCandidateCount.toLocaleString()} strike candidates</span>
                      <span>{battleLogLeadershipSummary.bubbleDropCount.toLocaleString()} bubble drop rows</span>
                      <span>{battleLogLeadershipSummary.strikeReviewCount.toLocaleString()} strike reviews</span>
                      <span>{battleLogLeadershipSummary.largeExchangeCount.toLocaleString()} large exchanges</span>
                      <span>{battleLogLeadershipSummary.unresolvedImpactCount.toLocaleString()} unknown impact</span>
                      <span>{battleLogLeadershipSummary.needsReportCount.toLocaleString()} need reports</span>
                    </div>
                    <div>
                      <strong>Evidence</strong>
                      <span>{battleLogLeadershipSummary.evidence.battleLog.toLocaleString()} log</span>
                      <span>{battleLogLeadershipSummary.evidence.replay.toLocaleString()} replay</span>
                      <span>{battleLogLeadershipSummary.evidence.report.toLocaleString()} report</span>
                    </div>
                    <div>
                      <strong>Official results</strong>
                      <span>{participantResults.length.toLocaleString()} commander scores</span>
                      <span>{formatBattlePoints(officialParticipantResultTotal)} total points</span>
                    </div>
                  </div>

                  <div className="events-grid events-battle-log-filters">
                    <label>
                      Search
                      <input
                        value={battleLogSearch}
                        onChange={(e) => setBattleLogSearch(e.target.value)}
                        placeholder="Commander or alliance"
                      />
                    </label>
                    <MultiSelectFilter
                      label="Alliances"
                      options={battleLogAllianceOptions}
                      selected={battleLogAllianceFilterIds}
                      setSelected={setBattleLogAllianceFilterIds}
                      placeholder="All alliances"
                      pinSelectedToTop
                    />
                    <MultiSelectFilter
                      label="Commanders"
                      options={battleLogCommanderOptions}
                      selected={battleLogCommanderFilterIds}
                      setSelected={setBattleLogCommanderFilterIds}
                      placeholder="All commanders"
                      pinSelectedToTop
                    />
                    <label>
                      View
                      <select value={battleLogViewMode} onChange={(e) => setBattleLogViewMode(e.target.value)}>
                        <option value="battles">By battle</option>
                        <option value="commanders">By commander</option>
                      </select>
                    </label>
                    <label>
                      Rules Filter
                      <select value={battleLogRuleFilter} onChange={(e) => setBattleLogRuleFilter(e.target.value)}>
                        {battleLogRuleFilterOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Defender Nation
                      <select value={battleLogDefenderNationFilter} onChange={(e) => setBattleLogDefenderNationFilter(e.target.value)}>
                        <option value="all">All</option>
                        <option value="home">Home nation defending</option>
                        <option value="opponent">Opponent nation defending</option>
                      </select>
                    </label>
                    <label>
                      Reviewed
                      <select value={battleLogReviewedFilter} onChange={(e) => setBattleLogReviewedFilter(e.target.value)}>
                        <option value="all">All review states</option>
                        <option value="unreviewed">Unreviewed only</option>
                        <option value="reviewed">Reviewed only</option>
                      </select>
                    </label>
                    <label className="events-battle-log-mobile-sort">
                      Sort By
                      <select
                        value={battleLogSortConfig.key}
                        onChange={(e) => setBattleLogSortConfig((current) => ({
                          ...current,
                          key: e.target.value,
                        }))}
                      >
                        <option value="time">Time</option>
                        <option value="battleLog">Battle Log</option>
                        <option value="replay">Replay</option>
                        <option value="report">Report</option>
                        <option value="attacker">Attacker</option>
                        <option value="attackerTroops">Attacker Troops</option>
                        <option value="defender">Defender</option>
                        <option value="defenderTroops">Defender Troops</option>
                        <option value="homeNet">Impact Rank</option>
                        <option value="expectedWinner">Expected Winner</option>
                        <option value="location">Location</option>
                        <option value="triage">Triage</option>
                      </select>
                    </label>
                    <label className="events-battle-log-mobile-sort">
                      Sort Order
                      <select
                        value={battleLogSortConfig.direction}
                        onChange={(e) => setBattleLogSortConfig((current) => ({
                          ...current,
                          direction: e.target.value,
                        }))}
                      >
                        <option value="desc">Descending</option>
                        <option value="asc">Ascending</option>
                      </select>
                    </label>
                  </div>

                  {canWriteEvents && isBattleEntryModalOpen && (
                    <div className="events-create-modal-backdrop" onClick={closeBattleEntryModal}>
                      <div
                        className="events-card events-create-modal events-battle-entry-modal"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="events-create-modal-header">
                          <h3>{editingBattleId ? "Edit Battle" : "Add Battle Manually"}</h3>
                          <button
                            type="button"
                            className="events-create-modal-close"
                            onClick={closeBattleEntryModal}
                            aria-label="Close battle form"
                          >
                            x
                          </button>
                        </div>
                        <div className="events-grid events-battle-form">
                          <label>
                            Stronghold
                            <select
                              value={battleForm.strongholdId}
                              onChange={(e) =>
                                setBattleForm((p) => ({
                                  ...p,
                                  strongholdId: e.target.value,
                                }))
                              }
                            >
                              {(selectedEvent.strongholds || []).map((sh) => (
                                <option key={sh._id} value={sh._id}>
                                  {sh.name}
                                </option>
                              ))}
                            </select>
                          </label>

                          <label>
                            Attacking Alliance
                            <select
                              value={battleForm.attackingAlliance}
                              onChange={(e) =>
                                setBattleForm((p) => ({
                                  ...p,
                                  attackingAlliance: e.target.value,
                                }))
                              }
                            >
                              <option value="">Select</option>
                              {eventAlliances.map((a) => (
                                <option key={a._id} value={a._id}>
                                  {a.acronym || a.name}
                                </option>
                              ))}
                            </select>
                          </label>

                          <label>
                            Defending Alliance
                            <select
                              value={battleForm.defendingAlliance}
                              onChange={(e) =>
                                setBattleForm((p) => ({
                                  ...p,
                                  defendingAlliance: e.target.value,
                                }))
                              }
                            >
                              <option value="">Select</option>
                              {eventAlliances.map((a) => (
                                <option key={a._id} value={a._id}>
                                  {a.acronym || a.name}
                                </option>
                              ))}
                            </select>
                          </label>

                          <label>
                            Attacking Commander
                            <select
                              value={battleForm.attackingCommander}
                              onChange={(e) =>
                                setBattleForm((p) => ({
                                  ...p,
                                  attackingCommander: e.target.value,
                                }))
                              }
                            >
                              <option value="">Unknown</option>
                              {eventCommanders.map((c) => (
                                <option key={c._id} value={c._id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                          </label>

                          <label>
                            Defending Commander
                            <select
                              value={battleForm.defendingCommander}
                              onChange={(e) =>
                                setBattleForm((p) => ({
                                  ...p,
                                  defendingCommander: e.target.value,
                                }))
                              }
                            >
                              <option value="">Unknown</option>
                              {eventCommanders.map((c) => (
                                <option key={c._id} value={c._id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                          </label>

                          <label>
                            Result
                            <select
                              value={battleForm.result}
                              onChange={(e) =>
                                setBattleForm((p) => ({
                                  ...p,
                                  result: e.target.value,
                                }))
                              }
                            >
                              <option value="attacker_win">attacker_win</option>
                              <option value="defender_win">defender_win</option>
                            </select>
                          </label>

                          <label>
                            Points Awarded
                            <input
                              value={battleForm.pointsAwarded}
                              onChange={(e) =>
                                setBattleForm((p) => ({
                                  ...p,
                                  pointsAwarded: e.target.value,
                                }))
                              }
                            />
                          </label>

                          <label>
                            Source
                            <select
                              value={battleForm.source}
                              onChange={(e) =>
                                setBattleForm((p) => ({
                                  ...p,
                                  source: e.target.value,
                                }))
                              }
                            >
                              <option value="manual">manual</option>
                              <option value="screenshot">screenshot</option>
                            </select>
                          </label>

                          <label>
                            Screenshot Data URL
                            <input
                              value={battleForm.screenshotDataUrl}
                              onChange={(e) =>
                                setBattleForm((p) => ({
                                  ...p,
                                  screenshotDataUrl: e.target.value,
                                }))
                              }
                            />
                          </label>

                          <label className="events-span-2">
                            Notes
                            <input
                              value={battleForm.notes}
                              onChange={(e) =>
                                setBattleForm((p) => ({
                                  ...p,
                                  notes: e.target.value,
                                }))
                              }
                            />
                          </label>
                        </div>
                        <div className="events-battle-entry-actions">
                          <button
                            type="button"
                            className="events-btn events-btn-primary"
                            onClick={handleBattleSubmit}
                          >
                            {editingBattleId ? "Update Battle" : "Add Battle"}
                          </button>
                          <button
                            type="button"
                            className="events-btn events-btn-secondary"
                            onClick={closeBattleEntryModal}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="events-table-wrap events-battle-log-imported-wrap">
                    <table className="events-table events-battle-log-table">
                      <thead>
                        <tr>
                          <th>{renderBattleLogSortHeader("time", "Time")}</th>
                          <th>{renderBattleLogSortHeader("battleLog", "Battle Log")}</th>
                          <th>{renderBattleLogSortHeader("replay", "Replay")}</th>
                          <th>{renderBattleLogSortHeader("report", "Report")}</th>
                          <th>{renderBattleLogSortHeader("attacker", "Attacker")}</th>
                          <th>{renderBattleLogSortHeader("attackerTroops", "Attacker Troops")}</th>
                          <th>{renderBattleLogSortHeader("defender", "Defender")}</th>
                          <th>{renderBattleLogSortHeader("defenderTroops", "Defender Troops")}</th>
                          <th>{renderBattleLogSortHeader("homeNet", "Impact Rank")}</th>
                          <th>{renderBattleLogSortHeader("expectedWinner", "Expected Winner")}</th>
                          <th>{renderBattleLogSortHeader("location", "Location")}</th>
                          <th>{renderBattleLogSortHeader("triage", "Triage")}</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {battleLogDisplayRows.map((displayRow) => {
                          if (displayRow.type === "bubbleGroupSpacer" || displayRow.type === "commanderGroupSpacer") {
                            return (
                              <tr key={displayRow.key} className="events-battle-log-group-spacer" aria-hidden="true">
                                <td colSpan={13} />
                              </tr>
                            );
                          }
                          const b = displayRow.battle;
                          const isBubbleGroupParent = displayRow.type === "bubbleGroupParent";
                          const isBubbleGroupChild = displayRow.type === "bubbleGroupChild";
                          const isCommanderGroupParent = displayRow.type === "commanderGroupParent";
                          const isCommanderGroupChild = displayRow.type === "commanderGroupChild";
                          const attackerLabel = getBattleSideLabel(b, "attacker");
                          const defenderLabel = getBattleSideLabel(b, "defender");
                          const attackerTroops = getBattleSideTroopTotal(b, "attacker");
                          const defenderTroops = getBattleSideTroopTotal(b, "defender");
                          const homeTotals = getBattleHomeTotals(b);
                          const homeNet = homeTotals.gained == null && homeTotals.leaked == null
                            ? null
                            : asFiniteInteger(homeTotals.gained, 0) - asFiniteInteger(homeTotals.leaked, 0);
                          const impactRank = battleLogImpactRanks.get(getBattleRowKey(b));
                          const homeNetClassName = impactRank?.tone
                            ? `events-battle-log-impact-${impactRank.tone}`
                            : homeNet == null
                            ? ""
                            : (homeNet > 0 ? "events-battle-log-net-positive" : (homeNet < 0 ? "events-battle-log-net-negative" : ""));
                          const pointSource = getBattlePointSource(b);
                          const pointConfidence = getBattleConfidence(b);
                          const riskLevel = getBattleRiskLevel(b);
                          const exchangeLevel = getBattleExchangeLevel(b);
                          const impactStatus = getBattleImpactStatus(b);
                          const expectedWinner = getBattleExpectedWinner(b);
                          const strikeReviewLevel = getBattleStrikeReviewLevel(b);
                          const bubbleDropGroups = getBubbleDropGroupsForBattle(b);
                          const strikeCandidateState = getBattleStrikeCandidateState(
                            b,
                            strikeSettings,
                            {
                              attackerNationRole: getBattleAttackerNationRole(b),
                              defenderNationRole: getBattleDefenderNationRole(b),
                              bubbleDropGroups,
                            },
                          );
                          const needsReport = getBattleNeedsReport(b);
                          const bubbleDropGroup = displayRow.group || getPrimaryBubbleDropGroup(b);
                          const commanderGroup = displayRow.commanderGroup || null;
                          const isReviewed = Boolean(b.review?.reviewedAt || b.review?.reviewed);
                          const evidence = getBattleEvidence(b);
                          const hasCaptureTruthRows = battleHasCaptureTruthRows(b);
                          const hasBattleLogData = evidence.battleLog;
                          const hasReplayData = evidence.replay;
                          const hasReportData = evidence.report;
                          const canViewReport = hasReplayData || hasCaptureTruthRows;
                          const hasStrikeableCommanders = getBattleStrikeCommanderRefs(b).length > 0;
                          const isImportedAozBattle = ["aoz_battle_log", "aoz_battle_report"].includes(String(b.source || ""));
                          const canDeleteBattleRow = isImportedAozBattle ? isSuperUser : canDeleteEvents;
                          const location = formatBattleLocation(b);
                          const nationHighlight = getBattleNationHighlight(b);
                          const bubbleGroupId = String(bubbleDropGroup?.id || "");
                          const commanderGroupId = String(commanderGroup?.groupId || "");
                          const handleBubbleParentToggle = () => {
                            if (isBubbleGroupParent && bubbleGroupId) toggleBubbleDropGroup(bubbleGroupId);
                          };
                          const handleCommanderParentToggle = () => {
                            if (isCommanderGroupParent && commanderGroupId) toggleCommanderBattleGroup(commanderGroupId);
                          };
                          const rowClassName = [
                            "events-battle-log-row",
                            nationHighlight ? `events-battle-log-row-${nationHighlight}` : "",
                            isBubbleGroupParent ? "events-battle-log-bubble-parent" : "",
                            isBubbleGroupParent && displayRow.isExpanded ? "events-battle-log-bubble-parent-expanded" : "",
                            isBubbleGroupChild ? "events-battle-log-bubble-child" : "",
                            isBubbleGroupChild && displayRow.isLastGroupChild ? "events-battle-log-bubble-child-last" : "",
                            isCommanderGroupParent ? "events-battle-log-commander-parent" : "",
                            isCommanderGroupParent && displayRow.isExpanded ? "events-battle-log-commander-parent-expanded" : "",
                            isCommanderGroupChild ? "events-battle-log-commander-child" : "",
                            isCommanderGroupChild && displayRow.isLastGroupChild ? "events-battle-log-commander-child-last" : "",
                          ].filter(Boolean).join(" ");
                          const handleGroupedRowToggle = () => {
                            if (isBubbleGroupParent) handleBubbleParentToggle();
                            if (isCommanderGroupParent) handleCommanderParentToggle();
                          };
                          return (
                            <tr
                              key={displayRow.key || `log-${b._id}`}
                              className={rowClassName}
                              onClick={handleGroupedRowToggle}
                              tabIndex={(isBubbleGroupParent || isCommanderGroupParent) ? 0 : undefined}
                              onKeyDown={(event) => {
                                if (!isBubbleGroupParent && !isCommanderGroupParent) return;
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  handleGroupedRowToggle();
                                }
                              }}
                              aria-expanded={(isBubbleGroupParent || isCommanderGroupParent) ? displayRow.isExpanded : undefined}
                            >
                              <td data-label="Time">{formatBattleLogTime(b.at || b.battleTime)}</td>
                              <td data-label="Battle Log" className="events-battle-data-cell">{renderBattleDataMark(hasBattleLogData, "Battle log")}</td>
                              <td data-label="Replay" className="events-battle-data-cell">{renderBattleDataMark(hasReplayData, "Replay")}</td>
                              <td data-label="Report" className="events-battle-data-cell">{renderBattleDataMark(hasReportData, "Report")}</td>
                              <td data-label="Attacker">{attackerLabel}</td>
                              <td data-label="Attacker Troops">{Number(attackerTroops || 0).toLocaleString()}</td>
                              <td data-label="Defender">{defenderLabel}</td>
                              <td data-label="Defender Troops">{Number(defenderTroops || 0).toLocaleString()}</td>
                              <td data-label="Impact Rank" className={homeNetClassName}>
                                {impactRank ? (
                                  <div className="events-battle-log-impact-rank">
                                    <strong>{impactRank.label}</strong>
                                    <span>#{impactRank.worstRank} of {impactRank.total} worst to best</span>
                                  </div>
                                ) : "-"}
                              </td>
                              <td data-label="Expected Winner">{expectedWinner}</td>
                              <td data-label="Location">{location}</td>
                              <td data-label="Triage">
                                <div className="events-battle-log-badges">
                                  <span className={`events-battle-log-badge source-${pointSource}`}>
                                    {BATTLE_POINT_SOURCE_LABELS[pointSource] || pointSource}
                                  </span>
                                  <span className={`events-battle-log-badge confidence-${pointConfidence}`}>
                                    {BATTLE_CONFIDENCE_LABELS[pointConfidence] || pointConfidence}
                                  </span>
                                  <span className={`events-battle-log-badge impact-${impactStatus}`}>
                                    {BATTLE_IMPACT_LABELS[impactStatus] || impactStatus}
                                  </span>
                                  {BATTLE_RISK_RANK[riskLevel] > BATTLE_RISK_RANK.none && (
                                    <span className={`events-battle-log-badge risk-${riskLevel}`}>
                                      {BATTLE_RISK_LABELS[riskLevel] || riskLevel}
                                    </span>
                                  )}
                                  {BATTLE_RISK_RANK[exchangeLevel] >= BATTLE_RISK_RANK.high && (
                                    <span className={`events-battle-log-badge exchange-${exchangeLevel}`}>
                                      {BATTLE_EXCHANGE_LABELS[exchangeLevel] || exchangeLevel}
                                    </span>
                                  )}
                                  {strikeReviewLevel !== "none" && BATTLE_STRIKE_REVIEW_LABELS[strikeReviewLevel] && (
                                    <span className={`events-battle-log-badge strike-${strikeReviewLevel}`}>
                                      {BATTLE_STRIKE_REVIEW_LABELS[strikeReviewLevel]}
                                    </span>
                                  )}
                                  {strikeCandidateState.matches.length > 0 && (
                                    <span className="events-battle-log-badge strike-configured">
                                      {strikeCandidateState.matches.length === 1
                                        ? (strikeCandidateState.matches[0].label || "Rule")
                                        : `${strikeCandidateState.matches.length} rules`}
                                    </span>
                                  )}
                                  {bubbleDropGroup && (
                                    <span
                                      className="events-battle-log-badge bubble-drop"
                                      title={`${bubbleDropGroup.battleCount} home defenses at City (${bubbleDropGroup.cityKey}) within ${Math.max(1, Math.ceil((bubbleDropGroup.endTime - bubbleDropGroup.startTime) / 60000))} minutes`}
                                    >
                                      {isBubbleGroupParent
                                        ? `Bubble drop x${bubbleDropGroup.battleCount}`
                                        : "Bubble drop member"}
                                    </span>
                                  )}
                                  {isBubbleGroupParent && (
                                    <button
                                      type="button"
                                      className="events-battle-log-bubble-toggle"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleBubbleParentToggle();
                                      }}
                                      aria-expanded={displayRow.isExpanded}
                                    >
                                      <span className="events-battle-log-bubble-chevron" aria-hidden="true">
                                        {displayRow.isExpanded ? "v" : ">"}
                                      </span>
                                      <span>
                                        {displayRow.isExpanded
                                          ? "Hide grouped battles"
                                          : `${Math.max(0, displayRow.childCount)} more grouped`}
                                      </span>
                                    </button>
                                  )}
                                  {isCommanderGroupParent && commanderGroup && (
                                    <button
                                      type="button"
                                      className="events-battle-log-commander-toggle"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleCommanderParentToggle();
                                      }}
                                      aria-expanded={displayRow.isExpanded}
                                    >
                                      <span className="events-battle-log-bubble-chevron" aria-hidden="true">
                                        {displayRow.isExpanded ? "v" : ">"}
                                      </span>
                                      <span>
                                        {(commanderGroup.commanderRef?.name || commanderNameById[commanderGroup.commanderId] || "Commander")}
                                        {" · "}
                                        {displayRow.isExpanded
                                          ? "Hide battles"
                                          : `${Math.max(0, displayRow.childCount)} more battle(s)`}
                                      </span>
                                    </button>
                                  )}
                                  {isReviewed && (
                                    <button
                                      type="button"
                                      className="events-battle-log-badge events-battle-log-badge-button reviewed"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openBattleReviewModal(b);
                                      }}
                                    >
                                      Reviewed
                                    </button>
                                  )}
                                  {needsReport && (
                                    <span className="events-battle-log-badge needs-report">
                                      Needs report
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td data-label="Actions">
                                <div className="events-battle-log-actions">
                                    <button
                                      type="button"
                                      className="events-battle-log-action events-battle-log-action-secondary"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleCopyBattleReportRequest(b);
                                      }}
                                    >
                                    {copiedReportRequestBattleId === String(b._id) ? "Copied" : "Copy request"}
                                  </button>
                                  <button
                                    type="button"
                                    className="events-battle-log-action events-battle-log-action-report"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setReportBattleId(String(b._id));
                                    }}
                                    disabled={!canViewReport}
                                  >
                                    View report
                                  </button>
                                  {canWriteCommanders && (
                                    <button
                                      type="button"
                                      className="events-battle-log-action events-battle-log-action-strike"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openStrikeModal(
                                          b,
                                          commanderGroup
                                            ? {
                                              preferredCommanderId: commanderGroup.commanderId,
                                              autoReviewBattleIds: commanderGroup.battleIds,
                                            }
                                            : bubbleDropGroup,
                                        );
                                      }}
                                      disabled={!hasStrikeableCommanders}
                                    >
                                      Strike
                                    </button>
                                  )}
                                  {canWriteEvents && (
                                    <button
                                      type="button"
                                      className="events-battle-log-action events-battle-log-action-secondary"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openBattleReviewModal(b);
                                      }}
                                      disabled={reviewingBattleId === String(b._id)}
                                    >
                                      {reviewingBattleId === String(b._id)
                                        ? "Saving..."
                                        : (isReviewed ? "Edit review" : "Review")}
                                    </button>
                                  )}
                                  {canWriteEvents && !isImportedAozBattle && (
                                    <button
                                      type="button"
                                      className="events-battle-log-action events-battle-log-action-secondary"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        startEditBattle(b);
                                      }}
                                    >
                                      Edit
                                    </button>
                                  )}
                                  {isBubbleGroupParent && displayRow.childCount > 0 && (
                                    <button
                                      type="button"
                                      className="events-battle-log-action events-battle-log-action-secondary"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleBubbleParentToggle();
                                      }}
                                    >
                                      {displayRow.isExpanded
                                        ? "Hide group"
                                        : `Show ${Math.max(0, displayRow.childCount)} more`}
                                    </button>
                                  )}
                                  {canDeleteBattleRow && (
                                    <button
                                      type="button"
                                      className="events-battle-log-action events-battle-log-action-delete"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleDeleteBattle(b._id);
                                      }}
                                    >
                                      Delete
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {reportBattleId && selectedReportBattle && (
                    <BattleReportModal
                      battle={selectedReportBattle}
                      title="Battle Report"
                      onClose={() => {
                        setReportBattleId("");
                        setReportBattleDetail(null);
                      }}
                      evidence={(() => {
                        const evidence = getBattleEvidence(selectedReportBattle);
                        const pointSource = getBattlePointSource(selectedReportBattle);
                        const confidence = getBattleConfidence(selectedReportBattle);
                        const risk = getBattleRiskLevel(selectedReportBattle);
                        return (
                          <>
                            {isLoadingReportBattleDetail && <span>Loading report details...</span>}
                            <span>{evidence.report ? "Report present" : "No report"}</span>
                            <span>{evidence.replay ? "Replay present" : "No replay"}</span>
                            <span>{BATTLE_POINT_SOURCE_LABELS[pointSource] || pointSource}</span>
                            <span>{BATTLE_CONFIDENCE_LABELS[confidence] || confidence} confidence</span>
                            <span>{BATTLE_RISK_LABELS[risk] || risk} risk</span>
                            {pointSource === "replay_estimate" && <strong>Estimated from replay data</strong>}
                          </>
                        );
                      })()}
                    />
                  )}

                  {reviewBattleId && selectedReviewBattle && (
                    <div className="events-battle-report-modal-backdrop" onClick={closeBattleReviewModal}>
                      <div className="events-strike-modal events-review-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="events-battle-report-modal-header">
                          <div>
                            <h4>Review Battle</h4>
                            <span>{formatBattleLogTime(selectedReviewBattle.at || selectedReviewBattle.battleTime)} - {formatBattleLocation(selectedReviewBattle)}</span>
                          </div>
                          <button
                            type="button"
                            className="events-create-modal-close"
                            onClick={closeBattleReviewModal}
                            disabled={Boolean(reviewingBattleId)}
                            aria-label="Close review form"
                          >
                            x
                          </button>
                        </div>
                        <div className="events-battle-report-evidence">
                          {(() => {
                            const pointSource = getBattlePointSource(selectedReviewBattle);
                            const confidence = getBattleConfidence(selectedReviewBattle);
                            const risk = getBattleRiskLevel(selectedReviewBattle);
                            const impact = getBattleImpactStatus(selectedReviewBattle);
                            return (
                              <>
                                <span>{BATTLE_POINT_SOURCE_LABELS[pointSource] || pointSource}</span>
                                <span>{BATTLE_CONFIDENCE_LABELS[confidence] || confidence} confidence</span>
                                <span>{BATTLE_RISK_LABELS[risk] || risk} risk</span>
                                <span>{BATTLE_IMPACT_LABELS[impact] || impact}</span>
                              </>
                            );
                          })()}
                        </div>
                        <div className="events-strike-form">
                          <label className="events-strike-note">
                            Reason
                            <textarea
                              value={reviewForm.reviewNote}
                              onChange={(e) => setReviewForm((prev) => ({ ...prev, reviewNote: e.target.value }))}
                              rows={5}
                              placeholder="Why was this battle reviewed?"
                              disabled={Boolean(reviewingBattleId) || !canWriteEvents}
                            />
                          </label>
                        </div>
                        <div className="events-battle-entry-actions">
                          {canWriteEvents && (
                            <button
                              type="button"
                              className="events-btn events-btn-primary"
                              onClick={handleSaveBattleReview}
                              disabled={Boolean(reviewingBattleId)}
                            >
                              {reviewingBattleId ? "Saving..." : "Save Review"}
                            </button>
                          )}
                          <button
                            type="button"
                            className="events-btn events-btn-secondary"
                            onClick={closeBattleReviewModal}
                            disabled={Boolean(reviewingBattleId)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {strikeBattleId && selectedStrikeBattle && (
                    <div className="events-battle-report-modal-backdrop" onClick={closeStrikeModal}>
                      <div className="events-strike-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="events-battle-report-modal-header">
                          <div>
                            <h4>Apply Strike</h4>
                            <span>{formatBattleLogTime(selectedStrikeBattle.at || selectedStrikeBattle.battleTime)} - {formatBattleLocation(selectedStrikeBattle)}</span>
                          </div>
                          <button
                            type="button"
                            className="events-create-modal-close"
                            onClick={closeStrikeModal}
                            aria-label="Close strike form"
                          >
                            x
                          </button>
                        </div>
                        <div className="events-battle-report-evidence">
                          {(() => {
                            const pointSource = getBattlePointSource(selectedStrikeBattle);
                            const confidence = getBattleConfidence(selectedStrikeBattle);
                            const risk = getBattleRiskLevel(selectedStrikeBattle);
                            const impact = getBattleImpactStatus(selectedStrikeBattle);
                            return (
                              <>
                                <span>{BATTLE_POINT_SOURCE_LABELS[pointSource] || pointSource}</span>
                                <span>{BATTLE_CONFIDENCE_LABELS[confidence] || confidence} confidence</span>
                                <span>{BATTLE_RISK_LABELS[risk] || risk} risk</span>
                                <span>{BATTLE_IMPACT_LABELS[impact] || impact}</span>
                              </>
                            );
                          })()}
                        </div>
                        <div className="events-strike-form">
                          <label>
                            Commander
                            <select
                              value={strikeForm.commanderId}
                              onChange={(e) => setStrikeForm((prev) => ({ ...prev, commanderId: e.target.value }))}
                            >
                              {strikeCommanderOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          {isLoadingExistingEventStrikes && (
                            <div className="events-strike-duplicate-check">
                              Checking prior event strikes...
                            </div>
                          )}
                          {!isLoadingExistingEventStrikes && existingEventCommanderStrikes.length > 0 && (
                            <div className="events-strike-duplicate-warning" role="alert">
                              <strong>Existing event strike</strong>
                              <span>
                                {selectedStrikeCommanderLabel} already has {existingEventCommanderStrikes.length === 1 ? "a strike" : `${existingEventCommanderStrikes.length} strikes`} from this event.
                              </span>
                              <small>
                                Applying another strike should be intentional; the create button will ask for confirmation.
                              </small>
                            </div>
                          )}
                          <label>
                            Strike reason
                            <select
                              value={strikeForm.reasonCode}
                              onChange={(e) => {
                                const nextReason = strikeReasonOptions.find((option) => option.value === e.target.value);
                                setStrikeForm((prev) => ({
                                  ...prev,
                                  reasonCode: e.target.value,
                                  warningLevelDelta: nextReason?.warningLevelDelta || prev.warningLevelDelta,
                                  reasonNote: nextReason?.reasonNoteTemplate || prev.reasonNote,
                                }));
                              }}
                            >
                              <option value="">Manual reason</option>
                              {strikeReasonOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Warning increase
                            <select
                              value={strikeForm.warningLevelDelta}
                              onChange={(e) => setStrikeForm((prev) => ({ ...prev, warningLevelDelta: e.target.value }))}
                            >
                              <option value="1">+1</option>
                              <option value="2">+2</option>
                              <option value="3">+3</option>
                            </select>
                          </label>
                          <label className="events-strike-note">
                            Reason note
                            <textarea
                              value={strikeForm.reasonNote}
                              onChange={(e) => setStrikeForm((prev) => ({ ...prev, reasonNote: e.target.value }))}
                              rows={4}
                              placeholder="Explain why this commander is receiving a strike."
                            />
                          </label>
                          {(strikeForm.evidenceRefs || []).length > 0 && (
                            <div className="events-strike-related-review">
                              <strong>Attached evidence</strong>
                              <div className="events-strike-evidence-list">
                                {(strikeForm.evidenceRefs || []).map((evidence) => (
                                  <div key={`${evidence.reasonModel}:${evidence.reasonRef}`} className="events-strike-evidence-item">
                                    <span>{evidence.label || "Battle report"}</span>
                                    <small>{evidence.summary || ""}</small>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {(strikeForm.autoReviewBattleIds || []).length > 1 && (
                            <div className="events-strike-related-review">
                              <label className="events-strike-checkbox">
                                <input
                                  type="checkbox"
                                  checked={strikeForm.autoReviewRelatedBattles !== false}
                                  onChange={(e) => setStrikeForm((prev) => ({
                                    ...prev,
                                    autoReviewRelatedBattles: e.target.checked,
                                  }))}
                                />
                                <span>
                                  Mark related battles reviewed
                                  <small>{Math.max(0, (strikeForm.autoReviewBattleIds || []).length - 1)} other grouped battle(s)</small>
                                </span>
                              </label>
                              <label className="events-strike-note">
                                Related review note
                                <textarea
                                  value={strikeForm.autoReviewRelatedReviewNote || ""}
                                  onChange={(e) => setStrikeForm((prev) => ({
                                    ...prev,
                                    autoReviewRelatedReviewNote: e.target.value,
                                  }))}
                                  rows={3}
                                  placeholder="Why should the other grouped battles be marked reviewed?"
                                  disabled={strikeForm.autoReviewRelatedBattles === false}
                                />
                              </label>
                            </div>
                          )}
                        </div>
                        <div className="events-battle-entry-actions">
                          <button
                            type="button"
                            className="events-btn events-btn-primary"
                            onClick={handleApplyStrike}
                            disabled={isApplyingStrike || !strikeCommanderOptions.length}
                          >
                            {isApplyingStrike ? "Applying..." : "Apply Strike"}
                          </button>
                          <button
                            type="button"
                            className="events-btn events-btn-secondary"
                            onClick={closeStrikeModal}
                            disabled={isApplyingStrike}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {false && (
                  <div className="events-table-wrap">
                    <table className="events-table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Stronghold</th>
                          <th>Attacker</th>
                          <th>Defender</th>
                          <th>Result</th>
                          <th>Points</th>
                          <th>Source</th>
                          <th>Notes</th>
                          {canWriteEvents && <th>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedEvent.battles || [])
                          .slice()
                          .reverse()
                          .map((b) => (
                            <tr key={b._id}>
                              <td>
                                {b.at ? new Date(b.at).toLocaleString() : "â€”"}
                              </td>
                              <td>
                                {selectedEvent.strongholds?.find(
                                  (s) =>
                                    String(s._id) === String(b.strongholdId),
                                )?.name || "â€”"}
                              </td>
                              <td>
                                {allianceNameById[
                                  String(b.attackingAlliance)
                                ] || "â€”"}{" "}
                                /{" "}
                                {commanderNameById[
                                  String(b.attackingCommander)
                                ] || "â€”"}
                              </td>
                              <td>
                                {allianceNameById[
                                  String(b.defendingAlliance)
                                ] || "â€”"}{" "}
                                /{" "}
                                {commanderNameById[
                                  String(b.defendingCommander)
                                ] || "â€”"}
                              </td>
                              <td>{b.result}</td>
                              <td>{b.pointsAwarded ?? 0}</td>
                              <td>{b.source || "manual"}</td>
                              <td>{b.notes || ""}</td>
                              {canWriteEvents && (
                                <td>
                                  <button onClick={() => startEditBattle(b)}>
                                    Edit
                                  </button>
                                  {canDeleteEvents && (
                                    <button
                                      className="events-delete-btn"
                                      onClick={() => handleDeleteBattle(b._id)}
                                    >
                                      Delete
                                    </button>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  )}
                </EventBattlesPanel>
              )}

              {activeSection === "events" && (activeTab === "results" || activeTab === "upload") && (
                <EventResultsPanel
                  mode={resultEntryMode}
                  canUpload={false}
                  onModeChange={(mode) => {
                    setResultEntryMode(mode === "manual" ? "manual" : "manual");
                    setActiveTab("results");
                  }}
                >
                  {canWriteEvents && isResultEntryModalOpen && (
                    <div className="events-create-modal-backdrop" onClick={closeResultEntryModal}>
                      <div
                        className="events-card events-create-modal events-result-entry-modal"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="events-create-modal-header">
                          <h3>{editingResultId ? "Edit Result Entry" : "Add Result Entry"}</h3>
                          <button
                            type="button"
                            className="events-create-modal-close"
                            onClick={closeResultEntryModal}
                            aria-label="Close result form"
                          >
                            x
                          </button>
                        </div>
                        <div className="events-grid events-battle-form events-manual-results-grid">
                          <div className="events-search-select-field">
                            <SingleSelectDropdown
                              label="Alliance"
                              options={eventAlliances}
                              value={resultForm.allianceId || ""}
                              onChange={(allianceId) =>
                                setResultForm((prev) => ({
                                  ...prev,
                                  allianceId,
                                }))
                              }
                              getOptionLabel={(option) => option.acronym || option.name || ""}
                              getOptionValue={(option) => option._id}
                              helperText="Clear to keep alliance unknown"
                              disabled={Boolean(editingResultId)}
                              placeholder="Search alliances"
                            />
                          </div>

                          <div className="events-search-select-field">
                            <SingleSelectDropdown
                              label="Commander"
                              options={eventCommanderSelectOptions}
                              value={resultForm.commanderId || ""}
                              onChange={(commanderId) => handleResultCommanderChange(commanderId)}
                              getOptionLabel={(option) => option.label || ""}
                              getOptionValue={(option) => option.id}
                              helperText="Search and select one commander"
                              disabled={Boolean(editingResultId)}
                              placeholder="Search commanders"
                            />
                          </div>

                          <label className="events-manual-field-score">
                            Score
                            <input
                              type="text"
                              inputMode="numeric"
                              value={resultForm.score}
                              onChange={(e) =>
                                setResultForm((prev) => ({
                                  ...prev,
                                  score: formatScoreDisplay(e.target.value),
                                }))
                              }
                              placeholder="e.g. 139,796,564"
                            />
                          </label>

                          <label className="events-span-2 events-manual-notes-field">
                            Notes
                            <textarea
                              rows={2}
                              value={resultForm.notes}
                              onChange={(e) =>
                                setResultForm((prev) => ({
                                  ...prev,
                                  notes: e.target.value,
                                }))
                              }
                              placeholder="Optional notes"
                            />
                          </label>

                          <div className="events-manual-results-actions">
                            <AppButton onClick={handleResultSubmit}>
                              {editingResultId ? "Update Result Entry" : "Save Result Entry"}
                            </AppButton>
                            <AppButton
                              kind="secondary"
                              className="events-cancel-btn"
                              onClick={closeResultEntryModal}
                            >
                              Cancel
                            </AppButton>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {false && canWriteEvents && (
                    <div className="events-collapsible-card">
                      <button
                        className="events-collapse-toggle"
                        onClick={() => toggleResultsPanel("entry")}
                      >
                        <span>Add Result Entry</span>
                        <span>{resultsPanelOpen.entry ? "-" : "+"}</span>
                      </button>

                      {resultsPanelOpen.entry && (
                        <>

                          {resultEntryMode === "manual" ? (
                            <div className="events-grid events-battle-form events-manual-results-grid">
                              <div className="events-search-select-field">
                                <SingleSelectDropdown
                                  label="Alliance"
                                  options={eventAlliances}
                                  value={resultForm.allianceId || ""}
                                  onChange={(allianceId) =>
                                    setResultForm((prev) => ({
                                      ...prev,
                                      allianceId,
                                    }))
                                  }
                                  getOptionLabel={(option) => option.acronym || option.name || ""}
                                  getOptionValue={(option) => option._id}
                                  helperText="Clear to keep alliance unknown"
                                  disabled={Boolean(editingResultId)}
                                  placeholder="Search alliances"
                                />
                              </div>

                              <div className="events-search-select-field">
                                <SingleSelectDropdown
                                  label="Commander"
                                  options={eventCommanderSelectOptions}
                                  value={resultForm.commanderId || ""}
                                  onChange={(commanderId) => handleResultCommanderChange(commanderId)}
                                  getOptionLabel={(option) => option.label || ""}
                                  getOptionValue={(option) => option.id}
                                  helperText="Search and select one commander"
                                  disabled={Boolean(editingResultId)}
                                  placeholder="Search commanders"
                                />
                              </div>

                              <label className="events-manual-field-score">
                                Score
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={resultForm.score}
                                  onChange={(e) =>
                                    setResultForm((prev) => ({
                                      ...prev,
                                      score: formatScoreDisplay(e.target.value),
                                    }))
                                  }
                                  placeholder="e.g. 139,796,564"
                                />
                              </label>

                              <label className="events-span-2 events-manual-notes-field">
                                Notes
                                <textarea
                                  rows={2}
                                  value={resultForm.notes}
                                  onChange={(e) =>
                                    setResultForm((prev) => ({
                                      ...prev,
                                      notes: e.target.value,
                                    }))
                                  }
                                  placeholder="Optional notes"
                                />
                              </label>

                              <div className="events-manual-results-actions">
                                <AppButton onClick={handleResultSubmit}>
                                  {editingResultId ? "Update Result Entry" : "Save Result Entry"}
                                </AppButton>
                                <AppButton
                                  kind="secondary"
                                  className="events-cancel-btn"
                                  onClick={resetResultForm}
                                >
                                  {editingResultId ? "Cancel Edit" : "Clear"}
                                </AppButton>
                              </div>
                            </div>
                          ) : (
                            <div className="events-upload-flow">
                              <div className="events-grid events-battle-form">
                                <div className="events-search-select-field">
                                  <SingleSelectDropdown
                                    label="Participating Alliance"
                                    options={eventAlliances}
                                    value={uploadAllianceId || ""}
                                    onChange={setUploadAllianceId}
                                    getOptionLabel={(option) => option.acronym || option.name || ""}
                                    getOptionValue={(option) => option._id}
                                    helperText="Search and select one alliance"
                                    placeholder="Search alliances"
                                  />
                                </div>

                                <label>
                                  OCR Template
                                  <select
                                    value={selectedTemplateId}
                                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                                  >
                                    <option value="">None (global OCR)</option>
                                    {ocrTemplates.map((t) => (
                                      <option key={t._id} value={t._id}>
                                        {t.name} v{t.version || 1}
                                      </option>
                                    ))}
                                  </select>
                                </label>

                                <div className="events-upload-actions events-upload-actions-stack">
                                  <button
                                    type="button"
                                    className="events-btn events-btn-secondary"
                                    onClick={openTemplateAdmin}
                                  >
                                    Manage Templates
                                  </button>
                                </div>

                                <div className="events-upload-actions events-upload-actions-stack">
                                  <button
                                    type="button"
                                    className="events-btn events-btn-secondary"
                                    onClick={() => setIsUploadModalOpen(true)}
                                  >
                                    Add Screenshots
                                  </button>
                                  <span>{uploadFiles.length} file(s) ready</span>
                                </div>

                                <div className="events-upload-actions events-upload-actions-stack">
                                  <button
                                    type="button"
                                    className="events-btn events-btn-primary"
                                    onClick={handleProcessUploadScreenshots}
                                    disabled={isProcessingUploads || !uploadAllianceId || !uploadFiles.length}
                                  >
                                    {isProcessingUploads ? "Processingâ€¦" : "Process Uploads"}
                                  </button>
                                </div>
                              </div>

                              <ImageUploadModal
                                open={isUploadModalOpen}
                                title="Add Result Screenshots"
                                subtitle="Use drag/drop, paste, or browse. Multiple images supported."
                                files={uploadFiles}
                                onFilesChange={setUploadFiles}
                                onClose={() => setIsUploadModalOpen(false)}
                                multiple
                                maxFiles={20}
                                primaryActionLabel={isProcessingUploads ? "Processingâ€¦" : "Process Uploads"}
                                primaryActionDisabled={isProcessingUploads || !uploadAllianceId || !uploadFiles.length}
                                onPrimaryAction={async () => {
                                  const ok = await handleProcessUploadScreenshots();
                                  if (ok) setIsUploadModalOpen(false);
                                }}
                              />

                              {isTemplateAdminOpen && (
                                <div
                                  className="events-upload-modal-backdrop"
                                  onClick={() => setIsTemplateAdminOpen(false)}
                                >
                                  <div
                                    className="events-upload-modal events-template-admin-modal"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="events-upload-modal-header">
                                      <h4>OCR Template Admin ({selectedEvent?.eventType || "event"})</h4>
                                      <button
                                        className="events-create-modal-close"
                                        onClick={() => setIsTemplateAdminOpen(false)}
                                      >
                                        âœ•
                                      </button>
                                    </div>

                                    <div className="events-grid events-battle-form">
                                      <label>
                                        Existing Template
                                        <select
                                          value={templateDraft._id}
                                          onChange={(e) => {
                                            const chosen = ocrTemplates.find(
                                              (t) => String(t._id) === String(e.target.value),
                                            );
                                            if (!chosen) {
                                              setTemplateDraft({
                                                _id: "",
                                                name: "",
                                                eventType: selectedEvent?.eventType || "",
                                                enabled: true,
                                                description: "",
                                                version: 1,
                                                rowsPerScreenshot: 10,
                                                rowGapRatio: 0.02,
                                                selectionProfile: normalizeSelectionProfileDraft({ resultsScope: "commander", layoutFamily: "leaderboard" }),
                                                sourceImage: {
                                                  dataUrl: "",
                                                  fileName: "",
                                                  mimeType: "",
                                                  width: null,
                                                  height: null,
                                                },
                                                annotations: [],
                                              });
                                              setTemplateUploadFiles([]);
                                              return;
                                            }

                                            setTemplateDraft({
                                              _id: String(chosen._id || ""),
                                              name: chosen.name || "",
                                              eventType:
                                                chosen.eventType ||
                                                selectedEvent?.eventType ||
                                                "",
                                              enabled: chosen.enabled !== false,
                                              description: chosen.description || "",
                                              version: Number(chosen.version || 1),
                                              rowsPerScreenshot: Number(
                                                chosen.layout?.rowsPerScreenshot || 10,
                                              ),
                                              rowGapRatio: Number(
                                                chosen.layout?.rowGapRatio || 0,
                                              ),
                                              selectionProfile: normalizeSelectionProfileDraft(chosen.selectionProfile || {}),
                                              sourceImage: chosen.sourceImage || {
                                                dataUrl:
                                                  chosen.referenceImageDataUrl || "",
                                                fileName: "",
                                                mimeType: "",
                                                width: null,
                                                height: null,
                                              },
                                              annotations:
                                                Array.isArray(chosen.annotations) &&
                                                chosen.annotations.length
                                                  ? chosen.annotations.map((row, idx) => ({
                                                      id: row.id || `ann-${idx}`,
                                                      label: row.label,
                                                      box: row.box,
                                                    }))
                                                  : layoutToAnnotations(chosen.layout),
                                            });
                                            setTemplateUploadFiles([]);
                                          }}
                                        >
                                          <option value="">New template</option>
                                          {ocrTemplates.map((t) => (
                                            <option key={t._id} value={t._id}>
                                              {t.name} v{t.version || 1}
                                            </option>
                                          ))}
                                        </select>
                                      </label>

                                      <label>
                                        Template Name
                                        <input
                                          value={templateDraft.name}
                                          onChange={(e) =>
                                            setTemplateDraft((prev) => ({
                                              ...prev,
                                              name: e.target.value,
                                            }))
                                          }
                                        />
                                      </label>

                                      <label>
                                        Version
                                        <input
                                          type="number"
                                          value={templateDraft.version}
                                          onChange={(e) =>
                                            setTemplateDraft((prev) => ({
                                              ...prev,
                                              version: e.target.value,
                                            }))
                                          }
                                        />
                                      </label>

                                      <label>
                                        Enabled
                                        <select
                                          value={templateDraft.enabled ? "true" : "false"}
                                          onChange={(e) =>
                                            setTemplateDraft((prev) => ({
                                              ...prev,
                                              enabled: e.target.value === "true",
                                            }))
                                          }
                                        >
                                          <option value="true">Enabled</option>
                                          <option value="false">Disabled</option>
                                        </select>
                                      </label>

                                      <label className="events-span-2">
                                        Description
                                        <input
                                          value={templateDraft.description}
                                          onChange={(e) =>
                                            setTemplateDraft((prev) => ({
                                              ...prev,
                                              description: e.target.value,
                                            }))
                                          }
                                        />
                                      </label>

                                      <label>
                                        Result Scope
                                        <select
                                          value={templateDraft.selectionProfile?.resultsScope || "commander"}
                                          onChange={(e) =>
                                            setTemplateDraft((prev) => ({
                                              ...prev,
                                              selectionProfile: {
                                                ...(prev.selectionProfile || {}),
                                                resultsScope: e.target.value,
                                              },
                                            }))
                                          }
                                        >
                                          <option value="commander">Commander leaderboard</option>
                                          <option value="alliance">Alliance leaderboard</option>
                                          <option value="nation">Nation leaderboard</option>
                                          <option value="mixed">Mixed</option>
                                          <option value="generic">Generic</option>
                                        </select>
                                      </label>

                                      <label>
                                        Layout Family
                                        <input
                                          value={templateDraft.selectionProfile?.layoutFamily || "leaderboard"}
                                          onChange={(e) =>
                                            setTemplateDraft((prev) => ({
                                              ...prev,
                                              selectionProfile: {
                                                ...(prev.selectionProfile || {}),
                                                layoutFamily: e.target.value,
                                              },
                                            }))
                                          }
                                        />
                                      </label>

                                      <label>
                                        <span className="events-field-with-info">
                                          Rows per Screenshot
                                          <span
                                            className="events-info-icon"
                                            title="Rows are the repeated commander entries in the leaderboard section, not the whole screenshot. Draw Row area only around the repeating list. Set this to how many entries are visible (e.g. 8 commanders = 8 rows). Top metadata like date/time and participants should be labeled separately."
                                            aria-label="Rows help"
                                          >
                                            â„¹ï¸
                                          </span>
                                        </span>
                                        <input
                                          type="number"
                                          min={1}
                                          max={200}
                                          value={templateDraft.rowsPerScreenshot}
                                          onChange={(e) =>
                                            setTemplateDraft((prev) => ({
                                              ...prev,
                                              rowsPerScreenshot: e.target.value,
                                            }))
                                          }
                                        />
                                      </label>

                                      <label>
                                        Row Gap Ratio
                                        <input
                                          type="number"
                                          step="0.01"
                                          min={0}
                                          max={0.5}
                                          value={templateDraft.rowGapRatio}
                                          onChange={(e) =>
                                            setTemplateDraft((prev) => ({
                                              ...prev,
                                              rowGapRatio: e.target.value,
                                            }))
                                          }
                                        />
                                      </label>

                                      <label>
                                        Visible Rows Min (hint)
                                        <input
                                          type="number"
                                          min={1}
                                          value={templateDraft.selectionProfile?.visibleRowsHintMin || ""}
                                          onChange={(e) =>
                                            setTemplateDraft((prev) => ({
                                              ...prev,
                                              selectionProfile: {
                                                ...(prev.selectionProfile || {}),
                                                visibleRowsHintMin: e.target.value,
                                              },
                                            }))
                                          }
                                        />
                                      </label>

                                      <label>
                                        Visible Rows Max (hint)
                                        <input
                                          type="number"
                                          min={1}
                                          value={templateDraft.selectionProfile?.visibleRowsHintMax || ""}
                                          onChange={(e) =>
                                            setTemplateDraft((prev) => ({
                                              ...prev,
                                              selectionProfile: {
                                                ...(prev.selectionProfile || {}),
                                                visibleRowsHintMax: e.target.value,
                                              },
                                            }))
                                          }
                                        />
                                      </label>

                                      <label>
                                        Aspect Ratio Min (hint)
                                        <input
                                          type="number"
                                          step="0.01"
                                          min={0}
                                          value={templateDraft.selectionProfile?.aspectRatioMin || ""}
                                          onChange={(e) =>
                                            setTemplateDraft((prev) => ({
                                              ...prev,
                                              selectionProfile: {
                                                ...(prev.selectionProfile || {}),
                                                aspectRatioMin: e.target.value,
                                              },
                                            }))
                                          }
                                        />
                                      </label>

                                      <label>
                                        Aspect Ratio Max (hint)
                                        <input
                                          type="number"
                                          step="0.01"
                                          min={0}
                                          value={templateDraft.selectionProfile?.aspectRatioMax || ""}
                                          onChange={(e) =>
                                            setTemplateDraft((prev) => ({
                                              ...prev,
                                              selectionProfile: {
                                                ...(prev.selectionProfile || {}),
                                                aspectRatioMax: e.target.value,
                                              },
                                            }))
                                          }
                                        />
                                      </label>

                                      <label className="events-span-2">
                                        Tags (comma separated)
                                        <input
                                          value={templateDraft.selectionProfile?.tagsText || ""}
                                          onChange={(e) =>
                                            setTemplateDraft((prev) => ({
                                              ...prev,
                                              selectionProfile: {
                                                ...(prev.selectionProfile || {}),
                                                tagsText: e.target.value,
                                              },
                                            }))
                                          }
                                        />
                                      </label>

                                      <div className="events-template-admin-actions events-span-2">
                                        <button onClick={() => setIsTemplateImageUploadOpen(true)}>
                                          Upload / Replace Base Image
                                        </button>
                                        {!!templateDraft.sourceImage?.dataUrl && (
                                          <span className="events-template-base-meta">
                                            {templateDraft.sourceImage.fileName || "base image ready"}
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    <TemplateBoxAnnotator
                                      imageSrc={templateDraft.sourceImage?.dataUrl}
                                      annotations={templateDraft.annotations}
                                      onChange={(next) =>
                                        setTemplateDraft((prev) => ({
                                          ...prev,
                                          annotations: next,
                                        }))
                                      }
                                      labelOptions={TEMPLATE_LABEL_OPTIONS}
                                    />

                                    <p className="events-template-help">
                                      Draw boxes directly on the screenshot. After each box, choose
                                      a label. Required labels: Row area, Commander name, Points.
                                    </p>

                                    <div className="events-template-admin-actions">
                                      <button
                                        onClick={saveTemplateDraft}
                                        disabled={isSavingTemplate}
                                      >
                                        {isSavingTemplate ? "Saving..." : "Save Template"}
                                      </button>
                                      {!!templateDraft._id && (
                                        <button
                                          className="events-delete-btn"
                                          onClick={deleteTemplateDraft}
                                        >
                                          Delete Template
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              <ImageUploadModal
                                open={isTemplateImageUploadOpen}
                                title="Upload template base screenshot"
                                subtitle="Upload a clear screenshot, then draw bounding boxes and label them."
                                files={templateUploadFiles}
                                onFilesChange={handleTemplateUploadFilesChange}
                                onClose={() => setIsTemplateImageUploadOpen(false)}
                                multiple={false}
                                maxFiles={1}
                              />

                              {!!uploadReviewRows.length && (
                                <div className="events-upload-review">
                                  <div className="events-upload-review-header">
                                    <strong>Review & Fix OCR Matches</strong>
                                    <button
                                      type="button"
                                      className="events-btn events-btn-primary"
                                      onClick={handleApplyUploadResults}
                                      disabled={isApplyingUploadRows}
                                    >
                                      {isApplyingUploadRows ? "Applyingâ€¦" : "Apply Valid Rows"}
                                    </button>
                                  </div>
                                  <div className="events-table-wrap">
                                    <table className="events-table">
                                      <thead>
                                        <tr>
                                          <th>Screenshot</th>
                                          <th>OCR Name</th>
                                          <th>Score</th>
                                          <th>Matched Commander</th>
                                          <th>Confidence</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {uploadReviewRows.map((row) => {
                                          const confidenceValue = Math.max(0, Math.min(1, Number(row.confidence || 0)));
                                          const confidenceHue = Math.round(confidenceValue * 120);
                                          const rowBackground = `hsla(${confidenceHue}, 72%, 44%, 0.16)`;
                                          const rowBorder = `hsla(${confidenceHue}, 72%, 34%, 0.28)`;

                                          return (
                                          <tr
                                            key={row.id}
                                            className="events-upload-review-row"
                                            style={{
                                              backgroundColor: rowBackground,
                                              boxShadow: `inset 0 0 0 1px ${rowBorder}`,
                                            }}
                                          >
                                            <td>
                                              {row.previewDataUrl ? (
                                                <div className="events-upload-row-preview-wrap">
                                                  <img
                                                    src={row.previewDataUrl}
                                                    alt={`Screenshot ${Number(row.screenshotIndex || 0) + 1} row ${Number(row.rowIndex || 0) + 1}`}
                                                    className="events-upload-row-preview"
                                                    onClick={() =>
                                                      setExpandedRowPreview({
                                                        src: row.previewDataUrl,
                                                        label: `Screenshot ${Number(row.screenshotIndex || 0) + 1} Â· Row ${Number(row.rowIndex || 0) + 1}`,
                                                      })
                                                    }
                                                  />
                                                  <span>{`#${Number(row.screenshotIndex || 0) + 1} Â· Row ${Number(row.rowIndex || 0) + 1}`}</span>
                                                </div>
                                              ) : (
                                                `#${Number(row.screenshotIndex || 0) + 1}`
                                              )}
                                            </td>
                                            <td>{row.rawName || "â€”"}</td>
                                            <td>
                                              <input
                                                type="text"
                                                inputMode="numeric"
                                                value={formatScoreForDisplay(row.score)}
                                                onChange={(e) =>
                                                  setUploadReviewRows((prev) =>
                                                    prev.map((x) =>
                                                      x.id === row.id
                                                        ? { ...x, score: parseScoreInput(e.target.value) }
                                                        : x,
                                                    ),
                                                  )
                                                }
                                              />
                                            </td>
                                            <td>
                                              <div className="events-upload-matched-commander">
                                                <SingleSelectDropdown
                                                  label=""
                                                  size="small"
                                                  minWidth={0}
                                                  placeholder="Search commander"
                                                  options={uploadCommanderCandidates}
                                                  value={row.commanderId || ""}
                                                  getOptionLabel={(option) => String(option?.label || "")}
                                                  getOptionValue={(option) => String(option?.commanderId || "")}
                                                  onChange={(commanderId) => {
                                                    const picked = uploadCommanderCandidates.find(
                                                      (item) => String(item.commanderId) === String(commanderId),
                                                    );
                                                    setUploadReviewRows((prev) =>
                                                      prev.map((x) =>
                                                        x.id === row.id
                                                          ? {
                                                              ...x,
                                                              commanderId,
                                                              commanderLookup: picked?.commanderName || x.commanderLookup || "",
                                                            }
                                                          : x,
                                                      ),
                                                    );
                                                  }}
                                                />
                                              </div>
                                            </td>
                                            <td>{`${Math.round(Number(row.confidence || 0) * 100)}%`}</td>
                                          </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>

                                  {expandedRowPreview && (
                                    <div
                                      className="events-image-lightbox-backdrop"
                                      onClick={() => setExpandedRowPreview(null)}
                                    >
                                      <div
                                        className="events-image-lightbox"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <button
                                          className="events-image-lightbox-close"
                                          onClick={() => setExpandedRowPreview(null)}
                                        >
                                          âœ•
                                        </button>
                                        <img
                                          src={expandedRowPreview.src}
                                          alt={expandedRowPreview.label || "OCR row preview"}
                                          className="events-image-lightbox-img"
                                        />
                                        <div className="events-image-lightbox-label">
                                          {expandedRowPreview.label || "OCR row preview"}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {activeTab === "results" && availableResultModes.includes("nation") && (
                    <div className="events-collapsible-card">
                      <button
                        className="events-collapse-toggle"
                        onClick={() => toggleResultsPanel("nation")}
                      >
                        <span>Nation Leaderboard</span>
                        <span>{resultsPanelOpen.nation ? "-" : "+"}</span>
                      </button>
                      {resultsPanelOpen.nation && (
                        <div className="events-nation-scoreboard-wrap">
                          {hasNationResultBreakdown && (
                            <div className="events-result-section-toolbar">
                              <button
                                type="button"
                                className="events-secondary-btn"
                                onClick={() => setIsNationScoreBreakdownOpen((value) => !value)}
                              >
                                {isNationScoreBreakdownOpen ? "Hide phase breakdown" : "Show phase breakdown"}
                              </button>
                            </div>
                          )}
                          {nationMatchupScoreboard.rows.length ? (
                            <div className="events-nation-scoreboard">
                              <div className="events-nation-scorebar" aria-label="Nation score comparison">
                                {nationMatchupScoreboard.rows.map((row) => {
                                  const pct = nationMatchupScoreboard.total > 0
                                    ? Math.max(4, (Number(row.points || 0) / nationMatchupScoreboard.total) * 100)
                                    : 50;
                                  return (
                                    <span
                                      key={row.key}
                                      className={`events-nation-scorebar-segment events-nation-scorebar-segment-${row.side}`}
                                      style={{ width: `${pct}%` }}
                                    />
                                  );
                                })}
                              </div>
                              <div className="events-nation-scoreboard-sides">
                                {nationMatchupScoreboard.rows.map((row) => (
                                  <div
                                    key={row.key}
                                    className={`events-nation-scoreboard-side events-nation-scoreboard-side-${row.side}`}
                                  >
                                    <strong>{row.label}</strong>
                                    <span>{Number(row.points || 0).toLocaleString()}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="events-empty-state">
                              <strong>No nation scores yet.</strong>
                              <span>Add or import official nation results.</span>
                            </div>
                          )}
                          {hasNationResultBreakdown && isNationScoreBreakdownOpen && (
                            <div className="events-nation-score-breakdown-grid">
                              {nationResultRows.map((row, index) => {
                                const breakdown = getNationScoreBreakdown(row);
                                const total = breakdown.reduce(
                                  (sum, field) => sum + (Number.isFinite(field.value) ? field.value : 0),
                                  0,
                                );
                                const nationLabel = nationNameById[String(row.nationId)] ||
                                  row.displayName ||
                                  String(row.nationId);
                                return (
                                  <div
                                    className="events-nation-score-breakdown-card"
                                    key={String(row.key || row.nationId || index)}
                                  >
                                    <div className="events-nation-score-breakdown-card-header">
                                      <strong>{nationLabel}</strong>
                                      <span>{Number(row.points || 0).toLocaleString()}</span>
                                    </div>
                                    <div className="events-nation-score-breakdown-list">
                                      {breakdown.map((field) => (
                                        <div key={field.key}>
                                          <span>{field.label}</span>
                                          <strong>{Number(field.value || 0).toLocaleString()}</strong>
                                        </div>
                                      ))}
                                      {total > 0 && total !== Number(row.points || 0) && (
                                        <div>
                                          <span>Breakdown Total</span>
                                          <strong>{Number(total).toLocaleString()}</strong>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === "results" && availableResultModes.includes("alliance") && (
                    <div className="events-collapsible-card">
                      <button
                        className="events-collapse-toggle"
                        onClick={() => toggleResultsPanel("alliance")}
                      >
                        <span>Alliance Leaderboard</span>
                        <span>{resultsPanelOpen.alliance ? "-" : "+"}</span>
                      </button>
                      {resultsPanelOpen.alliance && (
                        <div className="events-table-wrap">
                          <table className="events-table">
                            <thead>
                              <tr>
                                <th>Alliance</th>
                                <th>Nation</th>
                                <th>Points</th>
                              </tr>
                            </thead>
                            <tbody>
                              {allianceResultRows.map((row, index) => (
                                <tr key={String(row.key || row.alliance || index)}>
                                  <td>
                                    {allianceNameById[String(row.alliance)] ||
                                      row.displayName ||
                                      String(row.alliance)}
                                  </td>
                                  <td>
                                    {nationNameById[String(row.nationId)] ||
                                      row.nationDisplayName ||
                                      "-"}
                                  </td>
                                  <td>{Number(row.points || 0).toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === "results" && availableResultModes.includes("individual") && (
                    <div className="events-collapsible-card">
                      <button
                        className="events-collapse-toggle"
                        onClick={() => toggleResultsPanel("individual")}
                      >
                        <span>Commander Leaderboard</span>
                        <span>{resultsPanelOpen.individual ? "-" : "+"}</span>
                      </button>
                      {resultsPanelOpen.individual && (
                        <div className="events-table-wrap">
                          {individualScoreboardTabs.length > 1 && (
                            <div className="events-entry-mode-switch events-scoreboard-type-switch">
                              {individualScoreboardTabs.map((tab) => (
                                <button
                                  key={tab.id}
                                  type="button"
                                  className={individualScoreboardType === tab.id ? "active" : ""}
                                  onClick={() => setIndividualScoreboardType(tab.id)}
                                >
                                  {tab.label}
                                </button>
                              ))}
                            </div>
                          )}
                          <table className="events-table">
                            <thead>
                              <tr>
                                <th>Commander</th>
                                <th>Alliance</th>
                                <th>Nation</th>
                                <th>Score</th>
                                <th>Rank</th>
                                <th>Source</th>
                                <th>Updated</th>
                                <th>Notes</th>
                                {canWriteEvents && <th>Actions</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {individualScoreboardRows.map((row) => (
                                <tr key={row._id}>
                                  <td>
                                    {commanderNameById[String(row.commander)] ||
                                      row.commanderDisplayName ||
                                      "â€”"}
                                  </td>
                                  <td>
                                    {allianceNameById[String(row.alliance)] ||
                                      allianceNameById[
                                        String(
                                          commanderById[String(row.commander)]
                                            ?.alliance?._id ||
                                            commanderById[String(row.commander)]
                                              ?.alliance,
                                        )
                                      ] ||
                                      row.allianceDisplayName ||
                                      "â€”"}
                                  </td>
                                  <td>
                                    {nationNameById[String(row.nationId)] ||
                                      nationNameById[String(row.subjectNation?._id || row.subjectNation || "")] ||
                                      nationNameById[
                                        String(
                                          commanderById[String(row.commander)]
                                            ?.nation?._id ||
                                            commanderById[String(row.commander)]
                                              ?.nation ||
                                            "",
                                        )
                                      ] ||
                                      row.nationDisplayName ||
                                      "-"}
                                  </td>
                                  <td>
                                    {row?.participated === false
                                      ? "-"
                                      : Number(row.score || 0).toLocaleString()}
                                  </td>
                                  <td>{row.rank ?? "â€”"}</td>
                                  <td>{row.source || "manual"}</td>
                                  <td>
                                    {row.updatedAt
                                      ? new Date(row.updatedAt).toLocaleString()
                                      : "â€”"}
                                  </td>
                                  <td>{row.notes || ""}</td>
                                  {canWriteEvents && (
                                    <td>
                                      <button
                                        disabled={isOfficialScoreboardRow(row)}
                                        onClick={() => {
                                          if (!isOfficialScoreboardRow(row)) startEditResult(row);
                                        }}
                                      >
                                        Edit
                                      </button>
                                      {canDeleteEvents && (
                                        <button
                                          className="events-delete-btn"
                                          disabled={isOfficialScoreboardRow(row)}
                                          onClick={() => {
                                            if (!isOfficialScoreboardRow(row)) handleDeleteResult(row._id);
                                          }}
                                        >
                                          Delete
                                        </button>
                                      )}
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </EventResultsPanel>
              )}

              {activeSection === "events" && activeTab === "recommendations" && (
                <div className="events-table-wrap">
                  <table className="events-table">
                    <thead>
                      <tr>
                        <th>Commander</th>
                        <th>Alliance</th>
                        <th>Battle Power</th>
                        <th>Blended Rank</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recommendations.map((c) => (
                        <tr key={c._id}>
                          <td>{c.name}</td>
                          <td>
                            {allianceNameById[
                              String(c?.alliance?._id || c?.alliance)
                            ] || "â€”"}
                          </td>
                          <td>{Number(c.battlePower || 0).toLocaleString()}</td>
                          <td>{Number(c.blendedRank || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
      </div>
    </PageShell>
  );
};

export default Events;
